import { useEffect, useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { approvalActionLabel, approvalLabel, assignmentCreatorRole, assignmentLastDate, assignmentOccursOn, assignmentPriorityDate,
  byAssignmentPrecedence, canModifyAssignment, describeAssignmentRule,
  isAssignmentPlayable, isAssignmentProposal, isValidAssignmentRule, roleLabel,
  lunarDateLabel, lunarDayOptions, lunarMonthOptions, lunarToSolar, nthWeekdayOfMonth, overlappingDates,
  reorderedPriority, solarToLunar, parseClock,
  weekdayInMonth, MAX_REPEAT_COUNT,
  type AssignmentCalendar, type AssignmentLayer, type AssignmentRepeat, type AssignmentRepeatEnd, type AssignmentRule,
  type ContentAccessMetadata, type DevicePowerSchedule,
  type MediaItem, type Notice, type Playlist, type ScheduleAssignment, type ScheduleType, type TimelineBlock } from '@cornerstone/shared';
import { api } from '../client/api';
import { usePublicHolidays, type HolidayNames } from '../client/publicHolidays';
import { useIsMobile } from '../utils/useIsMobile';
import { Button, Chip, Segmented } from '../components/ui';
import { ModalShell } from '../components/menu';
import { DeviceDiagnosticsBadge, useDeviceDiagnostics, type DeviceDiagnostic } from '../components/DeviceDiagnostics';
import { toast, describeApiError } from '../components/toast';
import { PageContainer } from '../nav/PageContainer';
import { PageTitle } from '../nav/PageTitle';
import { useWriteGate } from '../nav/useWriteGate';
import { playerPreviewUrl } from '../client/previewUrl';
import { useLibraryStore } from '../store/library';
import { canDecideAssignment, currentApprovalReason, isOwnPendingRequest, useWorkflowScope, workflowJson, type WorkflowScope } from './mock/ContentApproval';
import { ScheduleReview } from './ScheduleReview';
import { ASSIGNMENTS_CHANGED_EVENT } from '../components/assistantPage';
import { NO_GROUP_TITLES, NoAssignedGroupsNotice, hasNoAssignedGroups } from '../components/GroupAssignmentNotice';
import { groupSections, type GroupSection } from '../components/groupSections';

// =============================================================================
// 편성표 = 스케줄. 24시간 템플릿을 기기에 기간·반복 조건으로 붙인 것이 하나의 편성이다.
// 시간대는 템플릿이 정하므로 여기서는 정하지 않는다.
// =============================================================================

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
const pad = (n: number) => String(n).padStart(2, '0');
export const minuteToHHMM = (minute: number) => `${pad(Math.floor(minute / 60))}:${pad(minute % 60)}`;

export const todayISO = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
/** 서울 기준 현재 시각(분). 일 뷰의 현재시각 선이 쓴다. */
export const nowMinuteSeoul = () => {
  const [hour, minute] = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .format(new Date()).split(':');
  return Number(hour) * 60 + Number(minute);
};
export const addDays = (iso: string, n: number) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date(Date.parse(`${iso}T12:00:00+09:00`) + n * 86400000));
export const weekdayOf = (iso: string) => new Date(Date.parse(`${iso}T12:00:00+09:00`)).getUTCDay();
const NO_HOLIDAYS: HolidayNames = new Map();
/** 두 날짜 사이의 일수(to - from). 날짜를 옮긴 만큼 기기별 제외 날짜도 함께 옮길 때 쓴다. */
export const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T12:00:00+09:00`) - Date.parse(`${from}T12:00:00+09:00`)) / 86400000);
export const weekStart = (iso: string) => addDays(iso, -((weekdayOf(iso) + 6) % 7));
export const monthDays = (iso: string): string[] => {
  const days: string[] = [];
  for (let d = `${iso.slice(0, 7)}-01`; d.slice(0, 7) === iso.slice(0, 7); d = addDays(d, 1)) days.push(d);
  return days;
};
const dateLabel = (iso: string) => `${Number(iso.slice(0, 4))}. ${Number(iso.slice(5, 7))}. ${Number(iso.slice(8, 10))} (${DAY_LABELS[weekdayOf(iso)]})`;

export const periodLabel = (a: Pick<ScheduleAssignment, 'startDate' | 'endDate'>) =>
  a.startDate === a.endDate ? a.startDate : `${a.startDate} ~ ${a.endDate}`;

/**
 * 보기 필터 버튼에 적는 요약. 고른 기기가 그룹과 정확히 맞아떨어지면 그룹 이름으로,
 * 아니면 대수로 적는다. 버튼만 보고도 무엇이 걸려 있는지 알아야 한다.
 */
export function viewFilterSummary(selected: readonly string[] | null,
  sections: readonly { name: string; members: readonly { id: string }[] }[]) {
  if (!selected) return '모든 디바이스';
  const picked = new Set(selected);
  if (!picked.size) return '선택된 디바이스 없음';
  const whole = sections.filter(s => s.members.length > 0 && s.members.every(m => picked.has(m.id)));
  const covered = whole.reduce((count, s) => count + s.members.length, 0);
  if (whole.length && covered === picked.size) {
    return whole.length === 1 ? whole[0]!.name : `${whole[0]!.name} 외 ${whole.length - 1}개 그룹`;
  }
  return `${picked.size}대 선택`;
}

type CreatePreset = { deviceIds?: string[]; layer?: AssignmentLayer; date: string; endDate?: string; startMinute?: number; endMinute?: number };

/**
 * 쇼핑몰에서 흔히 쓰는 깔때기 — 글자 없이도 '거르기'로 읽힌다.
 * 16px에서 속이 메워 보이지 않도록 목을 넉넉히 벌려 둔다.
 */
const FunnelIcon = () => <svg aria-hidden width="17" height="17" viewBox="0 0 16 16" fill="none"
  stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
  <path d="M2.6 3.4h10.8l-3.5 4.9v3.9l-3.8 1.4V8.3L2.6 3.4Z" />
</svg>;

/** 되돌리기 화살표 — 고른 것을 모두 푼다. */
const ResetIcon = () => <svg aria-hidden width="15" height="15" viewBox="0 0 16 16" fill="none"
  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
  <path d="M3 8a5 5 0 1 0 1.6-3.7L2.5 6.2" /><path d="M2.2 2.9v3.4h3.4" />
</svg>;

/**
 * 스케줄 편성 한 곳에서 캘린더처럼 편성을 만들고 고친다.
 * (템플릿 전용 화면은 LegacySchedules로 남아 있다 — 되돌릴 수 있게 지우지 않는다.)
 */
// 페이지 머리 통일(2026-09-14): 브레드크럼·설명 없이 네비 라벨과 같은 제목만(PageTitle).
type DialogState = { mode: 'create'; preset: CreatePreset } | { mode: 'edit'; assignment: ScheduleAssignment; date: string };

/** 되돌리기 한 칸. 무엇을 되돌리고 다시 적용할지 스스로 알고 있다. */
type HistoryStep = { undo: () => void; redo: () => void };

/** 대화상자에서 일어난 변경. 되돌리려면 무엇이 어떻게 바뀌었는지 알아야 한다. */
export type ScheduleMutation =
  | { kind: 'create'; after: ScheduleAssignment[] }
  | { kind: 'update'; before: ScheduleAssignment[]; after: ScheduleAssignment[] }
  | { kind: 'delete'; before: ScheduleAssignment[] };

/** 편성 한 벌을 그대로 다시 만들거나 덮어쓰기 위한 본문. 제외 날짜는 기기마다 다르므로 기기별로 붙인다. */
export function assignmentPayload(items: readonly ScheduleAssignment[]) {
  const first = items[0]!;
  return { name: first.name, layer: first.layer,
    blocks: [...first.blocks].sort((a, b) => a.startMinute - b.startMinute)
      .map(({ playlistId, startMinute, endMinute, label }) => ({ playlistId, startMinute, endMinute, label })),
    deviceIds: [...new Set(items.map(item => item.deviceId))],
    startDate: first.startDate, endDate: first.endDate, repeat: first.repeat, repeatEnd: first.repeatEnd,
    calendar: first.calendar ?? 'solar', skipDates: first.skipDates ?? [],
    skipDatesByDevice: Object.fromEntries(items.map(item => [item.deviceId, item.skipDates ?? []])),
    priorityAt: first.priorityAt ?? null };
}

const UndoIcon = ({ flip }: { flip?: boolean }) => <svg aria-hidden width="16" height="16" viewBox="0 0 16 16" fill="none"
  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
  style={flip ? { transform: 'scaleX(-1)' } : undefined}>
  <path d="M6.2 4.2 3.2 7.2l3 3" /><path d="M3.4 7.2h6.1a3.3 3.3 0 0 1 0 6.6H6.6" />
</svg>;

/**
 * 편성에 박힌 승인 버전(cv-…) 플레이리스트를 이름과 함께 읽어 온다.
 *
 * 권한을 적용한 조직에서 편성을 저장하면 서버가 구간의 playlistId를 승인 버전 id로 바꿔 넣는다
 * (packages/main/src/services/scheduleAssignments.ts assignableBlocks). 목록 API(/api/playlists)는
 * 원본만 주므로 이 승인본을 따로 읽지 않으면 다시 연 편성의 플레이리스트 칸이 빈 채로 보인다 —
 * 대교가 "할당이 올바로 이뤄지지 않음"으로 본 자리 중 하나다(2026-09-17).
 * 2026-09-17부터 새 조직은 처음부터 권한 적용이라 이 경로가 기본이 됐다.
 *
 * 승인본은 읽기 전용 스냅샷이다. 공용 라이브러리 스토어에 섞지 않고 이 화면에서만 쓴다
 * (섞으면 라이브러리·폴더 목록에 스냅샷이 콘텐츠처럼 나타난다).
 */
function useApprovedPlaylists(assignments: ScheduleAssignment[], orgId: string | undefined): Playlist[] {
  const [loaded, setLoaded] = useState<Playlist[]>([]);
  // 한 번 물어본 id는 다시 묻지 않는다 — 30초마다 도는 편성 새로고침을 그대로 따라가면 안 된다.
  const asked = useRef(new Set<string>());
  const idsKey = [...new Set(assignments.flatMap(a => a.blocks.map(b => b.playlistId))
    .filter(id => id.startsWith('cv-')))].sort().join(',');
  // 조직(대행 포함)이 바뀌면 남의 조직 스냅샷을 들고 있지 않는다.
  useEffect(() => { asked.current = new Set(); setLoaded([]); }, [orgId]);
  useEffect(() => {
    const missing = (idsKey ? idsKey.split(',') : []).filter(id => !asked.current.has(id));
    if (!missing.length) return;
    for (const id of missing) asked.current.add(id);
    let active = true;
    void Promise.allSettled(missing.map(id => api.getPlaylist(id))).then(results => {
      if (!active) return;
      // 못 읽은 것은 조용히 둔다 — 이름만 비고, 그룹 경계 판정은 '모르면 막지 않는다'로 간다.
      const items = results.flatMap((result, index) =>
        result.status === 'fulfilled' && result.value?.id === missing[index] ? [result.value] : []);
      if (items.length) setLoaded(current => [...current.filter(p => !items.some(item => item.id === p.id)), ...items]);
    });
    return () => { active = false; };
  }, [idsKey, orgId]);
  return loaded;
}

/**
 * 편성을 만들거나 고칠 수 있는 역할인가.
 *
 * 목록 화면과 편집 창이 같은 규칙을 써야 한다. 편집 창이 이 판정을 빼먹어
 * 조회자에게 삭제 버튼이 눌렸다(QA A1).
 */
export const SCHEDULE_WRITE_DENIED = '스케줄을 만들거나 고칠 권한이 없습니다';

export function canWriteSchedules(actor: WorkflowScope['actor']): boolean {
  return !!actor.role && ['Owner', 'Admin', 'Editor'].includes(actor.role)
    && (actor.central || actor.groups.some(g => ['Owner', 'Admin', 'Editor'].includes(g.role)));
}

export function Schedules() {
  const work = useWorkflowScope();
  // 일·주·월 보기의 날짜 칸과 전원 줄이 함께 쓴다.
  const { holidays } = usePublicHolidays();
  // 모바일은 표 대신 날짜별 목록(MobileAgenda) — 구조가 달라 CSS 만으로는 안 된다.
  const isMobile = useIsMobile();
  const library = useLibraryStore();
  // BO 대행 '읽기 전용'이면 이 화면의 쓰기 컨트롤을 전부 비활성으로 돌린다(검수 S1).
  // 숨기지 않는다 — 버튼은 제자리에 두고 왜 못 누르는지만 title로 말한다.
  const write = useWriteGate();
  const [view, setView] = useState<'day' | 'week' | 'month'>(() => {
    try {
      const saved = localStorage.getItem('schedule-calendar-view');
      return saved === 'day' || saved === 'month' ? saved : 'week';
    } catch { return 'week'; }
  });
  useEffect(() => {
    try { localStorage.setItem('schedule-calendar-view', view); } catch { /* Storage may be unavailable. */ }
  }, [view]);
  const [anchor, setAnchor] = useState(todayISO);
  // 보기 필터는 '보여줄 디바이스 집합' 하나로 다룬다. 그룹 체크는 그 집합을 한 번에
  // 켜고 끄는 단축키일 뿐이라, 그룹 두 개나 기기 다섯 대 같은 조합도 그대로 만들어진다.
  // null = 모든 디바이스(필터 없음), [] = 하나도 고르지 않음. 둘을 나눠야 '모든 디바이스'를 눌러
  // 전부 해제할 수 있다(SCDS-82 재검수 — 예전에는 빈 배열이 곧 전체라 해제가 되지 않았다).
  const [filterIds, setFilterIds] = useState<string[] | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const filterRef = useRef<HTMLDivElement | null>(null);
  // 바깥을 누르거나 Esc를 누르면 닫는다 — 고르는 동안에는 열린 채로 둔다.
  useEffect(() => {
    if (!filterOpen) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (filterRef.current && target && filterRef.current.contains(target)) return;
      setFilterOpen(false);
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setFilterOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [filterOpen]);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  // 승인 관리는 화면을 갈아끼우지 않고 캘린더 위에 겹쳐 띄운다.
  const [reviewOpen, setReviewOpen] = useState(() => window.location.hash.includes('section='));
  const [assignments, setAssignments] = useState<ScheduleAssignment[]>([]);
  // 편성이 가리키는 승인 버전 플레이리스트. 목록 API가 주지 않으므로 여기서 따로 읽는다.
  const approvedPlaylists = useApprovedPlaylists(assignments, work.scope?.actor.orgId);
  // 알림에서 들어오면 링크의 편성으로 곧장 연다 — 알림은 종류에 따라 승인 관리 모달 또는 편성 정보로.
  //   #/schedule?assignment=<id>&date=YYYY-MM-DD[&section=review]
  const openedFromLink = useRef<string | null>(null);
  // 어시스턴트 바로가기(#/schedule?section=review)는 편성 id 없이 승인 관리만 연다. 같은 해시로
  // 편성이 다시 읽혀도 한 번만 연다 — 닫은 뒤 목록 새로고침마다 다시 뜨면 안 된다.
  const openedSection = useRef<string | null>(window.location.hash.includes('section=') ? window.location.hash : null);
  // 링크로 연 창을 닫으면 주소의 편성 id를 지우고 기억도 푼다. 그래야 같은 알림을 다시
  // 눌렀을 때 또 열린다(SCDS-76) — 주소가 그대로면 목록 새로고침마다 다시 뜨므로 둘은 같이 푼다.
  const releaseLink = () => {
    openedFromLink.current = null; openedSection.current = null;
    const [path, query] = window.location.hash.split('?');
    if (query) window.location.replace(path || '#/schedule');
  };
  const closeReview = () => { setReviewOpen(false); releaseLink(); };
  const closeDialog = () => { setDialog(null); releaseLink(); };
  useEffect(() => {
    const apply = () => {
      const query = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
      const id = query.get('assignment'); const date = query.get('date');
      if (!id && query.get('section')) {
        if (openedSection.current !== window.location.hash) { openedSection.current = window.location.hash; setReviewOpen(true); }
        return;
      }
      if (!id || openedFromLink.current === id) return;
      const target = assignments.find(a => a.id === id);
      if (!target) return;
      openedFromLink.current = id;
      if (date) setAnchor(date);
      if (query.get('section')) setReviewOpen(true);
      else setDialog({ mode: 'edit', assignment: target, date: date ?? target.startDate });
    };
    apply();
    window.addEventListener('hashchange', apply);
    return () => window.removeEventListener('hashchange', apply);
  }, [assignments]);
  const mutationEpoch = useRef(0);
  const saves = useRef(new Map<string, { tail: Promise<void>; confirmed: ScheduleAssignment; version: number }>());
  const [assignmentError, setAssignmentError] = useState('');
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  // 한 번 그린 뒤에는 뒤에서 무엇을 다시 읽어도 표를 비우지 않는다.
  // 저장하면 서버가 변경을 알리고 권한 범위를 다시 읽는데, 그때마다 화면을 갈면 깜빡인다.
  const painted = useRef(false);
  const [nowMinute, setNowMinute] = useState(nowMinuteSeoul);
  // 썸네일 띠에 쓸 미디어 — 다른 라이브러리 자료와 함께 한 번에 읽는다.
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [hoverDevice, setHoverDevice] = useState<DeviceHover | null>(null);
  // 중앙 관리자 편성을 넘어서려는 순서 변경은 물어보고 나서 승인 요청으로 보낸다.
  // 반복 일정은 한 번 손대면 모든 날짜가 함께 바뀐다 — 적용 전에 묻는다.
  const [confirmRepeat, setConfirmRepeat] = useState<{ summary: string; apply: () => void } | null>(null);
  // 이 화면에서 한 변경은 되돌릴 수 있다. 실수로 끌어 놓았을 때 원래대로 돌아갈 자리가 필요하다.
  const [history, setHistory] = useState<{ past: HistoryStep[]; future: HistoryStep[] }>({ past: [], future: [] });

  // 그리드가 가로로 잘렸는지, 그래서 어느 날짜가 안 보이는지 재 둔다(검수 S6).
  const boardRef = useRef<HTMLDivElement | null>(null);
  const [boardScroll, setBoardScroll] = useState({ scrollLeft: 0, clientWidth: 0, scrollWidth: 0 });

  // 현재시각 선은 1분에 한 번만 움직이면 된다.
  useEffect(() => {
    const timer = setInterval(() => setNowMinute(nowMinuteSeoul()), 30_000);
    return () => clearInterval(timer);
  }, []);
  // 진단 폴링은 화면에 보이는 기기 전부를 한 번에 본다. 훅이라 이른 반환보다 위에 있어야 한다.
  const deviceDiagnostics = useDeviceDiagnostics((work.scope?.devices ?? []).filter(d => !d.revokedAt).map(d => d.id));

  useEffect(() => {
    let active = true;
    if (!work.scope) return;
    const refresh = () => {
      if (saves.current.size) return;
      const epoch = mutationEpoch.current;
      void workflowJson<ScheduleAssignment[]>('schedule-assignments')
        .then(values => {
          if (!active || epoch !== mutationEpoch.current || saves.current.size) return;
          setAssignmentError('');
          // 내용이 그대로면 상태를 갈지 않는다 — 같은 값으로 다시 그리면 화면이 흔들린다.
          setAssignments(current => JSON.stringify(current) === JSON.stringify(values) ? current : values);
        })
        .catch(e => { if (active && epoch === mutationEpoch.current) setAssignmentError(e instanceof Error ? e.message : String(e)); });
    };
    refresh();
    const timer = setInterval(refresh, 30_000);
    // 어시스턴트가 편성을 등록·수정하면 30초를 기다리지 않고 바로 다시 읽는다.
    window.addEventListener(ASSIGNMENTS_CHANGED_EVENT, refresh);
    return () => { active = false; clearInterval(timer); window.removeEventListener(ASSIGNMENTS_CHANGED_EVENT, refresh); };
  }, [work.scope, revision]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    void Promise.all([library.loadScheduleTypes(), library.loadTimelineBlocks(), library.loadPlaylists(),
      library.loadNotices(),
      api.listMedia().then(items => { if (active) setMedia(items); }).catch(() => { if (active) setMedia([]); })])
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [library.loadScheduleTypes, library.loadTimelineBlocks, library.loadPlaylists, work.scope?.actor.orgId]);
  // 스크롤 컨테이너의 실제 치수를 따라간다. 표가 아직 없을 때(불러오는 중)는 ref가 비어 있으므로
  // loading·scope도 의존에 넣어 표가 붙은 뒤 다시 건다.
  useEffect(() => {
    const node = boardRef.current;
    if (!node) return;
    const read = () => setBoardScroll(current =>
      current.scrollLeft === node.scrollLeft && current.clientWidth === node.clientWidth && current.scrollWidth === node.scrollWidth
        ? current
        : { scrollLeft: node.scrollLeft, clientWidth: node.clientWidth, scrollWidth: node.scrollWidth });
    read();
    node.addEventListener('scroll', read, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(read);
    observer?.observe(node);
    if (node.firstElementChild) observer?.observe(node.firstElementChild);
    return () => { node.removeEventListener('scroll', read); observer?.disconnect(); };
  }, [view, anchor, filterIds, loading, work.loading, work.scope]);

  const shell = (body: ReactNode, actions?: ReactNode, controls?: ReactNode) =>
    <PageContainer variant="full" className="flex h-full min-h-0 flex-col bg-bg">
      <header className="flex-none px-4 pt-4 md:px-6 md:pt-6">
        <PageTitle className="mb-0" title="스케줄" actions={actions} />
        {controls}
      </header>
      <div className="flex min-h-0 flex-1 flex-col">{body}</div>
    </PageContainer>;

  const scope = work.scope;
  if (scope && !loading) painted.current = true;
  if (!scope || (!painted.current && (work.loading || loading)))
    return shell(<p role="status" className="px-6 py-4">편성표를 불러오는 중…</p>);
  // 담당 그룹이 아직 없는 계정은 오류가 아니다 — work.error 분기보다 먼저 걸러야
  // 403이 빨간 배너로 새지 않는다(2026-09-17 대교).
  if (hasNoAssignedGroups(work.scope?.actor))
    return shell(<div className="px-6 py-4"><NoAssignedGroupsNotice title={NO_GROUP_TITLES.schedule} onRetry={work.reload} /></div>);
  if (work.error) return shell(<div className="px-6 py-4"><p role="alert">{work.error}</p><Button onClick={work.reload}>다시 불러오기</Button></div>);

  const actor = scope.actor;
  // 역할로 본 편집 권한 + 대행 읽기 전용을 한 게이트로 합친다(검수 S1).
  // 왜 못 쓰는지가 버튼마다 달라지므로 이유를 가진 게이트 하나를 만들어 돌려 쓴다.
  const roleCanWrite = canWriteSchedules(actor);
  const scheduleWrite = write.and(roleCanWrite, SCHEDULE_WRITE_DENIED);
  const canWrite = !scheduleWrite.blocked;
  // 승인 관리는 요청을 올리거나 결재하는 사람의 화면이다. 조회자는 남의 요청 내용까지 보게 되므로
  // 버튼도, 알림·주소로 여는 길도 두지 않는다(SCDS-81). 대행 읽기 전용은 역할이 아니라 막지 않는다.
  const canReview = roleCanWrite;
  const devices = scope.devices.filter(d => !d.revokedAt && (actor.central || actor.groups.some(g => g.groupId === d.groupId)));
  const groups = [...scope.groups.filter(g => actor.central || actor.groups.some(x => x.groupId === g.id))
    .map(g => ({ id: g.id as string | null, name: g.name })), ...(actor.central ? [{ id: null, name: '그룹 미지정' }] : [])]
    .filter(g => devices.some(d => d.groupId === g.id));
  // 원본 + 편성에 박힌 승인본. 이름·썸네일을 찾을 때 둘 다 본다(고를 수 있는 것은 원본뿐 — BlockTimelineEditor).
  const playlists = [...library.playlists, ...approvedPlaylists];
  const templates = library.scheduleTypes.filter(type => !!actor.role && (!actor.enforced || actor.central || actor.groups.some(g => g.groupId === type.groupId)));
  // 보기 필터는 권한으로 제한된 목록에만 적용한다. 편집·승인 대상은 바꾸지 않는다.
  const groupKey = (id: string | null) => id === null ? '__ungrouped__' : id;
  // 권한 밖으로 사라진 기기가 필터에 남아 화면을 비우지 않도록 매번 걸러낸다.
  const picked = filterIds && new Set(filterIds.filter(id => devices.some(d => d.id === id)));
  const visibleDevices = picked ? devices.filter(d => picked.has(d.id)) : devices;
  const visibleGroups = groups.filter(g => visibleDevices.some(d => d.groupId === g.id));
  const filterQueryText = filterQuery.trim().toLowerCase();
  // 그룹 이름이 검색어에 걸리면 그 그룹은 통째로 남기고, 아니면 기기 이름으로 추린다.
  const filterSections = groups
    .map(g => ({ key: groupKey(g.id), name: g.name, members: devices.filter(d => d.groupId === g.id) }))
    .map(section => filterQueryText && !section.name.toLowerCase().includes(filterQueryText)
      ? { ...section, members: section.members.filter(d => d.name.toLowerCase().includes(filterQueryText)) }
      : section)
    .filter(section => section.members.length > 0);
  const allSections = groups.map(g => ({ name: g.name, members: devices.filter(d => d.groupId === g.id) }));
  // 전부 켜져 있으면 끄고, 하나라도 빠져 있으면 모두 켠다.
  // '모든 디바이스'일 때는 모든 그룹·기기를 선택된 것으로 보여 주고(SCDS-82), 거기서 하나를 끄면
  // 나머지 전부가 남는다. 결과가 전체와 같으면 다시 '모든 디바이스'로.
  const toggleFilter = (ids: string[]) => setFilterIds(current => {
    const next = new Set(current ? current.filter(id => devices.some(d => d.id === id)) : devices.map(d => d.id));
    const turnOff = ids.every(id => next.has(id));
    for (const id of ids) { if (turnOff) next.delete(id); else next.add(id); }
    return next.size === devices.length ? null : [...next];
  });
  const shown = (id: string) => !picked || picked.has(id);
  // 기기가 있는데 필터로 전부 뺐으면 빈 표 대신 고르라고 안내한다.
  const boardNotice = !devices.length ? '등록된 디바이스가 없습니다. 디바이스를 등록하면 편성표가 표시됩니다.'
    : !visibleDevices.length ? '선택된 디바이스가 없습니다. 디바이스 필터에서 볼 디바이스를 골라 주세요.' : null;

  // 기기 상태는 디바이스 목록과 같은 진단 폴링을 그대로 쓴다.
  const diagnostics = deviceDiagnostics.data;

  const days = view === 'day' ? [anchor]
    : view === 'week' ? Array.from({ length: 7 }, (_, i) => addDays(weekStart(anchor), i)) : monthDays(anchor);
  const move = (dir: 1 | -1) => setAnchor(view === 'day' ? addDays(anchor, dir)
    : view === 'week' ? addDays(anchor, dir * 7)
      : `${addDays(`${anchor.slice(0, 7)}-15`, dir * 30).slice(0, 7)}-01`);
  const headline = view === 'day' ? dateLabel(anchor)
    : view === 'week'
      ? `${dateLabel(days[0]!)} – ${Number(days[6]!.slice(5, 7))}. ${Number(days[6]!.slice(8, 10))} (${DAY_LABELS[weekdayOf(days[6]!)]})`
      : `${Number(anchor.slice(0, 4))}. ${Number(anchor.slice(5, 7))}`;
  const openCreate = (preset: CreatePreset) => {
    if (!canWrite) return;
    // 빗금(전원 꺼짐) 시간을 눌러 편성을 시작하면 바로 알린다. 저장 때 한 번 더 확인한다.
    if (preset.startMinute !== undefined && preset.endMinute !== undefined) {
      const dark = (preset.deviceIds ?? []).map(id => devices.find(d => d.id === id)).filter(d => !!d)
        .filter(d => (powerRangesOn(d.powerSchedule, preset.date, holidays)?.off ?? []).some(r => preset.startMinute! < r.end && r.start < preset.endMinute!));
      if (dark.length) toast(`${dark.map(d => d.name).join(', ')}: 이 시간은 기기 전원이 꺼져 있어 편성해도 재생되지 않습니다.`, 'error');
    }
    setDialog({ mode: 'create', preset });
  };
  // 결재할 수 있는 요청만 센다 — 그룹 승인 그룹의 그룹 관리자도 결재자라 숫자가 보여야 한다(SCDS-101).
  // 중앙 승인 그룹의 요청은 그룹 관리자가 처리할 수 없으니 그 사람의 숫자에서는 뺀다.
  const pendingCount = assignments.filter(a => a.approval?.status === 'pending' && canDecideAssignment(scope, a)).length;
  const canDecideAny = actor.central || scope.groups.some(g => g.approvalMode === 'group'
    && actor.groups.some(x => x.groupId === g.id && x.role === 'Admin'));
  // 편성표에는 재생될 수 있는 것과 대기 중인 것만 그린다.
  // 반려된 요청은 승인 관리에서만 본다(원본이 있으면 원본이 그대로 남아 재생된다).
  // 처리가 끝난 삭제 요청도 그리지 않는다 — 지워졌거나(승인) 원본이 그대로 남아 있다(반려).
  const boardAssignments = assignments.filter(a => a.approval?.status !== 'rejected' && a.approval?.status !== 'cancelled'
    && !(a.approval?.action === 'delete' && a.approval.status !== 'pending'));
  // 지금 보고 있는 기간·기기에 편성이 하나도 없으면 빈 표만 두지 않는다(검수 S15).
  const rangeWord = view === 'day' ? '이 날짜에' : view === 'week' ? '이번 주에' : '이번 달에';
  const emptyRange = visibleDevices.length > 0 && !boardAssignments.some(a =>
    visibleDevices.some(d => d.id === a.deviceId) && days.some(day => assignmentOccursOn(a, day)));
  // 가로로 잘려 안 보이는 날짜(검수 S6). 월 뷰는 원래 가로로 길어 같은 단서를 그대로 쓴다.
  const hiddenCue = view === 'day' ? '' : hiddenDaysCue(days, boardScroll, { month: view === 'month' });
  // 드롭과 동시에 반영한다. 같은 일정의 연속 조작은 순서대로 저장하고 오래된 응답은 화면을 덮지 않는다.
  // 수정은 언제나 함께 올린 기기 전부에 걸린다(scope=batch) — 한 기기만 따로 고친 행이 남아 다른 저장에 덮이는 일을 없앤다.
  // 기기별로 다른 것은 제외 날짜뿐이라, 날짜를 옮길 때는 기기마다 제외 날짜를 같은 만큼 옮겨 보낸다.
  const saveChange = (previous: ScheduleAssignment, next: ScheduleAssignment,
    send?: (value: ScheduleAssignment) => Promise<ScheduleAssignment[]>) => {
    if (!canWrite || !canModifyAssignment(actor, previous)) return;
    mutationEpoch.current++;
    const entry = saves.current.get(previous.id) ?? { tail: Promise.resolve(), confirmed: previous, version: 0 };
    saves.current.set(previous.id, entry);
    const version = ++entry.version;
    const batch = assignments.filter(a => a.batchId === next.batchId && (a.id === next.id || !isAssignmentProposal(a)));
    const delta = daysBetween(previous.startDate, next.startDate);
    const skipDatesByDevice = Object.fromEntries(batch.map(a => [a.deviceId,
      a.id === next.id ? next.skipDates ?? [] : (a.skipDates ?? []).map(day => addDays(day, delta))]));
    setAssignments(values => values.map(a => a.id === next.id ? next : a));
    entry.tail = entry.tail.then(async () => {
      try {
        const saved = send ? await send(next) : await workflowJson<ScheduleAssignment[]>(`schedule-assignments/${encodeURIComponent(next.id)}?scope=batch`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: next.name, deviceIds: [...new Set([next.deviceId, ...batch.map(a => a.deviceId)])], layer: next.layer,
            blocks: next.blocks.map(({ playlistId, startMinute, endMinute }) => ({ playlistId, startMinute, endMinute })),
            startDate: next.startDate, endDate: next.endDate, repeat: next.repeat, repeatEnd: next.repeatEnd,
            calendar: next.calendar ?? 'solar', skipDatesByDevice }) });
        // 승인이 필요한 사람의 변경은 원본 대신 복제본(요청)으로 돌아온다 — 원본은 그대로 두고 요청을 옆에 얹는다.
        const proposal = saved.find(a => a.approval?.replacesId === next.id && a.approval.status === 'pending');
        entry.confirmed = proposal ? previous : saved.find(a => a.id === next.id) ?? next;
        if (version === entry.version && proposal) {
          setAssignments(values => [...values.map(a => a.id === next.id ? previous : a), ...saved.filter(a => !values.some(v => v.id === a.id))]);
          toast('수정 승인 요청을 보냈습니다. 승인 전까지 기존 스케줄이 그대로 재생됩니다.', 'success');
        } else if (version === entry.version) {
          // 함께 올린 다른 기기 행도 같은 내용으로 돌아온다 — 응답에 있는 행은 모두 갈아 끼운다.
          const byId = new Map(saved.map(a => [a.id, a]));
          setAssignments(values => values.map(a => a.id === next.id ? entry.confirmed : byId.get(a.id) ?? a));
          if (entry.confirmed.approval?.status === 'pending') toast('변경 사항을 저장하고 승인 요청을 보냈습니다.', 'success');
        }
      } catch (e) {
        if (version === entry.version) setAssignments(values => values.map(a => a.id === next.id ? entry.confirmed : a));
        toast(`스케줄 변경을 저장하지 못했습니다: ${describeApiError(e)}`, 'error');
      } finally {
        mutationEpoch.current++;
        if (version === entry.version) saves.current.delete(next.id);
      }
    });
  };
  const record = (step: HistoryStep) => setHistory(h => ({ past: [...h.past, step].slice(-50), future: [] }));
  const undo = () => {
    const step = history.past.at(-1);
    if (!step) return;
    step.undo();
    setHistory(h => ({ past: h.past.slice(0, -1), future: [step, ...h.future] }));
  };
  const redo = () => {
    const step = history.future[0];
    if (!step) return;
    step.redo();
    setHistory(h => ({ past: [...h.past, step], future: h.future.slice(1) }));
  };

  /** 반복 일정이거나 여러 기기에 함께 올린 편성이면, 모든 날짜·모든 기기에 함께 적용된다는 사실을 먼저 알리고 확인해야 저장한다. */
  const applyEdit = (assignment: ScheduleAssignment, next: ScheduleAssignment, what: string) => {
    const commit = () => {
      saveChange(assignment, next);
      record({ undo: () => saveChange(next, assignment), redo: () => saveChange(assignment, next) });
    };
    const batchCount = new Set(assignments.filter(a => a.batchId === assignment.batchId && !isAssignmentProposal(a)).map(a => a.deviceId)).size;
    if (assignment.repeat.kind === 'none' && batchCount <= 1) { commit(); return; }
    const where = [batchCount > 1 ? `${batchCount}대 기기에 함께 올라가 있` : '', assignment.repeat.kind !== 'none' ? `${describeAssignmentRule(assignment)} 반복됩니다` : '']
      .filter(Boolean).join(batchCount > 1 && assignment.repeat.kind !== 'none' ? '고 ' : '') + (assignment.repeat.kind === 'none' ? '습니다' : '');
    const applies = [batchCount > 1 ? '모든 기기' : '', assignment.repeat.kind !== 'none' ? '모든 날짜' : ''].filter(Boolean).join('의 ');
    setConfirmRepeat({
      summary: `'${assignment.name}'은(는) ${where}. ${what}을(를) 바꾸면 ${applies}에 함께 적용됩니다.`,
      apply: commit,
    });
  };
  const moveBlock = (assignment: ScheduleAssignment, blockId: string, startMinute: number, endMinute: number) => {
    applyEdit(assignment, { ...assignment, blocks: assignment.blocks.map(b => b.id === blockId ? { ...b, startMinute, endMinute } : b) }, '시간');
  };

  const moveDates = (assignment: ScheduleAssignment, delta: number) => {
    if (!canWrite || !canModifyAssignment(actor, assignment) || !delta) return;
    applyEdit(assignment, { ...assignment, ...shiftAssignmentDates(assignment, delta) }, '날짜');
  };

  /**
   * 대화상자에서 만든·고친·지운 편성도 한 칸씩 되돌린다.
   * 다시 만들면 id가 새로 생기므로, 각 단계는 지금 서버에 있는 편성을 계속 따라간다.
   */
  const recreate = (items: readonly ScheduleAssignment[]) =>
    workflowJson<ScheduleAssignment[]>('schedule-assignments', { method: 'POST',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(assignmentPayload(items)) });
  const overwrite = (id: string, items: readonly ScheduleAssignment[]) =>
    workflowJson<ScheduleAssignment[]>(`schedule-assignments/${encodeURIComponent(id)}?scope=batch`, { method: 'PUT',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(assignmentPayload(items)) });
  const erase = (items: readonly ScheduleAssignment[]) =>
    workflowJson(`schedule-assignments/${encodeURIComponent(items[0]!.id)}?scope=batch`, { method: 'DELETE' });
  const run = (work: Promise<unknown>) => {
    void work.then(() => setRevision(v => v + 1))
      .catch(e => toast(`되돌리지 못했습니다: ${describeApiError(e)}`, 'error'));
  };
  const recordMutation = (mutation: ScheduleMutation) => {
    setRevision(v => v + 1);
    if (mutation.kind === 'create') {
      let live = mutation.after;
      record({ undo: () => run(erase(live)), redo: () => run(recreate(live).then(saved => { live = saved; })) });
      return;
    }
    if (mutation.kind === 'delete') {
      let live = mutation.before;
      record({ undo: () => run(recreate(live).then(saved => { live = saved; })), redo: () => run(erase(live)) });
      return;
    }
    let live = mutation.after;
    const swap = (target: ScheduleAssignment[]) => () =>
      run(overwrite(live[0]!.id, target).then(saved => { live = saved.length ? saved : live; }));
    record({ undo: swap(mutation.before), redo: swap(mutation.after) });
  };

  return shell(<>
    {reviewOpen && canReview && <ModalShell title="승인 관리" onClose={closeReview}
      panelClassName="flex w-[min(96vw,64rem)] flex-col h-[min(86vh,44rem)]"
      footer={<Button onClick={closeReview}>닫기</Button>}>
      <ScheduleReview assignments={assignments} scope={scope} canCreate={scheduleWrite}
        onChanged={() => setRevision(v => v + 1)}
        onCreate={() => { closeReview(); openCreate({ date: todayISO() }); }}
        onOpen={assignment => { closeReview(); setDialog({ mode: 'edit', assignment, date: assignment.startDate }); }} />
    </ModalShell>}
    {/* 지금 보는 기간에 아무것도 없으면 빈 표만 두지 않고 다음 행동을 알려 준다(검수 S15).
        버튼은 두지 않는다 — 도구줄의 '＋ 스케줄 추가'와 한 화면에 두 개가 되어 헷갈렸다(SCDS-89).
        대신 그 버튼이 어디 있는지 글로 가리킨다. */}
    {emptyRange && <div role="status" className="mx-4 mt-4 flex flex-wrap items-center gap-3 rounded border border-line bg-panel-2 px-4 py-3 md:mx-6">
      <span className="text-sm text-text-dim">{rangeWord} 배정된 스케줄이 없습니다.{!scheduleWrite.blocked ? ' 위의 ＋ 스케줄 추가로 등록할 수 있습니다.' : ''}</span>
    </div>}
    {/* 표는 내용만큼만 차지한다 — 남는 높이까지 늘리면 쓰지 않는 빈 칸이 테두리 안에 남는다.
        위아래 여백(mt-4 + mb-6 = 2.5rem)을 뺀 만큼만 높이를 허용해 스크롤이 잘리지 않게 한다.
        바깥 상자는 가려진 날짜 단서를 얹기 위한 좌표계일 뿐이다 — 스크롤은 안쪽이 한다. */}
    {isMobile ? <div className="min-h-0 flex-1 overflow-auto px-4 pb-4 pt-3">
      {boardNotice ? <p role="status" className="p-8 text-center text-text-dim">{boardNotice}</p>
        : <MobileAgenda view={view} days={days} today={todayISO()} groups={visibleGroups} devices={visibleDevices}
          assignments={boardAssignments} holidays={holidays}
          onEdit={(a, d) => setDialog({ mode: 'edit', assignment: a, date: d })} onCreate={canWrite ? openCreate : undefined} />}
    </div> : <div className="relative mx-6 mb-6 mt-4 flex max-h-[calc(100%-2.5rem)] min-h-0">
      {/* 전역 스크롤바는 트랙이 투명해 "더 있다"는 사실이 보이지 않는다. 이 표에서만 트랙을
          칠하고 자리를 미리 잡아(gutter stable) 스크롤바를 상시 표시로 바꾼다(검수 S6). */}
      <div ref={boardRef}
        className="min-w-0 flex-1 overflow-auto rounded border border-line bg-panel [scrollbar-color:var(--color-line-strong)_var(--color-panel-2)] [scrollbar-gutter:stable] [&::-webkit-scrollbar-track]:bg-panel-2 [&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar]:w-2.5">
        {boardNotice ? <p role="status" className="p-12 text-center text-text-dim">{boardNotice}</p>
          : view === 'day'
            ? <DayBoard date={anchor} groups={visibleGroups} devices={visibleDevices} assignments={boardAssignments} diagnostics={diagnostics}
              nowMinute={nowMinute} canWrite={canWrite} holidays={holidays}
              canEdit={a => canModifyAssignment(actor, a)} onMove={moveBlock}
              onEdit={(a, d) => setDialog({ mode: 'edit', assignment: a, date: d })} onCreate={openCreate}
              onHoverDevice={setHoverDevice} />
            : <GridBoard days={days} groups={visibleGroups} devices={visibleDevices} assignments={boardAssignments} month={view === 'month'}
              diagnostics={diagnostics} today={todayISO()} holidays={holidays} canWrite={canWrite} canEdit={a => canModifyAssignment(actor, a)} onMove={moveDates}
              onEdit={(a, d) => setDialog({ mode: 'edit', assignment: a, date: d })} onCreate={canWrite ? openCreate : undefined}
              onHoverDevice={setHoverDevice} />}
      </div>
      {/* 오른쪽으로 무엇이 잘렸는지 글자로 말한다 — 주말이 사라진 주간 캘린더는 쓸 수 없다. */}
      {hiddenCue && <span role="status" className="pointer-events-none absolute right-5 top-2 rounded border border-line bg-panel-2/95 px-2 py-0.5 text-[12px] font-medium text-text-dim shadow-sm">
          → {hiddenCue}</span>}
    </div>}
    {/* 기기에 커서를 올리면 지금 그 화면에 무엇이 나오는지 알려준다. */}
    {hoverDevice && <DeviceStatusCard hover={hoverDevice} diagnostic={diagnostics[hoverDevice.device.id]}
      playlists={playlists} playing={nowPlayingOn(assignments, hoverDevice.device.id, todayISO(), nowMinute)} />}
    {/* 중앙 관리자 편성보다 먼저 재생하려면 승인을 받아야 한다 — 바꾸기 전에 묻는다. */}
    {/* 반복 일정은 하루만 고칠 수 없다 — 무엇이 함께 바뀌는지 먼저 알린다. */}
    {confirmRepeat && <ModalShell title="스케줄 변경" onClose={() => setConfirmRepeat(null)} size="sm"
      footer={<>
        <Button onClick={() => setConfirmRepeat(null)}>취소</Button>
        <Button variant="primary" onClick={() => { confirmRepeat.apply(); setConfirmRepeat(null); }}>전체 적용</Button>
      </>}>
      <p className="text-sm">{confirmRepeat.summary}</p>
    </ModalShell>}
    {dialog && <AssignmentDialog scope={scope} templates={templates} blocks={library.timelineBlocks} playlists={playlists}
      media={media} notices={library.notices}
      devices={devices} groups={scope.groups} assignments={assignments}
      initial={dialog.mode === 'edit' ? dialog.assignment : null}
      occurrenceDate={dialog.mode === 'edit' ? dialog.date : null}
      preset={dialog.mode === 'create' ? dialog.preset : null}
      batchSize={dialog.mode === 'edit' ? assignments.filter(a => a.batchId === dialog.assignment.batchId).length : 0}
      batchDevices={dialog.mode === 'edit' ? assignments.filter(a => a.batchId === dialog.assignment.batchId).map(a => a.deviceId) : []}
      onClose={closeDialog}
      onSaved={change => { closeDialog(); if (change) recordMutation(change); else setRevision(v => v + 1); }} />}
  </>,
  <div className="flex flex-wrap items-center gap-2">
    {/* 대기 건수는 결정권이 있는 사람에게만 숫자로 보인다. 결정권이 있으면 0일 때도 뱃지 자리를
        비우지 않는다 — 건수가 1이 되는 순간 버튼 폭이 튀면 안 된다("조작부는 움직이지 않는다", 검수 C16).
        0은 읽어 줄 값이 아니므로 aria-hidden으로 가리고, 대신 버튼 title이 건수를 말한다. */}
    {canReview && <Button onClick={() => setReviewOpen(true)}
      aria-label={canDecideAny && pendingCount ? `승인 관리 ${pendingCount}` : '승인 관리'}
      title={canDecideAny ? `승인 대기 ${pendingCount}건` : '스케줄 승인 요청 보기'}>승인 관리
      {canDecideAny && <span aria-hidden
        className={`ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[12px] font-semibold leading-none ${pendingCount
          ? 'bg-accent-solid text-white' : 'bg-panel-2 text-text-dim'}`}>{pendingCount}</span>}</Button>}
    {/* 권한이 없어도 숨기지 않는다 — 같은 자리에 비활성으로 두고 이유만 말한다(검수 S1). */}
    {/* 일간 보기에서는 지금 보는 날짜로 연다 — 빈 기간 안내 띠의 버튼을 없애며 그 동작을 여기로 옮겼다(SCDS-89). */}
    <Button variant="primary" aria-label="스케줄 추가"
      {...scheduleWrite.props} onClick={() => openCreate({ date: view === 'day' ? anchor : todayISO() })}>＋ 스케줄 추가</Button>
  </div>,
  <>
    {/* 좌정렬 고정 컨트롤 — 기간 텍스트 길이가 변해도 버튼이 움직이지 않는다. */}
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <Segmented value={view} onChange={setView}
        options={[{ value: 'day', label: '일' }, { value: 'week', label: '주' }, { value: 'month', label: '월' }]} />
      <Button aria-label="이전" onClick={() => move(-1)}>‹</Button>
      <Button onClick={() => setAnchor(todayISO())}>오늘</Button>
      <Button aria-label="다음" onClick={() => move(1)}>›</Button>
      <span className="text-sm font-medium tabular-nums" aria-label="조회 기간">{headline}</span>
      <div className="relative ml-auto flex flex-wrap items-center gap-2" ref={filterRef} role="group" aria-label="스케줄 보기 필터">
        {/* 이 화면에서 끌어 옮기거나 순서를 바꾼 것을 한 칸씩 되돌린다.
            비활성일 때 title에 동작 이름만 적으면 왜 못 누르는지 알 수 없다 — 이유를 적는다(검수 S15).
            권한이 없어도 숨기지 않는다(검수 S1). */}
        {(() => {
          // 되돌리기는 끌어 옮긴 변경을 위한 것 — 모바일은 끌기가 없어 자리만 차지한다.
          if (isMobile) return null;
          const undoGate = scheduleWrite.and(!!history.past.length, '되돌릴 변경이 없습니다');
          const redoGate = scheduleWrite.and(!!history.future.length, '다시 실행할 변경이 없습니다');
          return <><Button aria-label="되돌리기" className="!px-2.5" onClick={undo}
            {...undoGate.props} title={undoGate.reason ?? '되돌리기'}><UndoIcon /></Button>
          <Button aria-label="다시 실행" className="!px-2.5" onClick={redo}
            {...redoGate.props} title={redoGate.reason ?? '다시 실행'}><UndoIcon flip /></Button></>;
        })()}
        {/* 필터는 아이콘 하나로 접어 둔다 — 걸린 내용은 열었을 때 안에서 읽는다. */}
        <Button aria-label="디바이스 필터" aria-expanded={filterOpen}
          title={`디바이스 필터 · ${viewFilterSummary(picked && [...picked], allSections)}`}
          className={`relative !px-2.5 ${picked ? '!border-accent !text-accent' : ''}`}
          onClick={() => setFilterOpen(open => !open)}>
          <FunnelIcon />
          {picked && <span aria-hidden
            className="absolute -right-1 -top-1 min-w-4 rounded-full bg-accent px-1 text-center font-mono text-[10px] font-semibold leading-4 text-white">
            {picked.size}</span>}
        </Button>
        {filterOpen && <div role="dialog" aria-label="디바이스 필터 선택"
          className="absolute right-0 top-full z-40 mt-1 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2 rounded border border-line bg-panel p-2 shadow-lg">
          {/* 무엇이 걸려 있고 몇 대가 보이는지는 이 안에서만 밝힌다. */}
          <div className="flex shrink-0 items-center gap-2 text-[13px]">
            <span className="truncate font-semibold" aria-label="필터 요약">{viewFilterSummary(picked && [...picked], allSections)}</span>
            <span className="shrink-0 font-mono text-[11px] text-text-dim" role="status" aria-label="표시 디바이스 수">
              {visibleDevices.length}/{devices.length}대</span>
            <Button variant="ghost" aria-label="필터 초기화" title="필터 초기화" disabled={!picked}
              className="ml-auto !px-1.5 !py-1"
              onClick={() => { setFilterIds(null); setFilterQuery(''); }}><ResetIcon /></Button>
          </div>
          <input aria-label="그룹·디바이스 검색" value={filterQuery} placeholder="그룹·디바이스 이름 검색"
            onChange={event => setFilterQuery(event.target.value)}
            className="h-8 shrink-0 rounded border border-line bg-panel-2 px-2 text-[13px] outline-none focus:border-accent" />
          {/* 전체 선택·해제. 전부 켜져 있을 때 누르면 모두 끄고, 하나라도 빠져 있으면 모두 켠다(SCDS-82). */}
          <label className="flex shrink-0 items-center gap-2 border-b border-line px-1 pb-2 text-[13px] font-semibold">
            <input type="checkbox" aria-label="모든 디바이스" checked={!picked}
              ref={node => { if (node) node.indeterminate = !!picked?.size; }}
              onChange={() => setFilterIds(picked ? null : [])} />
            <span>모든 디바이스</span>
            <span className="ml-auto shrink-0 font-mono text-[11px] font-normal text-text-dim">{devices.length}대</span>
          </label>
          <div className="max-h-72 min-h-0 overflow-auto">
            {!filterSections.length && <p className="px-1 py-2 text-[13px] text-text-dim" role="status">검색 결과가 없습니다.</p>}
            {filterSections.map(section => {
              const chosen = section.members.filter(d => shown(d.id)).length;
              const whole = chosen === section.members.length;
              return <section key={section.key} className="border-b border-line py-1.5 last:border-b-0">
                <label className="flex items-center gap-2 px-1 text-[13px] font-semibold">
                  <input type="checkbox" aria-label={`${section.name} 전체`} checked={whole}
                    ref={node => { if (node) node.indeterminate = chosen > 0 && !whole; }}
                    onChange={() => toggleFilter(section.members.map(d => d.id))} />
                  <span className="truncate">{section.name}</span>
                  <span className={`ml-auto shrink-0 rounded px-1.5 py-0.5 font-mono text-[11px] font-normal ${chosen
                    ? 'bg-accent-soft text-accent' : 'text-text-dim'}`}>{chosen}/{section.members.length}대</span>
                </label>
                <div className="flex flex-wrap gap-1 px-1 pt-1.5">{section.members.map(d =>
                  <Chip key={d.id} active={shown(d.id)} onClick={() => toggleFilter([d.id])}
                    className="!py-0.5 text-[12px]">{d.name}</Chip>)}</div>
              </section>;
            })}
          </div>
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-line pt-2 text-[13px]">
            <Button variant="primary" className="!px-2.5 !py-1 !text-[13px]" onClick={() => setFilterOpen(false)}>완료</Button>
          </div>
        </div>}
      </div>
    </div>
    {assignmentError && <p role="alert" className="mt-2 text-sm text-red">편성 조회 실패: {assignmentError}{' '}
      <Button onClick={() => setRevision(v => v + 1)}>다시 불러오기</Button></p>}
  </>);
}

const laneStyle = { backgroundImage: 'linear-gradient(to right, var(--color-line, #e5e7eb) 1px, transparent 1px)', backgroundSize: `${100 / 24}% 100%` };

/** 이름을 비워 둔 스케줄에 붙는 기본 이름. */
const DEFAULT_SCHEDULE_NAME = '새로운 스케줄';

// ── 기기 이름칸 ────────────────────────────────────────────────────────────
// 편성표에서도 기기가 지금 살아 있는지 보여야 한다. 상태 점은 디바이스 목록과 같은 것을 쓴다.
type DeviceHover = { device: WorkflowScope['devices'][number]; rect: DOMRect };

const STATUS_LABEL = { online: '온라인', fault: '장애', offline: '오프라인' } as const;
const STATUS_COLOR = { online: 'bg-green', fault: 'bg-red', offline: 'bg-text-faint' } as const;

/**
 * 기기가 지금 내보내는 화면 그대로. 플레이어의 읽기 전용 미리보기를 축소해 넣는다.
 * 아무것도 재생하지 않을 때도 기기가 보여주는 대기 화면이 그대로 나온다.
 */
function DevicePreview({ device, width, maxHeight = width * 9 / 16 }: {
  device: WorkflowScope['devices'][number]; width: number; maxHeight?: number;
}) {
  // Resolution already describes the oriented display, as in the player preview.
  const hasResolution = device.resolutionW != null && device.resolutionW > 0
    && device.resolutionH != null && device.resolutionH > 0;
  const viewportWidth = hasResolution ? device.resolutionW! : device.orientation === 'portrait' ? 720 : 1280;
  const viewportHeight = hasResolution ? device.resolutionH! : device.orientation === 'portrait' ? 1280 : 720;
  const scale = Math.min(width / viewportWidth, maxHeight / viewportHeight);
  return <span className="relative block shrink-0 overflow-hidden rounded border border-line bg-black"
    style={{ width: viewportWidth * scale, height: viewportHeight * scale, boxSizing: 'content-box' }}>
    <iframe title={`${device.name} 현재 화면`} src={playerPreviewUrl(device.id)} loading="lazy" tabIndex={-1}
      width={viewportWidth} height={viewportHeight} className="pointer-events-none absolute left-0 top-0 border-0"
      style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }} />
  </span>;
}

/** 이름칸에는 점만 둔다. 자세한 상태는 커서를 올리면 나온다. */
function StatusDot({ device, diagnostic }: { device: WorkflowScope['devices'][number]; diagnostic?: DeviceDiagnostic | undefined }) {
  const status = diagnostic?.status;
  const label = status ? STATUS_LABEL[status] : '진단 대기';
  return <span role="img" aria-label={`${device.name} 상태 ${label}`} title={label}
    className={`inline-block h-2 w-2 shrink-0 rounded-full ${status ? STATUS_COLOR[status] : 'bg-line-strong'}`} />;
}

/** 지금 이 순간 그 기기에서 재생되는 블록. 승인받은 편성만 후보다(이벤트가 기본보다 앞선다). */
export function nowPlayingOn(assignments: readonly ScheduleAssignment[], deviceId: string, date: string, minute: number):
{ assignment: ScheduleAssignment; block: TimelineBlock } | null {
    const candidates = byAssignmentPrecedence(assignments.filter(a => a.deviceId === deviceId
      && isAssignmentPlayable(a) && assignmentOccursOn(a, date))).sort((a, b) =>
        Number(a.approval?.status === 'pending') - Number(b.approval?.status === 'pending')
        || Number(b.layer === 'event') - Number(a.layer === 'event'));
    for (const assignment of candidates) {
      const block = assignment.blocks.find(b => b.startMinute <= minute && minute < b.endMinute);
      if (block) return { assignment, block };
    }
  return null;
}

/** 실제 재생 후보와 같은 기준으로 블록을 재생/밀림 구간으로 나눈다. */
export function blockPlaybackSlices(assignment: ScheduleAssignment, block: TimelineBlock, assignments: readonly ScheduleAssignment[], date: string) {
  const candidates = assignments.filter(a => a.deviceId === assignment.deviceId && assignmentOccursOn(a, date));
  const points = [...new Set([block.startMinute, block.endMinute, ...candidates.flatMap(a => a.blocks
    .flatMap(b => [b.startMinute, b.endMinute]).filter(minute => minute > block.startMinute && minute < block.endMinute))])].sort((a, b) => a - b);
  const slices: { start: number; end: number; muted: boolean; winner: string | undefined }[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i]!, end = points[i + 1]!;
    const winner = nowPlayingOn(candidates, assignment.deviceId, date, start)?.assignment;
    const muted = winner?.id !== assignment.id;
    const previous = slices.at(-1);
    if (previous && previous.muted === muted && previous.winner === winner?.name) previous.end = end;
    else slices.push({ start, end, muted, winner: winner?.name });
  }
  return slices;
}

function DeviceName({ device, diagnostic, onHover }: {
  device: WorkflowScope['devices'][number]; diagnostic?: DeviceDiagnostic | undefined;
  onHover: (hover: DeviceHover | null) => void;
}) {
  // 상태 카드는 미리보기 아이콘에만 붙인다. 이름칸 전체에 걸어두면 목록을 훑기만 해도
  // 카드가 계속 튀어나와 달력을 가린다.
  const openCard = (event: { currentTarget: HTMLElement }) =>
    onHover({ device, rect: event.currentTarget.getBoundingClientRect() });
  return <div className="flex h-full min-w-0 flex-1 items-center pr-4">
    <span className="flex min-w-0 flex-1 items-center gap-1.5">
      <StatusDot device={device} {...(diagnostic ? { diagnostic } : {})} />
      <span className="truncate text-sm" title={device.name}>{device.name}</span>
    </span>
    <button type="button" aria-label={`${device.name} 미리보기 새 탭에서 열기`}
      title="현재 화면을 새 탭에서 미리보기"
      className="ml-2 flex h-8 w-8 shrink-0 items-center justify-center rounded border border-line bg-panel text-text-dim hover:border-accent hover:text-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
      onMouseEnter={openCard}
      onMouseLeave={() => onHover(null)}
      onFocus={openCard}
      onBlur={() => onHover(null)}
      onPointerDown={event => event.stopPropagation()}
      onClick={event => { event.stopPropagation(); onHover(null); window.open(playerPreviewUrl(device.id), '_blank', 'noopener'); }}>
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" />
        <path d="m10 8 5 2.5-5 2.5z" />
      </svg>
    </button>
  </div>;
}

/**
 * 기기에 커서를 올리면 지금 그 기기에서 무엇이 나오고 있는지 보여준다.
 * 템플릿 타임라인의 호버 미리보기와 같은 생각 — 시간 위의 한 점을 설명한다.
 */
function DeviceStatusCard({ hover, diagnostic, playing, playlists }: {
  hover: DeviceHover; diagnostic?: DeviceDiagnostic | undefined;
  playing: { assignment: ScheduleAssignment; block: TimelineBlock } | null; playlists: Playlist[];
}) {
  const { device, rect } = hover;
  const top = Math.min(rect.bottom + 8, window.innerHeight - 360);
  const left = Math.min(rect.left, window.innerWidth - 340);
  const playlistName = playing ? playlists.find(p => p.id === playing.block.playlistId)?.name ?? '' : '';
  const report = device.nowPlaying;
  const reportedPlaylistId = report?.startsWith('playlist:') ? report.slice('playlist:'.length) : null;
  const reportedName = reportedPlaylistId
    ? playlists.find(p => p.id === reportedPlaylistId)?.name ?? '플레이리스트 (이름 확인 불가)'
    : !report ? '재생 정보 없음'
      : report.startsWith('notice:') ? '공지 콘텐츠'
        : report.startsWith('single-media:') || report.startsWith('test-uploaded:') ? '미디어 콘텐츠'
          : report.startsWith('proc:') || report.startsWith('test-procedural:') ? '테스트 화면'
            : '재생 대상 확인 불가';
  const mismatch = diagnostic?.status === 'online' && playing && reportedPlaylistId
    && reportedPlaylistId !== playing.block.playlistId;
  const rows: Array<[string, string]> = [
    ['해상도', device.resolutionW && device.resolutionH ? `${device.resolutionW} × ${device.resolutionH}` : '확인되지 않음'],
    ['화면 방향', device.resolutionW && device.resolutionH
      ? device.resolutionW === device.resolutionH ? '정사각형' : device.resolutionW > device.resolutionH ? '가로' : '세로'
      : device.orientation === 'portrait' ? '세로' : '가로'],
    ['현재 편성', playing
      ? `${playing.block.label || playlistName || playing.assignment.name} · ${minuteToHHMM(playing.block.startMinute)}–${minuteToHHMM(playing.block.endMinute)}`
      : '편성 없음'],
    ['스케줄', playing ? `${playing.assignment.name} · ${playing.assignment.layer === 'base' ? '기본' : '이벤트'}` : '-'],
    [diagnostic?.status === 'offline' ? '마지막 재생' : '기기 재생', reportedName],
    ['마지막 연결', diagnostic?.lastSeenAt ? new Date(diagnostic.lastSeenAt).toLocaleString('ko-KR') : '-'],
  ];
  return <div role="tooltip" aria-label={`${device.name} 현재 상태`}
    className="pointer-events-none fixed z-50 w-80 rounded border border-line bg-panel p-3 text-[12px] shadow-lg"
    style={{ top: Math.max(8, top), left: Math.max(8, left) }}>
    <div className="flex items-center justify-between gap-2">
      <span className="truncate text-[13px] font-semibold">{device.name}</span>
      <DeviceDiagnosticsBadge {...(diagnostic ? { diagnostic } : {})} />
    </div>
    {/* 실제 화면 — 지금 이 기기에 나가는 그대로다. */}
    <div className="mt-2 flex justify-center"><DevicePreview key={device.id} device={device} width={288} /></div>
    <dl className="mt-2 grid grid-cols-[5rem_1fr] gap-x-2 gap-y-1">
      {rows.map(([label, value]) => <div key={label} className="contents">
        <dt className="text-text-dim">{label}</dt><dd className="truncate" title={value}>{value}</dd></div>)}
    </dl>
    {mismatch && <p className="mt-2 text-amber-700" role="status">편성과 다르게 재생 중</p>}
  </div>;
}
/**
 * 색은 누가 올렸는지만 말한다 — 소유자 편성은 코랄, 관리자는 샌드, 편집자는 세이지.
 * 권한 서열이 사다리라서 색도 한 단씩 내려간다: 내 서열보다 위 색은 보기만 가능하다.
 * 기본·이벤트는 레인이 나뉘어 있어 자리로 읽히고, 밀린 구간은 같은 색을 옅게 깔아 표시한다.
 */
type FillKey = 'event' | 'base' | 'editor';
const fillKey = (assignment: Pick<ScheduleAssignment, 'central' | 'createdByRole'>): FillKey => {
  const role = assignmentCreatorRole(assignment);
  return role === 'Owner' ? 'event' : role === 'Admin' ? 'base' : 'editor';
};
// Tailwind가 클래스를 찾을 수 있도록 세 벌을 문자열 그대로 둔다.
const CHIP_TONE: Record<FillKey, string> = {
  event: 'border-transparent bg-[var(--schedule-event-fill)] text-[color:var(--schedule-event-text)] font-medium',
  base: 'border-transparent bg-[var(--schedule-base-fill)] text-[color:var(--schedule-base-text)] font-medium',
  editor: 'border-transparent bg-[var(--schedule-editor-fill)] text-[color:var(--schedule-editor-text)] font-medium',
};
const chipTone = (assignment: Pick<ScheduleAssignment, 'central' | 'createdByRole'>) => CHIP_TONE[fillKey(assignment)];
/** 블록 안쪽을 칠할 색. 가려진 구간은 이 색을 옅게 깐다. */
const scheduleFill = (assignment: Pick<ScheduleAssignment, 'central' | 'createdByRole'>) =>
  `var(--schedule-${fillKey(assignment)}-fill)`;

// ── 승인 표시 ──────────────────────────────────────────────────────────────
// 승인이 필요한 등록·수정·삭제 요청은 승인 전까지 재생되지 않는다. 캘린더에서 한눈에 구분되어야 한다.
// 반려된 편성은 편성표에 그리지 않으므로(boardAssignments) 여기서는 대기 상태만 구분한다.
const approvalTone = (assignment: ScheduleAssignment) => assignment.approval?.status === 'pending'
  ? 'border-dashed border-blue bg-blue-soft text-blue' : '';
// 승인이 끝난 스케줄은 보통 상태라 따로 표시하지 않는다 — 대기 중인 요청만 눈에 띄면 된다.
const approvalMark = (assignment: ScheduleAssignment) => assignment.approval?.status === 'pending'
  ? (assignment.approval.action === 'delete' ? '삭제 요청 · ' : assignment.approval.action === 'update' ? '수정 요청 · ' : '') : '';
/** 칩에 마우스를 올렸을 때 보이는 설명. 왜 대기 중인지까지 적는다. */
function assignmentHint(assignment: ScheduleAssignment): string {
  const owner = `${roleLabel(assignmentCreatorRole(assignment))} 스케줄`;
  if (!assignment.approval) return assignment.central ? `${owner} · 하위 권한자는 보기만 가능합니다.` : '';
  const { status, reason, note, action } = assignment.approval;
  return `${approvalActionLabel(action)} ${approvalLabel(status)}${reason ? ` · ${reason}` : ''}${note ? ` · ${note}` : ''}${status === 'pending'
    ? (action === 'update' || action === 'delete') ? ' · 승인 전까지 기존 스케줄이 그대로 재생됩니다.' : ' · 승인 전에는 재생되지 않습니다.'
    : status === 'rejected' || status === 'cancelled' ? ' · 재생되지 않습니다.' : ''}`;
}

/** 오늘은 채워진 배지로, 주말은 빨간 글자로 구분한다. */
/** 주가 달을 걸치면 두 달을 모두 적는다. */
function monthLabel(days: string[]): string {
  const first = Number(days[0]!.slice(5, 7));
  const last = Number(days[days.length - 1]!.slice(5, 7));
  return first === last ? `${first}월` : `${first}–${last}월`;
}

/** 모서리 칸 라벨 — 지금 보고 있는 주/달을 그대로 적는다. */
const WEEK_ORDINALS = ['첫', '둘', '셋', '넷', '다섯', '여섯'];
export function dayCornerLabel(date: string): string {
  return `${Number(date.slice(5, 7))}월 ${Number(date.slice(8, 10))}일 (${DAY_LABELS[weekdayOf(date)]})`;
}
export function weekCornerLabel(monday: string): string {
  const index = Math.min(Math.floor((Number(monday.slice(8, 10)) - 1) / 7), WEEK_ORDINALS.length - 1);
  return `${Number(monday.slice(5, 7))}월 ${WEEK_ORDINALS[index]}째 주`;
}

function DateBadge({ day, today, holiday }: { day: string; today: string; holiday?: string | undefined }) {
  const weekday = weekdayOf(day);
  const isToday = day === today;
  // 공휴일은 주말과 같은 색으로 읽히게 하고, 이름은 좁은 칸에서 잘리면 title 로 보인다.
  const weekend = weekday === 0 || weekday === 6 || !!holiday;
  return <>
    {/* 채워진 면 위 흰 글자는 accent-solid(4.6:1). 11px는 AA에서 너무 작아 12px로 올렸다(검수 S7). */}
    <span className={isToday
      ? 'inline-flex h-5 min-w-5 items-center justify-center rounded bg-accent-solid px-1 text-[12px] font-semibold text-white'
      : weekend ? 'text-red' : 'text-text'}>{Number(day.slice(8, 10))}</span>
    <span className={isToday ? 'font-semibold text-text' : weekend ? 'text-red' : ''}>{DAY_LABELS[weekday]}</span>
    {holiday && <span data-holiday className="min-w-0 truncate text-[11px] font-medium text-red" title={holiday}>{holiday}</span>}
  </>;
}

// ── 일 뷰: 기기별 24시간 트랙(이벤트/기본 레인) + 현재시간선 ──
/** 한 줄에 쌓이는 블록 높이(px). 칩 32 + 여백 2. */
const DAY_ROW_H = 34;

/**
 * 기기 전원 계획을 그날의 분 단위 구간으로 펼친다 — 켜져 있는 구간(on)과 꺼져 있는 구간(off).
 * 전날 밤을 넘겨 운영하는 계획(off ≤ on)은 그날 0시부터 off 시각까지 켜진 것으로 본다.
 * 계획이 없거나 꺼져 있으면 null — 그날은 기기 자체 설정을 따르므로 표기하지 않는다.
 * 공휴일 규칙이 있으면 공휴일에는 요일 시간 대신 그 규칙을 쓰고, 그날 이름을 `holiday` 로 돌려준다.
 */
export function powerRangesOn(schedule: DevicePowerSchedule | null | undefined, date: string, holidays: HolidayNames = NO_HOLIDAYS):
  { on: { start: number; end: number }[]; off: { start: number; end: number }[]; holiday?: string } | null {
  if (!schedule?.enabled || !schedule.entries.length) return null;
  const byWeekday = new Map(schedule.entries.map(e => [e.weekday, e]));
  const rule = schedule.holiday;
  const hoursOn = (iso: string) => rule && holidays.has(iso)
    ? (rule.mode === 'on' ? rule : undefined)
    : byWeekday.get(weekdayOf(iso));
  const on: { start: number; end: number }[] = [];
  const yesterday = hoursOn(addDays(date, -1));
  if (yesterday) {
    const s = parseClock(yesterday.on), e = parseClock(yesterday.off);
    if (s !== null && e !== null && e <= s && e > 0) on.push({ start: 0, end: e });
  }
  const today = hoursOn(date);
  if (today) {
    const s = parseClock(today.on), e = parseClock(today.off);
    if (s !== null && e !== null) on.push({ start: s, end: e > s ? e : 1440 });
  }
  on.sort((a, b) => a.start - b.start);
  const merged: { start: number; end: number }[] = [];
  for (const range of on) {
    const last = merged.at(-1);
    if (last && range.start <= last.end) last.end = Math.max(last.end, range.end); else merged.push({ ...range });
  }
  const off: { start: number; end: number }[] = [];
  let cursor = 0;
  for (const range of merged) { if (range.start > cursor) off.push({ start: cursor, end: range.start }); cursor = range.end; }
  if (cursor < 1440) off.push({ start: cursor, end: 1440 });
  const holiday = rule ? holidays.get(date) : undefined;
  return holiday ? { on: merged, off, holiday } : { on: merged, off };
}

const POWER_ROW_H = 18;

/**
 * 편성이 기기 전원 계획상 꺼진 시간에 걸리는지. 걸리면 안내 문장을, 아니면 null.
 * 이 시간엔 기기가 꺼져 있어 재생되지 않는다 — 막지는 않고 토스트로만 알린다(2026-09-19).
 * 반복 편성은 앞으로 5주까지만 본다.
 */
export function powerConflictMessage(items: readonly ScheduleAssignment[], devices: WorkflowScope['devices'], from = todayISO(),
  holidays: HolidayNames = NO_HOLIDAYS): string | null {
  const hits = new Map<string, Set<string>>();
  for (const item of items) {
    const device = devices.find(d => d.id === item.deviceId);
    if (!device?.powerSchedule?.enabled) continue;
    for (let i = 0; i < 35; i += 1) {
      const date = addDays(from < item.startDate ? item.startDate : from, i);
      if (!assignmentOccursOn(item, date)) continue;
      const off = powerRangesOn(device.powerSchedule, date, holidays)?.off ?? [];
      const block = item.blocks.find(b => off.some(r => b.startMinute < r.end && r.start < b.endMinute));
      if (!block) continue;
      if (!hits.has(device.name)) hits.set(device.name, new Set());
      hits.get(device.name)!.add(`${minuteToHHMM(block.startMinute)}–${minuteToHHMM(block.endMinute)}`);
      break;
    }
  }
  if (!hits.size) return null;
  const names = [...hits].slice(0, 3).map(([name, times]) => `${name}(${[...times][0]})`).join(', ');
  return `${names}${hits.size > 3 ? ` 외 ${hits.size - 3}대` : ''}는 이 시간에 전원이 꺼져 있어 재생되지 않습니다. 기기 전원 계획을 확인해 주세요.`;
}
/** 기기 행 아래 전원 줄 — 꺼진 시간은 트랙 전체에 빗금, 전원 줄에는 켜짐/꺼짐 시각. 클릭은 트랙으로 통과한다. */
function PowerRow({ ranges }: { ranges: NonNullable<ReturnType<typeof powerRangesOn>> }) {
  const pct = (minute: number) => `${minute / 1440 * 100}%`;
  return <>
    <div aria-hidden className="pointer-events-none absolute inset-0 z-[5]">
      {ranges.off.map(range => <div key={range.start} data-power-off
        className="absolute inset-y-0 bg-[repeating-linear-gradient(135deg,transparent_0_6px,rgba(0,0,0,0.07)_6px_8px)]"
        style={{ left: pct(range.start), width: pct(range.end - range.start) }} />)}
    </div>
    <div aria-label="기기 전원 시간" className="pointer-events-none relative" style={{ height: POWER_ROW_H }}>
      {ranges.on.map(range => <span key={range.start} className="contents">
        <span aria-hidden className="absolute inset-y-1.5 rounded-full bg-emerald-500/30" style={{ left: pct(range.start), width: pct(range.end - range.start) }} />
        {/* 짧은 구간이나 표 가장자리에서는 글자가 잘리지 않는 쪽으로 붙인다. */}
        {range.start > 0 && <span data-power-mark="on" className={`absolute top-0 whitespace-nowrap px-1 text-[10px] font-semibold leading-[18px] text-emerald-700 ${range.start > 1290 ? '-translate-x-full' : ''}`}
          style={{ left: pct(range.start) }}>⏻ 켜짐 {minuteToHHMM(range.start)}</span>}
        {range.end < 1440 && <span data-power-mark="off" className={`absolute top-0 whitespace-nowrap px-1 text-[10px] font-semibold leading-[18px] text-text-dim ${range.end - range.start >= 300 || range.end > 1290 ? '-translate-x-full' : ''}`}
          style={{ left: pct(range.end) }}>꺼짐 {minuteToHHMM(range.end)} ⏻</span>}
      </span>)}
      {!ranges.on.length && <span className="absolute left-2 top-0 text-[10px] leading-[18px] text-text-dim">{ranges.holiday ?? '휴무'} · 종일 꺼짐</span>}
    </div>
  </>;
}
type StackedBlock = { assignment: ScheduleAssignment; block: TimelineBlock };

/**
 * 겹치는 블록을 위아래 줄로 나눈다. 시간이 겹치지 않으면 같은 줄을 함께 쓴다.
 * 캘린더에서 서로 가려지면 무엇이 겹쳤는지 읽을 수 없다.
 * 줄 순서는 재생 우선순위를 따른다 — 먼저 재생될 편성이 위에, 밀린 편성이 아래에 놓인다.
 * 그래서 `assignments`는 우선순위가 높은 것부터 들어와야 한다.
 */
export function stackBlocks(assignments: readonly ScheduleAssignment[]): StackedBlock[][] {
  const items: StackedBlock[] = assignments
    .flatMap(assignment => assignment.blocks.map(block => ({ assignment, block })))
    .filter(item => item.block.startMinute < item.block.endMinute);
  const rows: StackedBlock[][] = [];
  for (const item of items) {
    const row = rows.find(candidate => candidate.every(other =>
      other.block.endMinute <= item.block.startMinute || item.block.endMinute <= other.block.startMinute));
    if (row) row.push(item); else rows.push([item]);
  }
  return rows;
}

function useCalendarRange() {
  const [range, setRange] = useState<{ key: string; start: number; end: number } | null>(null);
  const drag = useRef<{ key: string; first: number; last: number; left: number; width: number; count: number; edges: boolean; finish: (start: number, end: number, moved: boolean) => void } | null>(null);
  const suppressClick = useRef(false);
  const indexAt = (x: number, d: NonNullable<typeof drag.current>) => Math.max(0, Math.min(d.edges ? d.count : d.count - 1,
    (d.edges ? Math.round : Math.floor)((x - d.left) / d.width * d.count)));
  return { range,
    down(event: ReactPointerEvent<HTMLDivElement>, key: string, count: number, finish: (start: number, end: number, moved: boolean) => void, edges = false) {
      if (event.button !== 0 || (event.target as HTMLElement).closest('[data-block],[data-chip],button')) return;
      const rect = event.currentTarget.getBoundingClientRect();
      if (!rect.width) return;
      // Drawing a time range is not a native text-selection gesture.
      event.preventDefault();
      const d = { key, first: 0, last: 0, left: rect.left, width: rect.width, count, finish, edges };
      d.first = d.last = Math.min(count - 1, indexAt(event.clientX, d));
      drag.current = d;
      event.currentTarget.setPointerCapture(event.pointerId);
      setRange({ key, start: d.first, end: d.first });
    },
    move(event: ReactPointerEvent<HTMLDivElement>) {
      const d = drag.current; if (!d) return;
      d.last = indexAt(event.clientX, d);
      setRange({ key: d.key, start: Math.min(d.first, d.last), end: Math.max(d.first, d.last) });
    },
    up(event: ReactPointerEvent<HTMLDivElement>) {
      const d = drag.current; if (!d) return;
      d.last = indexAt(event.clientX, d);
      drag.current = null; setRange(null); suppressClick.current = true;
      d.finish(Math.min(d.first, d.last), Math.max(d.first, d.last), d.first !== d.last);
    },
    cancel() { drag.current = null; setRange(null); },
    clicked() { const suppressed = suppressClick.current; suppressClick.current = false; return suppressed; },
  };
}

function DayBoard({ date, groups, devices, assignments, diagnostics, nowMinute, canWrite, canEdit, onMove, onEdit, onCreate, onHoverDevice, holidays }: {
  date: string; groups: { id: string | null; name: string }[]; devices: WorkflowScope['devices'];
  assignments: ScheduleAssignment[]; diagnostics: Record<string, DeviceDiagnostic>; nowMinute: number; canWrite: boolean;
  canEdit: (assignment: ScheduleAssignment) => boolean; onMove: (assignment: ScheduleAssignment, blockId: string, startMinute: number, endMinute: number) => void;
  onEdit: (a: ScheduleAssignment, date: string) => void; onCreate: (preset: CreatePreset) => void;
  onHoverDevice: (hover: DeviceHover | null) => void;
  holidays: HolidayNames;
}) {
  const isToday = date === todayISO();
  const holiday = holidays.get(date);
  const selection = useCalendarRange();
  // 블록 자체를 끌면 그 시간대만 통째로 이동한다. 스냅은 5분.
  const [blockShift, setBlockShift] = useState<{ id: string; blockId: string; start: number; end: number } | null>(null);
  const dragBlock = useRef<{ assignment: ScheduleAssignment; block: TimelineBlock; startX: number; width: number;
    min: number; max: number; start: number; end: number; mode: 'move' | 'start' | 'end'; moved: boolean } | null>(null);
  const justMoved = useRef(false);
  const previewAssignments = blockShift ? assignments.map(a => a.id === blockShift.id ? { ...a, blocks: a.blocks.map(b =>
    b.id === blockShift.blockId ? { ...b, startMinute: blockShift.start, endMinute: blockShift.end } : b) } : a) : assignments;
  const nowLeft = `${nowMinute / 1440 * 100}%`;
  return <div className="relative min-w-[1100px]">
    <div className="sticky top-0 z-20 flex h-9 border-b border-line bg-panel-2 text-xs text-text-dim">
      <div className="sticky left-0 z-10 flex w-72 shrink-0 items-center gap-2 border-r border-line bg-panel-2 px-4">
        <span className={holiday ? 'text-red' : ''}>{dayCornerLabel(date)}</span>
        {holiday && <span data-holiday className="truncate rounded bg-red/10 px-1.5 py-0.5 text-[11px] font-medium text-red" title={holiday}>{holiday}</span>}
      </div>
      {/* 레이어 이름 칸 — 기기 칸과 타임라인 사이에서 어느 줄이 무엇인지 먼저 읽힌다. */}
      <div className="sticky left-72 z-10 w-16 shrink-0 border-r border-line bg-panel-2" />
      <div className="relative mr-5 flex-1">
        {Array.from({ length: 25 }, (_, hour) =>
          <span key={hour} className="absolute top-2.5 -translate-x-1/2" style={{ left: `${hour / 24 * 100}%` }}>{pad(hour)}</span>)}
        {/* 현재 시각은 눈금자에 한 번만 적는다. 선은 아래 보드 전체를 가로지른다. */}
        {isToday && <span className="absolute top-1.5 -translate-x-1/2 rounded bg-accent-solid px-1 text-[12px] font-semibold leading-4 text-white"
          style={{ left: nowLeft }}>{minuteToHHMM(nowMinute)}</span>}
      </div>
    </div>
    {groups.map(group => {
      const members = devices.filter(d => d.groupId === group.id);
      if (!members.length) return null;
      return <section key={group.id ?? 'unassigned'} aria-label={group.name}>
        <h2 className="flex border-b border-line bg-panel-2 text-sm font-semibold"><span className="sticky left-0 z-10 w-72 shrink-0 truncate border-r border-line bg-panel-2 px-4 py-1.5" title={group.name}>{group.name} · {members.length}대</span><span className="sticky left-72 z-10 w-16 shrink-0 border-r border-line bg-panel-2 py-1.5" /><span className="flex-1 py-1.5" /></h2>
        {members.map(device => {
          // 겹치는 편성은 같은 자리에 포개지 않고 위아래로 쌓는다. 레인 높이는 쌓인 줄 수를 따라간다.
          const lanes = (['event', 'base'] as const).map(layer => {
            const active = byAssignmentPrecedence(assignments.filter(a =>
              a.layer === layer && a.deviceId === device.id && assignmentOccursOn(a, date)));
            const rows = stackBlocks(active);
            return { layer, active, rows, height: Math.max(1, rows.length) * DAY_ROW_H + 12 };
          });
          const power = powerRangesOn(device.powerSchedule, date, holidays);
          return <div key={device.id} className="flex border-b border-line" aria-label={device.name}>
            <div className="sticky left-0 z-10 flex w-72 shrink-0 items-center border-r border-line bg-panel pl-4">
              <DeviceName device={device} diagnostic={diagnostics[device.id]} onHover={onHoverDevice} />
            </div>
            <div className="sticky left-72 z-10 flex w-16 shrink-0 flex-col border-r border-line bg-panel">
              {lanes.map(({ layer, height }) => <span key={layer} style={{ height }}
                className={`flex items-center justify-center text-[11px] font-medium ${layer === 'event'
                  ? 'border-b border-line/60 bg-accent-faint text-accent-dim' : 'text-text-dim'}`}>
                {layer === 'base' ? '기본' : '이벤트'}</span>)}
              {power && <span style={{ height: POWER_ROW_H }} className="flex items-center justify-center border-t border-line/60 text-[10px] font-medium text-text-dim">전원</span>}
            </div>
            <div className="relative mr-5 flex-1">
              {lanes.map(({ layer, active, rows, height }) =>
                <div key={layer} aria-label={`${device.name} ${layer === 'base' ? '기본' : '이벤트'} 트랙`}
                  className={`relative select-none border-b border-line/50 ${layer === 'event' ? 'bg-accent-faint' : ''} ${canWrite ? 'cursor-copy hover:bg-accent-soft/30' : ''}`}
                  title={canWrite ? '클릭해서 이 기기에 스케줄 추가' : undefined}
                  style={{ ...laneStyle, height, touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none' }}
                  onPointerDown={event => { if (canWrite) selection.down(event, `${device.id}:${layer}`, 288, (start, end, moved) =>
                    onCreate({ deviceIds: [device.id], layer, date, startMinute: start * 5, endMinute: Math.min(1440, moved ? Math.max(start + 1, end) * 5 : start * 5 + 60) }), true); }}
                  onPointerMove={selection.move} onPointerUp={selection.up} onPointerCancel={selection.cancel}
                  onClick={event => {
                    if (selection.clicked()) return;
                    if (!canWrite || (event.target as HTMLElement).closest('[data-block]')) return;
                    const rect = event.currentTarget.getBoundingClientRect();
                    const startMinute = rect.width ? Math.max(0, Math.min(1435, Math.floor((event.clientX - rect.left) / rect.width * 288) * 5)) : 0;
                    onCreate({ deviceIds: [device.id], layer, date, startMinute, endMinute: Math.min(1440, startMinute + 60) });
                  }}>
                  {selection.range?.key === `${device.id}:${layer}` && <div aria-label="선택 중인 시간" className="pointer-events-none absolute inset-y-1 z-10 rounded border border-accent bg-accent-soft opacity-80"
                    style={{ left: `${selection.range.start / 288 * 100}%`, width: `${Math.max(1, selection.range.end - selection.range.start) / 288 * 100}%` }}>
                    {minuteToHHMM(selection.range.start * 5)}–{minuteToHHMM(Math.max(selection.range.start + 1, selection.range.end) * 5)}
                  </div>}
                  {rows.flatMap((row, index) => row.map(({ assignment, block }) => {
                    const shift = blockShift && blockShift.id === assignment.id && blockShift.blockId === block.id ? blockShift : null;
                    const start = shift?.start ?? block.startMinute, end = shift?.end ?? block.endMinute;
                    const slices = blockPlaybackSlices(assignment, { ...block, startMinute: start, endMinute: end }, previewAssignments, date);
                    const bumped = slices.some(s => s.muted) && isAssignmentPlayable(assignment);
                    const fill = scheduleFill(assignment);
                    const background = `linear-gradient(to right, ${slices.flatMap(s => {
                      const color = s.muted ? `color-mix(in srgb, ${fill} 45%, transparent)` : fill;
                      return [`${color} ${(s.start - start) / (end - start) * 100}%`, `${color} ${(s.end - start) / (end - start) * 100}%`];
                    }).join(', ')})`;
                    const movable = canWrite && canEdit(assignment);
                    return <button key={`${assignment.id}-${block.id}`} type="button" data-block
                      aria-label={`${assignment.name} ${block.label || ''} ${minuteToHHMM(start)}–${minuteToHHMM(end)}${assignment.approval ? ` ${approvalLabel(assignment.approval.status)}` : ''}${bumped ? ' 밀림' : ''}`}
                      className={`group/block absolute flex h-8 items-center rounded border px-2 text-xs ${approvalTone(assignment) || chipTone(assignment)} ${movable ? (shift ? 'z-30 cursor-grabbing shadow-md' : 'cursor-grab') : ''}`}
                      style={{ top: 6 + index * DAY_ROW_H, left: `${start / 1440 * 100}%`,
                        ...(isAssignmentPlayable(assignment) ? { backgroundImage: background, backgroundColor: 'transparent', backgroundRepeat: 'no-repeat', backgroundClip: 'padding-box' } : {}),
                        width: `${Math.max(0.6, (end - start) / 1440 * 100)}%` }}
                      onPointerDown={event => {
                        if (!movable || event.button !== 0) return;
                        if ((event.target as HTMLElement).closest('[data-reorder]')) return;
                        event.preventDefault(); event.stopPropagation();
                        const width = (event.currentTarget.parentElement as HTMLElement).getBoundingClientRect().width;
                        if (!width) return;
                        event.currentTarget.setPointerCapture(event.pointerId);
                        // 같은 편성의 이웃 블록을 넘어가지 않는 범위에서만 움직인다.
                        const siblings = assignment.blocks.filter(b => b.id !== block.id);
                        const lower = Math.max(0, ...siblings.filter(b => b.endMinute <= block.startMinute).map(b => b.endMinute));
                        const upper = Math.min(1440, ...siblings.filter(b => b.startMinute >= block.endMinute).map(b => b.startMinute));
                        const mode = (event.target as HTMLElement).closest<HTMLElement>('[data-resize]')?.dataset.resize as 'start' | 'end' | undefined;
                        dragBlock.current = { assignment, block, startX: event.clientX, width,
                          min: lower, max: upper, start: block.startMinute, end: block.endMinute, mode: mode ?? 'move', moved: false };
                        setBlockShift({ id: assignment.id, blockId: block.id, start: block.startMinute, end: block.endMinute });
                      }}
                      onPointerMove={event => {
                        const d = dragBlock.current; if (!d) return;
                        const delta = Math.round((event.clientX - d.startX) / d.width * 1440 / SNAP) * SNAP;
                        // 클릭 중 손떨림(몇 px)은 끌기로 치지 않는다 — 5분 스냅 한 칸이 3~4px라 바로 옮겨졌다(SCDS-72).
                        if (Math.abs(event.clientX - d.startX) < 8 && !d.moved) return;
                        d.moved = true;
                        if (d.mode === 'start') d.start = Math.max(d.min, Math.min(d.block.endMinute - SNAP, d.block.startMinute + delta));
                        else if (d.mode === 'end') d.end = Math.min(d.max, Math.max(d.block.startMinute + SNAP, d.block.endMinute + delta));
                        else { const duration = d.block.endMinute - d.block.startMinute;
                          d.start = Math.max(d.min, Math.min(d.max - duration, d.block.startMinute + delta)); d.end = d.start + duration; }
                        setBlockShift({ id: d.assignment.id, blockId: d.block.id, start: d.start, end: d.end });
                      }}
                      onPointerUp={() => {
                        const d = dragBlock.current; dragBlock.current = null; setBlockShift(null);
                        // 손이 조금 떨렸거나(3px) 종일 블록처럼 옮길 자리가 없어 시간이 그대로면 클릭으로 친다.
                        // 그렇지 않으면 편성 정보가 열리지 않아 수정·삭제를 못 한다(SCDS-72).
                        if (d?.moved && (d.start !== d.block.startMinute || d.end !== d.block.endMinute)) {
                          justMoved.current = true; onMove(d.assignment, d.block.id, d.start, d.end);
                        }
                      }}
                      onPointerCancel={() => { dragBlock.current = null; setBlockShift(null); }}
                      // 블록을 스칠 때마다 툴팁이 뜨면 표를 읽을 수 없다. 자세한 내용은 눌러서 편성 정보로 본다.
                      onClick={event => { event.stopPropagation();
                        // 끌어 옮긴 직후의 클릭은 편집으로 치지 않는다.
                        if (justMoved.current) { justMoved.current = false; return; }
                        if ((event.target as HTMLElement).closest('[data-reorder]')) return;
                        onEdit(assignment, date); }}>
                      <span className={`pointer-events-none truncate ${bumped ? 'pr-6' : ''}`}>{approvalMark(assignment)}{block.label || assignment.name}</span>
                      {bumped && (() => {
                        const explanation = slices.filter(s => s.muted).map(slice => `${minuteToHHMM(slice.start)}–${minuteToHHMM(slice.end)} · ${slice.winner ? `'${slice.winner}' 스케줄이 우선 재생되어 이 구간은 재생되지 않습니다.` : '이 구간은 재생되지 않습니다.'}`).join('\n');
                        return <span role="img" aria-label={explanation}
                          data-overlap-warning
                          className="group/warn absolute right-2 top-1/2 z-10 flex h-5 w-5 -translate-y-1/2 cursor-help items-center justify-center rounded"
                          onPointerDown={event => event.stopPropagation()} onClick={event => event.stopPropagation()}>
                          ⚠
                          <span role="tooltip" className="pointer-events-none absolute bottom-full left-1/2 mb-1 hidden w-max max-w-64 -translate-x-1/2 whitespace-normal rounded border border-line bg-panel px-2 py-1 text-left text-xs font-normal text-text shadow-md group-hover/warn:block">
                            {explanation}
                          </span>
                        </span>;
                      })()}
                      {movable && <><span data-resize="start" aria-label="시작 시간 조절" className="absolute inset-y-0 left-0 w-2 cursor-ew-resize rounded-l hover:bg-black/10" />
                        <span data-resize="end" aria-label="종료 시간 조절" className="absolute inset-y-0 right-0 w-2 cursor-ew-resize rounded-r hover:bg-black/10" /></>}
                      {shift && <span role="status" aria-label="조절 중인 시간" className="pointer-events-none absolute bottom-full left-1/2 mb-1 -translate-x-1/2 whitespace-nowrap rounded border border-line bg-panel px-2 py-1 font-mono text-xs text-text shadow-md">
                        {minuteToHHMM(start)}–{minuteToHHMM(end)}</span>}
                    </button>; }))}
                  {!active.length && <span className="pointer-events-none absolute left-2 top-3 text-xs text-text-dim">편성 없음</span>}
                </div>)}
              {power && <PowerRow ranges={power} />}
            </div>
          </div>;
        })}
      </section>;
    })}
    {/* 기기 행 위에 겹쳐 그리는 단일 현재시각 선 — 헤더(h-9) 아래부터 보드 끝까지. */}
    {isToday && <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 top-9 flex">
      <div className="w-72 shrink-0 border-r border-transparent" />
      <div className="w-16 shrink-0 border-r border-transparent" />
      <div className="relative mr-5 flex-1">
        <span data-testid="now-line" className="absolute inset-y-0 w-px bg-accent" style={{ left: nowLeft }} />
      </div>
    </div>}
  </div>;
}

// ── 주/월 뷰: 날짜 컬럼 × 기기 행. 월 뷰는 가로 스크롤 ──
export function shiftAssignmentDates(assignment: ScheduleAssignment, delta: number): AssignmentRule {
  return { calendar: assignment.calendar ?? 'solar',
    startDate: addDays(assignment.startDate, delta), endDate: addDays(assignment.endDate, delta),
    repeat: assignment.repeat.kind === 'weekly' ? { kind: 'weekly',
      weekdays: assignment.repeat.weekdays.map(day => ((day + delta) % 7 + 7) % 7).sort((a, b) => a - b) } : assignment.repeat,
    repeatEnd: assignment.repeatEnd.kind === 'until'
      ? { kind: 'until', date: addDays(assignment.repeatEnd.date, delta) } : assignment.repeatEnd,
    skipDates: (assignment.skipDates ?? []).map(day => addDays(day, delta)) };
}

/** 주/월 그리드에서 기기 이름칸이 차지하는 폭(px). `w-72` = 18rem. */
export const BOARD_LABEL_WIDTH = 288;

/**
 * 가로로 잘려 보이지 않는 날짜 칸을 한 줄로 요약한다 (검수 S6).
 * 오버레이 스크롤바만 있는 환경에서는 "더 있다"는 사실 자체가 보이지 않는다 —
 * 주말이 사라진 주간 캘린더는 쓸 수 없으므로, 무엇이 숨었는지를 글자로 말해 준다.
 * 주 뷰는 요일(`토·일`), 월 뷰는 날짜(`24일부터 8칸`)로 읽는 편이 빠르다.
 */
export function hiddenDaysCue(days: readonly string[],
  metrics: { scrollLeft: number; clientWidth: number; scrollWidth: number },
  options: { month?: boolean; labelWidth?: number } = {}): string {
  const labelWidth = options.labelWidth ?? BOARD_LABEL_WIDTH;
  if (!days.length) return '';
  // 1px는 소수점 폭 반올림에서 늘 남는다 — 그 정도로는 "잘렸다"고 하지 않는다.
  if (metrics.scrollWidth - metrics.clientWidth - metrics.scrollLeft <= 1) return '';
  const columnWidth = (metrics.scrollWidth - labelWidth) / days.length;
  if (!(columnWidth > 0)) return '';
  const visibleRight = metrics.scrollLeft + metrics.clientWidth;
  // 오른쪽 끝이 화면 밖으로 넘어가는 첫 칸부터가 '못 보는 날짜'다(반쪽만 보이는 칸 포함).
  const first = days.findIndex((_, index) => labelWidth + (index + 1) * columnWidth > visibleRight + 1);
  if (first < 0) return '';
  const hidden = days.slice(first);
  if (options.month) return `${Number(hidden[0]!.slice(8, 10))}일부터 ${hidden.length}칸`;
  return hidden.map(day => DAY_LABELS[weekdayOf(day)]!).join('·');
}

// ── 모바일 편성 목록 ─────────────────────────────────────────────────────────
// 768px 미만에서는 기기 × 날짜 표가 들어가지 않는다. 날짜를 세로로 쌓고 그 아래에 기기별
// 편성·전원·공휴일을 적는다. 끌어 옮기기 대신 눌러서 편성 창을 연다(2026-09-23 합의).
// 월 보기는 위에 작은 달력을 두고 고른 하루만 펼친다 — 30일 × 기기를 한 번에 쌓으면 못 읽는다.
function blockTimes(assignment: ScheduleAssignment): string {
  const blocks = [...assignment.blocks].sort((a, b) => a.startMinute - b.startMinute);
  if (blocks.length === 1 && blocks[0]!.startMinute === 0 && blocks[0]!.endMinute >= 1440) return '종일';
  return blocks.map(b => `${minuteToHHMM(b.startMinute)}–${b.endMinute >= 1440 ? '24:00' : minuteToHHMM(b.endMinute)}`).join(', ');
}

function MobileAgenda({ view, days, today, groups, devices, assignments, holidays, onEdit, onCreate }: {
  view: 'day' | 'week' | 'month'; days: string[]; today: string;
  groups: { id: string | null; name: string }[]; devices: WorkflowScope['devices'];
  assignments: ScheduleAssignment[]; holidays: HolidayNames;
  onEdit: (a: ScheduleAssignment, date: string) => void; onCreate?: ((preset: CreatePreset) => void) | undefined;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const selected = view === 'month' ? (picked && days.includes(picked) ? picked : days.includes(today) ? today : days[0]!) : null;
  const ordered = groups.flatMap(group => devices.filter(d => d.groupId === group.id).map(device => ({ device, group: group.name })));
  const occurs = (day: string) => assignments.some(a => ordered.some(o => o.device.id === a.deviceId) && assignmentOccursOn(a, day));
  const shown = selected ? [selected] : days;
  return <div className="flex flex-col gap-3" aria-label="편성 목록">
    {view === 'month' && <div className="rounded border border-line bg-panel p-2" role="grid" aria-label="날짜 선택">
      <div className="grid grid-cols-7 text-center text-[11px] text-text-dim">
        {['월', '화', '수', '목', '금', '토', '일'].map(label => <span key={label} className="py-1">{label}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {Array.from({ length: (weekdayOf(days[0]!) + 6) % 7 }, (_, i) => <span key={`blank-${i}`} />)}
        {days.map(day => {
          const weekday = weekdayOf(day);
          const red = weekday === 0 || weekday === 6 || holidays.has(day);
          return <button key={day} type="button" role="gridcell" aria-selected={day === selected}
            aria-label={`${Number(day.slice(5, 7))}월 ${Number(day.slice(8, 10))}일${holidays.get(day) ? ` ${holidays.get(day)}` : ''}`}
            onClick={() => setPicked(day)}
            className={`relative mx-auto flex h-10 w-10 flex-col items-center justify-center rounded-full text-[14px] tabular-nums ${day === selected
              ? 'bg-accent-solid font-semibold text-white' : day === today ? 'font-semibold ring-1 ring-accent' : red ? 'text-red' : 'text-text'}`}>
            {Number(day.slice(8, 10))}
            {occurs(day) && <span aria-hidden className={`absolute bottom-1 h-1 w-1 rounded-full ${day === selected ? 'bg-white' : 'bg-accent'}`} />}
          </button>;
        })}
      </div>
    </div>}
    {shown.map(day => {
      const holiday = holidays.get(day);
      const weekday = weekdayOf(day);
      const red = weekday === 0 || weekday === 6 || !!holiday;
      return <section key={day} aria-label={`${day} 편성`} className="overflow-hidden rounded border border-line bg-panel">
        <header className={`flex items-center gap-2 border-b border-line px-3 py-2 ${day === today ? 'bg-accent-soft/40' : 'bg-panel-2'}`}>
          <span className={`text-[15px] font-semibold tabular-nums ${red ? 'text-red' : 'text-text'}`}>
            {Number(day.slice(5, 7))}. {Number(day.slice(8, 10))} ({DAY_LABELS[weekday]})</span>
          {holiday && <span data-holiday className="truncate rounded bg-red/10 px-1.5 py-0.5 text-[12px] font-medium text-red">{holiday}</span>}
          {day === today && <span className="rounded bg-accent-solid px-1.5 py-0.5 text-[11px] font-semibold text-white">오늘</span>}
          {onCreate && <button type="button" aria-label={`${day} 스케줄 추가`} onClick={() => onCreate({ date: day })}
            className="ml-auto flex h-9 w-9 flex-none items-center justify-center rounded border border-line text-[18px] text-text-dim">＋</button>}
        </header>
        <ul className="divide-y divide-line">
          {ordered.map(({ device, group }) => {
            const active = byAssignmentPrecedence(assignments.filter(a => a.deviceId === device.id && assignmentOccursOn(a, day)));
            const power = powerRangesOn(device.powerSchedule, day, holidays);
            const dark = !!power && !power.on.length;
            return <li key={device.id} className="flex flex-col gap-1.5 px-3 py-2.5" aria-label={`${device.name} ${day}`}>
              <div className="flex items-baseline gap-2">
                <span className="min-w-0 truncate text-[14px] font-medium text-text">{device.name}</span>
                <span className="min-w-0 flex-none truncate text-[12px] text-text-dim">{group}</span>
                {power && <span data-power-mark={dark ? 'off' : 'on'} className={`ml-auto flex-none text-[12px] tabular-nums ${dark ? 'text-text-dim' : 'text-emerald-700'}`}>
                  {dark ? `⏻ ${power.holiday ?? ''}${power.holiday ? ' · ' : ''}종일 꺼짐`
                    : `⏻ ${power.on.map(r => `${minuteToHHMM(r.start)}–${r.end >= 1440 ? '24:00' : minuteToHHMM(r.end)}`).join(', ')}`}</span>}
              </div>
              {active.length ? active.map(assignment => <button key={assignment.id} type="button" data-chip
                onClick={() => onEdit(assignment, day)}
                aria-label={`${approvalMark(assignment)}${assignment.name} ${blockTimes(assignment)}`}
                className={`flex min-h-10 w-full items-center gap-2 rounded border px-2.5 py-1.5 text-left text-[13px] ${approvalTone(assignment) || chipTone(assignment)}`}>
                {assignment.layer === 'event' && <span className="flex-none rounded bg-white/50 px-1 text-[11px] font-semibold">이벤트</span>}
                <span className="min-w-0 flex-1 truncate">{approvalMark(assignment)}{assignment.name}</span>
                <span className="flex-none tabular-nums text-[12px] opacity-80">{blockTimes(assignment)}</span>
              </button>) : <span className="text-[13px] text-text-dim">편성 없음</span>}
            </li>;
          })}
        </ul>
      </section>;
    })}
  </div>;
}


function GridBoard({ days, groups, devices, assignments, diagnostics, month, today, holidays, onEdit, onCreate, onHoverDevice, canWrite, canEdit, onMove }: {
  days: string[]; groups: { id: string | null; name: string }[]; devices: WorkflowScope['devices'];
  assignments: ScheduleAssignment[]; diagnostics: Record<string, DeviceDiagnostic>; month: boolean; today: string; holidays: HolidayNames;
  onEdit: (a: ScheduleAssignment, date: string) => void; onCreate?: ((preset: CreatePreset) => void) | undefined;
  onHoverDevice: (hover: DeviceHover | null) => void;
  canWrite: boolean; canEdit: (assignment: ScheduleAssignment) => boolean; onMove: (assignment: ScheduleAssignment, delta: number) => void;
}) {
  // 주 뷰의 최소 열 폭 104px — 1280 화면(본문 1019px)에서 288 + 7×104 = 1016으로
  // 토·일까지 다 들어간다. 예전 144px(min-w-36)은 1296을 요구해 금요일부터 잘렸다(검수 S6).
  // 남는 폭은 flex-1이 나눠 가지므로 넓은 화면에서는 예전과 같은 크기로 벌어진다.
  const column = month ? 'w-40 shrink-0' : 'flex-1 min-w-[104px]';
  const selection = useCalendarRange();
  const drag = useRef<{ assignment: ScheduleAssignment; day: string; x: number; width: number; delta: number; moved: boolean } | null>(null);
  const [preview, setPreview] = useState<{ id: string; day: string; delta: number; width: number } | null>(null);
  const suppressClick = useRef(false);
  // 월 뷰는 가로로 길다 — 들어오자마자 오늘 칸이 이름칸 바로 옆(맨 왼쪽)에 보이게 스크롤한다.
  const rootRef = useRef<HTMLDivElement | null>(null);
  const todayIndex = days.indexOf(today);
  useEffect(() => {
    const scroller = rootRef.current?.parentElement;
    if (!month || !scroller || todayIndex < 0) return;
    scroller.scrollLeft = todayIndex * 160;
  }, [month, todayIndex, days[0]]);
  return <div ref={rootRef} style={{ minWidth: (month ? 160 : 104) * days.length + BOARD_LABEL_WIDTH }}>
    <div className="sticky top-0 z-20 flex border-b border-line bg-panel-2 text-xs text-text-dim">
      <div className="sticky left-0 z-10 w-72 shrink-0 border-r border-line bg-panel-2 px-4 py-2.5">
        {month ? monthLabel(days) : weekCornerLabel(days[0]!)}</div>
      {days.map(day => <div key={day} aria-label={day === today ? `${Number(day.slice(8, 10))}일 오늘` : undefined}
        className={`${column} flex min-w-0 items-center gap-1.5 border-l border-line px-2 py-2 ${day === today ? 'bg-accent-soft/40' : ''}`}>
        <DateBadge day={day} today={today} holiday={holidays.get(day)} /></div>)}
    </div>
    {groups.map(group => {
      const members = devices.filter(d => d.groupId === group.id);
      if (!members.length) return null;
      return <section key={group.id ?? 'unassigned'} aria-label={group.name}>
        <h2 className="flex border-b border-line bg-panel-2 text-sm font-semibold"><span className="sticky left-0 z-10 w-72 shrink-0 truncate border-r border-line bg-panel-2 px-4 py-1.5" title={group.name}>{group.name} · {members.length}대</span><span className="flex-1 py-1.5" /></h2>
        {/* 기기마다 이벤트·기본 두 줄로 나눈다. 두 레이어는 서로 다른 것을 결정하므로 섞이면 안 된다. */}
        {members.map(device => <div key={device.id} className="flex border-b border-line" aria-label={`${device.name} 주간`}>
          <div className="sticky left-0 z-10 flex w-72 shrink-0 items-center border-r border-line bg-panel pl-4 text-sm">
            <DeviceName device={device} diagnostic={diagnostics[device.id]} onHover={onHoverDevice} />
          </div>
          <div className="flex-1">
            {/* 이벤트 레인은 옅은 브랜드 틴트를 깔아 빈 줄도 자리가 읽히게 한다. */}
            {(['event', 'base'] as const).map(layer => <div key={layer} className={`relative flex min-h-[46px] select-none border-b border-line/60 last:border-b-0 ${layer === 'event' ? 'bg-accent-faint' : ''}`}
              style={{ touchAction: 'none' }}
              onPointerDown={event => { if (onCreate) selection.down(event, `${device.id}:${layer}`, days.length, (start, end) =>
                onCreate({ deviceIds: [device.id], layer, date: days[start]!, endDate: days[end]!, startMinute: 0, endMinute: 1440 })); }}
              onPointerMove={selection.move} onPointerUp={selection.up} onPointerCancel={selection.cancel}
              aria-label={`${device.name} ${layer === 'base' ? '기본' : '이벤트'} 행`}>
              {selection.range?.key === `${device.id}:${layer}` && <div aria-label="선택 중인 기간" className="pointer-events-none absolute inset-y-1 z-10 rounded border border-accent bg-accent-soft opacity-80"
                style={{ left: `${selection.range.start / days.length * 100}%`, width: `${(selection.range.end - selection.range.start + 1) / days.length * 100}%` }} />}
              {days.map(day => {
                // 손으로 정한 순서도 그대로 따른다 — 일 뷰에서 올린 것이 여기서도 위에 있어야 한다.
                const active = assignments.filter(a =>
                  a.layer === layer && a.deviceId === device.id && assignmentOccursOn(a, day))
                  .sort((a, b) => assignmentPriorityDate(b).localeCompare(assignmentPriorityDate(a)) || b.id.localeCompare(a.id));
                // 전원 계획 — 종일 꺼지는 날만 빗금, 켜지는 날은 기본 줄 아래에 켜짐 시각을 적는다.
                const power = powerRangesOn(device.powerSchedule, day, holidays);
                const dark = !!power && !power.on.length;
                return <div key={day} className={`${column} min-h-10 cursor-copy border-l border-line p-1 ${day === today ? 'bg-accent-soft/20' : ''} ${dark ? 'bg-[repeating-linear-gradient(135deg,transparent_0_6px,rgba(0,0,0,0.07)_6px_8px)]' : ''}`}
                  title={dark ? `${power?.holiday ? `${power.holiday} · ` : ''}기기 전원 종일 꺼짐` : undefined}
                  onClick={event => { if (selection.clicked()) return; if (onCreate && !(event.target as HTMLElement).closest('[data-chip]')) onCreate({ deviceIds: [device.id], layer, date: day }); }}>
                  {active.map(assignment => <button key={assignment.id} type="button" data-chip
                    aria-label={assignment.approval ? `${assignment.name} ${approvalLabel(assignment.approval.status)}` : undefined}
                    className={`relative mb-0.5 block w-full rounded border px-1.5 py-0.5 text-left text-[12px] ${approvalTone(assignment) || chipTone(assignment)} ${canWrite && canEdit(assignment) ? 'cursor-grab active:cursor-grabbing' : ''}`}
                    style={preview?.id === assignment.id && preview.day === day ? { transform: `translateX(${preview.delta * preview.width}px)`, zIndex: 30 } : undefined}
                    onPointerDown={event => {
                      event.stopPropagation(); suppressClick.current = false;
                      if (event.button !== 0 || !canWrite || !canEdit(assignment)) return;
                      event.preventDefault();
                      const width = event.currentTarget.parentElement!.getBoundingClientRect().width;
                      if (!width) return;
                      event.currentTarget.setPointerCapture?.(event.pointerId);
                      drag.current = { assignment, day, x: event.clientX, width, delta: 0, moved: false };
                    }}
                    onPointerMove={event => {
                      event.stopPropagation(); const current = drag.current; if (!current) return;
                      if (Math.abs(event.clientX - current.x) > 3) current.moved = true;
                      const index = days.indexOf(current.day);
                      current.delta = Math.max(-index, Math.min(days.length - 1 - index, Math.round((event.clientX - current.x) / current.width)));
                      if (current.moved) setPreview({ id: current.assignment.id, day: current.day, delta: current.delta, width: current.width });
                    }}
                    onPointerUp={event => {
                      event.stopPropagation(); const current = drag.current; drag.current = null; setPreview(null);
                      if (!current) return;
                      // 날짜가 바뀌지 않은 끌기는 클릭으로 친다 — 살짝 흔들린 클릭이 편성 정보를 막지 않게(SCDS-72).
                      suppressClick.current = current.moved && current.delta !== 0;
                      if (current.moved && current.delta) onMove(current.assignment, current.delta);
                    }}
                    onPointerCancel={event => { event.stopPropagation(); drag.current = null; setPreview(null); suppressClick.current = true; }}
                    onClick={event => { event.stopPropagation(); if (suppressClick.current) { suppressClick.current = false; return; } onEdit(assignment, day); }}>
                    <span className="block truncate">{approvalMark(assignment)}{assignment.name}</span>
                    {preview?.id === assignment.id && preview.day === day && <span role="status" aria-label="이동할 날짜"
                      className="pointer-events-none absolute bottom-full left-0 mb-1 whitespace-nowrap rounded bg-panel px-2 py-1 text-text shadow-md">
                      {day} → {addDays(day, preview.delta)}{assignment.repeat.kind !== 'none' ? ' · 전체 반복' : ''}</span>}
                  </button>)}
                  {layer === 'base' && power && (dark
                    ? <span data-power-mark="off" className="block truncate text-[10px] leading-4 text-text-dimmer">종일 꺼짐</span>
                    : <span data-power-mark="on" className="block truncate text-[10px] leading-4 text-emerald-700">
                      ⏻ {power.on.map(r => `${minuteToHHMM(r.start)}–${r.end >= 1440 ? '24:00' : minuteToHHMM(r.end)}`).join(', ')}</span>)}
                </div>;
              })}
            </div>)}
          </div>
        </div>)}
      </section>;
    })}
  </div>;
}

// ── 스케줄 추가·수정 다이얼로그 ──

/**
 * 저장하기 전에 편성표에서 어떻게 보일지 그대로 그려 준다.
 * 겹치면 어느 구간이 가려지는지, 이 스케줄이 몇 번째로 재생되는지를 눈으로 확인하고 순서를 정한다.
 */
function BoardPreview({ draft, siblings, date, deviceName, overlapDays, labelClassName, onReorder }: {
  draft: ScheduleAssignment; siblings: readonly ScheduleAssignment[]; date: string; deviceName: string;
  overlapDays: number; labelClassName: string;
  onReorder: ((target: ScheduleAssignment, above: boolean) => void) | null;
}) {
  // 미리보기는 '승인되면 어떻게 재생되나'를 보여 준다. 승인 대기인 초안을 그대로 넣으면 재생 후보에서
  // 빠져, 겹치는 편성이 없어도 '가려짐'으로 나오고 이름 자리에 undefined가 찍혔다(SCDS-85).
  const { approval: _pending, ...playable } = draft;
  const planned: ScheduleAssignment = playable;
  // 두 레이어를 한 화면에 그린다 — 이벤트가 기본을 덮는 것까지 여기서 보여야 한다.
  const all = [...siblings, planned];
  const lanes = (['event', 'base'] as const).map(layer => {
    const active = byAssignmentPrecedence(all.filter(a => a.layer === layer));
    return { layer, active, rows: stackBlocks(active) };
  });
  const active = lanes.find(lane => lane.layer === draft.layer)!.active;
  const rank = active.indexOf(planned);
  const upper = rank > 0 ? active[rank - 1] : undefined;
  const lower = active[rank + 1];
  // 내가 가려지는 구간과, 내가 가리는 남의 구간을 모두 본다 — 어느 쪽이든 알아야 할 문제다.
  const hidden = planned.blocks.flatMap(block => blockPlaybackSlices(planned, block, all, date).filter(s => s.muted));
  const hiding = siblings.flatMap(other => other.blocks
    .flatMap(block => blockPlaybackSlices(other, block, all, date)
      .filter(s => s.muted && s.winner === draft.name)
      .map(s => ({ ...s, name: other.name }))));
  const trouble = hidden.length > 0 || hiding.length > 0;
  // 두 방향을 따로 말한다 — 내가 멀쩡해도 내가 남을 가리면 그것도 알아야 한다.
  const ranges = (slices: { start: number; end: number }[]) =>
    slices.map(s => `${minuteToHHMM(s.start)}–${minuteToHHMM(s.end)}`).join(', ');
  const victims = [...hiding.reduce((map, slice) => map.set(slice.name, [...(map.get(slice.name) ?? []), slice]),
    new Map<string, typeof hiding>()).entries()];
  // 문제가 있으면 경고만 말한다 — "문제없다"는 줄을 함께 두면 어느 쪽이 맞는지 헷갈린다.
  const lines: string[] = trouble
    ? [...(hidden.length ? [`${ranges(hidden)} 구간은 '${hidden[0]!.winner}'에 가려져 이 스케줄이 재생되지 않습니다.`] : []),
      ...victims.map(([name, slices]) => `이 스케줄이 먼저 재생되어 '${name}'의 ${ranges(slices)} 구간이 재생되지 않습니다.`)]
    : [siblings.length ? '겹쳐서 가려지는 구간 없이 그대로 재생됩니다.' : '겹치는 편성이 없어 그대로 재생됩니다.'];
  return <section className="flex flex-col gap-3" aria-label="편성표 미리보기">
    <div className="flex flex-wrap items-center gap-2">
      <span className={labelClassName}>편성표 미리보기</span>
      <span className="text-[12px] text-text-dim">{deviceName} · {date}</span>
      <span className="ml-auto flex items-center gap-1 text-[12px]">
        <span className="text-text-dim">재생 순서 {rank + 1}/{active.length}</span>
        {onReorder && ([['up', upper, true] as const, ['down', lower, false] as const]).map(([key, neighbour, above]) =>
          <Button key={key} className="!px-2 !py-1 !text-[12px]" disabled={!neighbour}
            aria-label={above ? '먼저 재생' : '나중에 재생'}
            onClick={() => neighbour && onReorder(neighbour, above)}>{above ? '▲' : '▼'}</Button>)}
      </span>
    </div>
    <div className="rounded border border-line bg-panel-2 p-2">
      <div className="flex pl-12">
        <div className="flex flex-1 justify-between px-0.5 text-[10px] text-text-dim">
          {['00:00', '06:00', '12:00', '18:00', '24:00'].map(l => <span key={l}>{l}</span>)}
        </div>
      </div>
      {/* 레이어마다 이름을 왼쪽에 붙여 어느 줄이 무엇인지 편성표와 같은 차례로 읽히게 한다. */}
      {lanes.map(({ layer, active: laneActive, rows }) => <div key={layer} className="mt-1 flex items-stretch gap-1"
        aria-label={`${layer === 'base' ? '기본' : '이벤트'} 레이어 미리보기`}>
        <span className={`flex w-11 shrink-0 items-center justify-center rounded text-[11px] font-medium ${layer === 'event'
          ? 'bg-accent-faint text-accent-dim' : 'text-text-dim'}`}>{layer === 'base' ? '기본' : '이벤트'}</span>
        <div className="flex flex-1 flex-col gap-1">
          {(rows.length ? rows : [[]]).map((row, index) => <div key={index} className="relative h-7 rounded bg-panel" style={laneStyle}>
            {!laneActive.length && index === 0 && <span className="absolute inset-y-0 left-2 flex items-center text-[11px] text-text-dimmer">편성 없음</span>}
            {row.map(({ assignment, block }) => {
              const slices = blockPlaybackSlices(assignment, block, all, date);
              const width = block.endMinute - block.startMinute;
              const fill = scheduleFill(assignment);
              const background = `linear-gradient(to right, ${slices.flatMap(s => {
                const color = s.muted ? `color-mix(in srgb, ${fill} 35%, transparent)` : fill;
                return [`${color} ${(s.start - block.startMinute) / width * 100}%`, `${color} ${(s.end - block.startMinute) / width * 100}%`];
              }).join(', ')})`;
              const mine = assignment === planned;
              // 색은 편성표와 같게 두고, 지금 편집 중인 편성만 테두리로 짚어 준다.
              return <span key={`${assignment.id}-${block.id}`}
                aria-label={`${mine ? '이 스케줄 ' : ''}${assignment.name} ${minuteToHHMM(block.startMinute)}–${minuteToHHMM(block.endMinute)}`}
                className={`absolute inset-y-0 flex items-center overflow-hidden rounded border px-1.5 text-[11px] ${chipTone(assignment)} ${mine
                  ? 'z-10 border-accent ring-2 ring-accent' : ''}`}
                style={{ left: `${block.startMinute / 1440 * 100}%`, width: `${Math.max(0.8, width / 1440 * 100)}%`,
                  backgroundImage: background, backgroundColor: 'transparent', backgroundRepeat: 'no-repeat', backgroundClip: 'padding-box' }}>
                <span className="truncate">{assignment.name}</span>
              </span>;
            })}
          </div>)}
        </div>
      </div>)}
    </div>
    {/* 안내는 한 줄로만 말한다 — 위아래에서 서로 다른 말을 하면 무엇이 맞는지 알 수 없다. */}
    <div role="status" className={`flex flex-col gap-1 rounded px-2 py-1.5 text-[12px] ${trouble
      ? 'border border-warning/40 bg-warning/10 text-warning' : 'text-text-dim'}`}>
      {lines.map(line => <p key={line}>{trouble && <span aria-hidden className="mr-1">⚠</span>}{line}</p>)}
      {/* 규모는 문제가 있을 때만 덧붙인다 — 겹치기만 하고 멀쩡한 날까지 세면 경고처럼 읽힌다. */}
      {trouble && overlapDays > 1 && <p>같은 겹침이 {overlapDays}일 동안 반복됩니다.</p>}
    </div>
  </section>;
}

// ── 날짜 선택 필드 ─────────────────────────────────────────────────────────
// 양력·음력이 같은 모양이어야 한다. 달력을 직접 그리고, 기준은 달력 안에서 고른다.
// 저장은 언제나 양력 ISO 날짜라 기준을 바꿔도 값이 상하지 않는다.
type LunarMonth = { year: number; month: number; intercalation: boolean };

/** 조건에 맞는 가장 가까운 날. 반복 설정을 바꾸면 시작일이 그 조건으로 따라간다. */
function nextMatchingDate(from: string, matches: (date: string) => boolean): string {
  for (let offset = 0; offset < 800; offset++) {
    const date = addDays(from, offset);
    if (matches(date)) return date;
  }
  return from;
}

function nextNthWeekday(from: string, nth: number, weekday: number): string {
  return nextMatchingDate(from, date => {
    const candidate = weekdayInMonth(Number(date.slice(0, 4)), Number(date.slice(5, 7)), weekday, nth);
    return candidate === date;
  });
}
const LUNAR_MIN_YEAR = 1000;
const LUNAR_MAX_YEAR = 2050;

/** 음력을 실제로 쓰는 명절 — 설(음 1.1)과 추석(음 8.15), 각각 앞뒤 하루씩 연휴. */
export function lunarHolidayLabel(solar: string): string | null {
  const lunar = solarToLunar(solar);
  if (!lunar || lunar.intercalation) return null;
  if (lunar.month === 1 && lunar.day <= 2) return '설 연휴';
  if (lunar.month === 8 && lunar.day >= 14 && lunar.day <= 16) return '추석 연휴';
  if (lunar.month === 12) {
    const last = lunarDayOptions(lunar.year, 12, false).at(-1);
    if (lunar.day === last) return '설 연휴';
  }
  return null;
}

function shiftLunarMonth(current: LunarMonth, delta: 1 | -1): LunarMonth {
  const months = lunarMonthOptions(current.year);
  const index = months.findIndex(item => item.month === current.month && item.intercalation === current.intercalation);
  const next = months[index + delta];
  if (next) return { year: current.year, ...next };
  const year = current.year + delta;
  if (year < LUNAR_MIN_YEAR || year > LUNAR_MAX_YEAR) return current;
  const neighbours = lunarMonthOptions(year);
  const edge = delta > 0 ? neighbours[0] : neighbours[neighbours.length - 1];
  return edge ? { year, ...edge } : current;
}

/** "2026-09-16부터" — 매주·매월에서 언제부터 반복이 시작되는지. */
function StartsOn({ label, value, calendar, onChange }: {
  label: string; value: string; calendar: AssignmentCalendar;
  onChange: (solar: string, calendar: AssignmentCalendar) => void;
}) {
  return <div className="flex flex-wrap items-center gap-2 text-[13px]">
    <CalendarField label={label} value={value} calendar={calendar} onChange={onChange} />
    <span className="text-[12px] text-text-dim">부터 반복합니다.</span>
  </div>;
}

/**
 * 달력 팝업(폭 width)이 감싼 스크롤 상자(모달 본문 등)의 안쪽 폭을 넘지 않도록 왼쪽으로 당길 거리.
 * 스크롤 상자는 넘친 내용을 자르거나 좌우 스크롤을 만든다 — 화면 폭이 아니라 그 상자가 기준이다.
 */
export function calendarShift(root: HTMLElement | null, width: number): number {
  if (!root) return 0;
  const rect = root.getBoundingClientRect();
  let left = 8, right = window.innerWidth - 8;
  for (let el = root.parentElement; el; el = el.parentElement) {
    const style = getComputedStyle(el);
    if (style.overflowX === 'visible' && style.overflowY === 'visible') continue;
    const box = el.getBoundingClientRect();
    left = Math.max(left, box.left + parseFloat(style.paddingLeft || '0'));
    right = Math.min(right, box.right - parseFloat(style.paddingRight || '0'));
    break;
  }
  return Math.max(left - rect.left, Math.min(0, right - (rect.left + width)));
}

export function CalendarField({ label, value, calendar, onChange }: {
  label: string; value: string; calendar: AssignmentCalendar;
  onChange: (solar: string, calendar: AssignmentCalendar) => void;
}) {
  const [open, setOpen] = useState(false);
  // 아래 공간이 모자라면 위로 펼친다 — 다이얼로그 하단에서 달력이 잘리지 않게.
  const [dropUp, setDropUp] = useState(false);
  // 오른쪽에 놓인 필드(종료일)에서 달력이 화면 밖으로 나가 좌우 스크롤이 생기지 않게 왼쪽으로 당긴다(2026-09-23 모바일).
  const [shiftX, setShiftX] = useState(0);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // 바깥을 누르거나 Esc를 누르면 닫는다 — 시작일과 종료일 달력이 동시에 떠 있으면 안 된다.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (rootRef.current && target && rootRef.current.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const [solarMonth, setSolarMonth] = useState(value.slice(0, 7));
  const [lunarMonth, setLunarMonth] = useState<LunarMonth | null>(null);
  const lunar = solarToLunar(value);
  const shown = calendar === 'lunar' && lunar
    ? `${lunar.year}. ${pad(lunar.month)}. ${pad(lunar.day)}.${lunar.intercalation ? ' (윤달)' : ''}`
    : `${value.slice(0, 4)}. ${value.slice(5, 7)}. ${value.slice(8, 10)}.`;
  const view: LunarMonth | null = calendar === 'lunar'
    ? lunarMonth ?? (lunar ? { year: lunar.year, month: lunar.month, intercalation: lunar.intercalation } : null) : null;

  const openPicker = () => {
    setSolarMonth(value.slice(0, 7));
    setLunarMonth(lunar ? { year: lunar.year, month: lunar.month, intercalation: lunar.intercalation } : null);
    const rect = anchorRef.current?.getBoundingClientRect();
    setDropUp(!!rect && window.innerHeight - rect.bottom < 380 && rect.top > window.innerHeight - rect.bottom);
    setShiftX(calendarShift(rootRef.current, 256));
    setOpen(true);
  };
  const pick = (solar: string) => { onChange(solar, calendar); setOpen(false); };
  const setCalendar = (next: AssignmentCalendar) => {
    onChange(value, next);
    if (next === 'lunar' && lunar) setLunarMonth({ year: lunar.year, month: lunar.month, intercalation: lunar.intercalation });
    else setSolarMonth(value.slice(0, 7));
  };

  // 달력 한 칸씩: 첫 날의 요일만큼 앞을 비우고 채운다.
  let title = '';
  let cells: Array<{ solar: string; label: number } | null> = [];
  if (calendar === 'lunar' && view) {
    const days = lunarDayOptions(view.year, view.month, view.intercalation);
    const solarDays = days.map(day => ({ day, solar: lunarToSolar({ ...view, day }) }))
      .filter((item): item is { day: number; solar: string } => item.solar !== null);
    title = `${view.year}년 ${view.intercalation ? '윤' : ''}${view.month}월 (음력)`;
    const lead = solarDays.length ? weekdayOf(solarDays[0]!.solar) : 0;
    cells = [...Array<null>(lead).fill(null), ...solarDays.map(item => ({ solar: item.solar, label: item.day }))];
  } else {
    const year = Number(solarMonth.slice(0, 4));
    const month = Number(solarMonth.slice(5, 7));
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    title = `${year}년 ${month}월`;
    const lead = weekdayOf(`${solarMonth}-01`);
    cells = [...Array<null>(lead).fill(null), ...Array.from({ length: daysInMonth }, (_, index) =>
      ({ solar: `${solarMonth}-${pad(index + 1)}`, label: index + 1 }))];
  }
  const step = (delta: 1 | -1) => {
    if (calendar === 'lunar' && view) { setLunarMonth(shiftLunarMonth(view, delta)); return; }
    const total = Number(solarMonth.slice(0, 4)) * 12 + Number(solarMonth.slice(5, 7)) - 1 + delta;
    setSolarMonth(`${String(Math.floor(total / 12)).padStart(4, '0')}-${pad(total % 12 + 1)}`);
  };

  // 라벨을 옆에 두어 세로로 쌓이지 않게 한다 — 다이얼로그 높이를 아끼는 쪽이 낫다.
  return <div ref={rootRef} className="relative flex items-center gap-1.5">
    <span className="shrink-0 text-text-dim">{label}</span>
    <button ref={anchorRef} type="button" aria-label={label} aria-haspopup="dialog" onClick={() => open ? setOpen(false) : openPicker()}
      className="flex items-center gap-1.5 rounded border border-line bg-panel-2 p-1.5 text-left">
      {calendar === 'lunar' && <span className="rounded bg-accent-soft px-1 text-[11px] font-semibold text-accent">음력</span>}
      <span className="font-mono">{shown}</span>
      <span aria-hidden className="text-text-dim">▾</span>
    </button>
    {open && <div role="dialog" aria-label={`${label} 달력`} style={shiftX ? { left: shiftX } : undefined}
      className={`absolute z-30 w-64 rounded border border-line bg-panel p-2 shadow-lg ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
      <div className="mb-2"><Segmented value={calendar} onChange={setCalendar}
        options={[{ value: 'solar', label: '양력' }, { value: 'lunar', label: '음력' }]} /></div>
      <div className="mb-1 flex items-center justify-between">
        <Button aria-label="이전 달" className="!px-2 !py-0.5" onClick={() => step(-1)}>‹</Button>
        <span className="text-[13px] font-medium">{title}</span>
        <Button aria-label="다음 달" className="!px-2 !py-0.5" onClick={() => step(1)}>›</Button>
      </div>
      <div className="grid grid-cols-7 text-center text-[11px] text-text-dim">
        {DAY_LABELS.map(day => <span key={day}>{day}</span>)}</div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((cell, index) => { if (cell === null) return <span key={`gap-${index}`} />;
          const holiday = lunarHolidayLabel(cell.solar);
          const isToday = cell.solar === todayISO();
          return <button key={cell.solar} type="button" onClick={() => pick(cell.solar)}
            aria-label={`${cell.solar}${holiday ? ` ${holiday}` : ''}`}
            title={holiday ? `${holiday} · ${lunarDateLabel(cell.solar) ?? ''}` : lunarDateLabel(cell.solar) ?? undefined}
            className={`relative rounded py-1 text-center text-[12px] ${cell.solar === value
              ? 'bg-accent font-semibold text-white' : `hover:bg-accent-soft ${holiday ? 'font-semibold text-red' : ''}`}
              ${isToday && cell.solar !== value ? 'border border-accent' : ''}`}>{cell.label}</button>; })}
      </div>
      {/* 오늘이 음력으로 며칠인지, 붉은 날이 무엇인지 달력 안에서 바로 읽힌다. */}
      <div className="mt-1.5 flex items-center justify-between gap-2 border-t border-line pt-1.5 text-[11px] text-text-dim">
        <span aria-label="오늘 정보">오늘 {Number(todayISO().slice(5, 7))}. {Number(todayISO().slice(8, 10))}. · {lunarDateLabel(todayISO()) ?? '음력 변환 불가'}</span>
        <span className="text-red">설·추석 연휴</span>
      </div>
      <div className="mt-1 flex justify-end"><Button className="!py-0.5 text-[12px]" onClick={() => setOpen(false)}>닫기</Button></div>
    </div>}
  </div>;
}
// ── 24시간 타임라인 편집기 ─────────────────────────────────────────────────
// 편성 하나가 하루 동안 무엇을 트는지 여기서 직접 만든다. 구간을 끌어 길이를 바꾸고,
// 구간마다 플레이리스트를 고른다. 저장 단위는 분이고 5분 단위로 맞춰진다.
const SNAP = 5;
export type Segment = { key: string; playlistId: string; startMinute: number; endMinute: number };

const snapMinute = (value: number) => Math.max(0, Math.min(1440, Math.round(value / SNAP) * SNAP));

/**
 * 끌고 간 자리에서 가장 가까우면서 구간이 통째로 들어가는 빈 자리를 고른다.
 * 이웃에 딱 붙어 있어도 그 너머 빈 자리로 건너갈 수 있어야 하기 때문이다.
 * 한 기기가 같은 시각에 두 화면을 틀 수는 없으므로 겹치는 자리는 고르지 않는다.
 */
export function nearestFreeStart(others: readonly { startMinute: number; endMinute: number }[],
  width: number, desired: number): number | null {
  const busy = [...others].sort((a, b) => a.startMinute - b.startMinute);
  const gaps: [number, number][] = [];
  let edge = 0;
  for (const segment of busy) {
    if (segment.startMinute > edge) gaps.push([edge, segment.startMinute]);
    edge = Math.max(edge, segment.endMinute);
  }
  if (edge < 1440) gaps.push([edge, 1440]);
  let best: number | null = null;
  for (const [from, to] of gaps) {
    if (to - from < width) continue;
    const start = Math.min(to - width, Math.max(from, desired));
    if (best === null || Math.abs(start - desired) < Math.abs(best - desired)) best = start;
  }
  return best;
}
export const hhmmToMinute = (value: string) => {
  const [hour, minute] = value.split(':');
  return Math.max(0, Math.min(1440, Number(hour) * 60 + Number(minute)));
};
const newKey = () => Math.random().toString(36).slice(2, 10);

/**
 * 한쪽 시각을 반대쪽 너머로 옮기면, 구간을 뒤집거나 잘라내지 않고 길이를 유지한 채 함께 민다.
 * 잘라내면 타이핑한 적 없는 시각(19시를 치면 17:55)이 조용히 저장돼 버린다.
 */
export function retimeSegment(segment: Pick<Segment, 'startMinute' | 'endMinute'>, edge: 'start' | 'end', value: number):
  Pick<Segment, 'startMinute' | 'endMinute'> {
  const span = Math.max(SNAP, segment.endMinute - segment.startMinute);
  if (edge === 'start') {
    const startMinute = Math.max(0, Math.min(1440 - SNAP, value));
    if (startMinute + SNAP <= segment.endMinute) return { startMinute, endMinute: segment.endMinute };
    return { startMinute, endMinute: Math.min(1440, startMinute + span) };
  }
  const endMinute = Math.max(SNAP, Math.min(1440, value));
  if (segment.startMinute + SNAP <= endMinute) return { startMinute: segment.startMinute, endMinute };
  return { startMinute: Math.max(0, endMinute - span), endMinute };
}

/**
 * 24시간제 시각 입력. 브라우저 기본 시간 입력은 지역 설정에 따라 오전·오후를
 * 따로 골라야 적용돼서 쓰기 불편하다. 시·분을 그대로 고르게 한다.
 */
/**
 * 애플 캘린더식 타이핑 시간 칸 — 자릿수를 이어 치면 그대로 들어가고,
 * 있을 수 없는 값이 되는 순간의 키 입력은 무시된다(26시의 6처럼).
 *
 * 치는 도중의 값은 칸 안에서만 보여 주고, 다 친 뒤에야 구간에 반영한다.
 * 첫 자리만 친 채로 반영하면(종료 20시의 "2" → 02시) 시작보다 앞선 시각이 되어
 * 반대쪽 시각이 밀려나 버린다 — 치지도 않은 시작 시각이 바뀌는 버그의 원인.
 */
function TimeCell({ label, value, max, disabled, onCommit }: {
  label: string; value: number; max: number; disabled?: boolean | undefined; onCommit: (next: number) => void;
}) {
  const buffer = useRef('');
  const [pending, setPending] = useState<number | null>(null);
  const commit = (next: number) => { buffer.current = ''; setPending(null); onCommit(next); };
  const flush = () => {
    if (pending !== null && pending !== value) commit(pending);
    else { buffer.current = ''; setPending(null); }
  };
  return <input aria-label={label} value={pad(pending ?? value)} disabled={disabled} inputMode="numeric"
    className="w-[2ch] cursor-text bg-transparent text-center font-mono text-[13px] outline-none disabled:cursor-not-allowed"
    onFocus={event => { buffer.current = ''; setPending(null); event.currentTarget.select(); }}
    onBlur={flush}
    onKeyDown={event => {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        commit(Math.max(0, Math.min(max, (pending ?? value) + (event.key === 'ArrowUp' ? 1 : -1))));
        return;
      }
      if (event.key === 'Enter') { flush(); return; }
      // 지우기는 값을 바꾸지 않고 타이핑만 처음부터 다시 받는다.
      // 0을 커밋하면 반대쪽 시각에 밀려 치지도 않은 값이 되어 버린다.
      if (event.key === 'Backspace' || event.key === 'Delete') { event.preventDefault(); buffer.current = ''; setPending(null); return; }
      if (!/^[0-9]$/.test(event.key)) return;
      event.preventDefault();
      // 앞자리에 이어 붙이면 있을 수 없는 값이 되는 숫자는, 그 숫자부터 새로 친 것으로 본다.
      // 무시만 하면 앞자리가 버퍼에 남아 이후 입력이 전부 막힌다.
      const joined = buffer.current + event.key;
      const candidate = Number(joined) <= max ? joined : event.key;
      if (Number(candidate) > max) return;
      // 두 자리가 찼거나, 뒤에 어떤 숫자를 붙여도 넘치는 첫 자리(시의 3~9, 분의 6~9)면 다 친 것이다.
      const complete = candidate.length >= 2 || Number(candidate) * 10 > max;
      if (complete) { commit(Number(candidate)); return; }
      buffer.current = candidate;
      setPending(Number(candidate));
    }}
    onChange={event => {
      // 붙여넣기 등 키 입력을 거치지 않는 변경도 같은 규칙으로 받는다.
      const digits = event.target.value.replace(/\D/g, '').slice(-2);
      if (!digits) return;
      if (Number(digits) <= max) commit(Number(digits));
    }} />;
}

function TimeField({ label, minute, disabled, onChange }: {
  label: string; minute: number; disabled?: boolean; onChange: (minute: number) => void;
}) {
  const hour = Math.min(24, Math.floor(minute / 60));
  const rest = hour === 24 ? 0 : minute % 60;
  return <span role="group" aria-label={label}
    className={`flex items-center gap-0.5 rounded border border-line bg-panel-2 px-1.5 py-1 ${disabled ? 'opacity-40' : ''}`}>
    <TimeCell label={`${label} 시`} value={hour} max={24} disabled={disabled}
      onCommit={next => onChange(next === 24 ? 1440 : next * 60 + rest)} />
    <span className="text-text-dim">:</span>
    <TimeCell label={`${label} 분`} value={rest} max={59} disabled={disabled || hour === 24}
      onCommit={next => onChange(hour * 60 + next)} />
  </span>;
}

/** 누른 지점이 들어 있는 빈 자리. 타임라인의 빈 곳을 눌러 바로 구간을 만들 때 쓴다. */
export function freeSlotAt(segments: Segment[], minute: number, span = 60): { startMinute: number; endMinute: number } | null {
  if (segments.some(s => s.startMinute <= minute && minute < s.endMinute)) return null;
  const start = Math.max(0, ...segments.filter(s => s.endMinute <= minute).map(s => s.endMinute));
  const end = Math.min(1440, ...segments.filter(s => s.startMinute > minute).map(s => s.startMinute));
  if (end - start < SNAP) return null;
  // 누른 곳을 가운데로 두되 빈 자리를 벗어나지 않는다.
  const width = Math.min(span, end - start);
  const from = Math.min(end - width, Math.max(start, snapMinute(minute - width / 2)));
  return { startMinute: from, endMinute: from + width };
}

/** 비어 있는 가장 이른 자리를 찾아 새 구간을 넣는다. 없으면 추가하지 않는다. */
export function nextFreeSlot(segments: Segment[], span = 60): { startMinute: number; endMinute: number } | null {
  const taken = [...segments].sort((a, b) => a.startMinute - b.startMinute);
  let cursor = 0;
  for (const segment of taken) {
    if (segment.startMinute - cursor >= SNAP) return { startMinute: cursor, endMinute: Math.min(segment.startMinute, cursor + span) };
    cursor = Math.max(cursor, segment.endMinute);
  }
  if (1440 - cursor >= SNAP) return { startMinute: cursor, endMinute: Math.min(1440, cursor + span) };
  return null;
}

export function segmentsOverlap(segments: Segment[]): boolean {
  return segments.some(segment => segment.startMinute >= segment.endMinute
    || segments.some(other => other.key !== segment.key
      && other.startMinute < segment.endMinute && segment.startMinute < other.endMinute));
}

/**
 * 플레이리스트 한 바퀴를 그대로 펼친 썸네일 띠. 영상 편집 툴의 트랙처럼
 * 각 항목이 재생 시간만큼 폭을 차지해, 무엇이 얼마나 도는지 한눈에 보인다.
 */
export type StripCell = { url: string | null; label: string; weight: number };
export function stripThumbs(playlist: Playlist | undefined, mediaById: Map<string, MediaItem>,
  noticeById: Map<string, Notice> = new Map()): StripCell[] {
  if (!playlist) return [];
  return playlist.items.slice(0, 60).map(item => {
    const fallback = item.durationSec ?? playlist.imageDurationSec ?? 10;
    // 공지도 저장된 썸네일이 있으면 그대로 보여준다. 없을 때만 글자로 적는다.
    if (item.noticeId) {
      const notice = noticeById.get(item.noticeId);
      return { url: notice?.thumbnailRelPath ? api.noticeThumbnailUrl(notice.id, notice.thumbnailRelPath) : null,
        label: '공지', weight: Math.max(1, fallback) };
    }
    if (!item.mediaId) return { url: null, label: item.webUrl ? '웹' : '공지', weight: Math.max(1, fallback) };
    const media = mediaById.get(item.mediaId);
    if (!media) return { url: null, label: '없음', weight: Math.max(1, fallback) };
    const url = media.thumbnailRelPath ? api.mediaThumbnailUrl(media.id)
      : media.mediaType === 'image' ? api.mediaFileUrl(media.id) : null;
    const seconds = media.mediaType === 'image' ? fallback : media.durationSec ?? fallback;
    return { url, label: media.mediaType === 'video' ? '영상' : '이미지', weight: Math.max(1, seconds) };
  });
}

function SmoothHeight({ children }: { children: ReactNode }) {
  const inner = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();
  useEffect(() => {
    if (!inner.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => { if (entry) setHeight(entry.contentRect.height); });
    observer.observe(inner.current);
    return () => observer.disconnect();
  }, []);
  return <div style={{ height }} className="shrink-0 transition-[height] duration-300 ease-in-out motion-reduce:transition-none">
    <div ref={inner}>{children}</div>
  </div>;
}

// 아이콘만 있는 정사각 버튼 — 글리프가 아니라 도형이라 어느 폰트에서도 가운데에 선다.
const iconButton = 'h-8 w-8 justify-center !gap-0 !px-0 !py-0';
function PlusIcon() {
  return <svg viewBox="0 0 16 16" aria-hidden className="h-4 w-4" fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <path d="M8 3.5v9M3.5 8h9" />
  </svg>;
}

// =============================================================================
// 그룹 경계 — 기기의 그룹과 콘텐츠의 소유 그룹이 맞아야 편성된다.
//
// 서버 대응(같은 규칙을 두 군데 다르게 쓰면 또 어긋난다):
//   packages/main/src/services/groupWorkflow.ts
//     · reachesGroup()    — 판정 한 곳. 아니면 403.
//     · approvedRoot() / approvedVersion() — 고정 버전(cv-)도 같은 판정을 다시 본다.
//   두 곳 모두 플레이리스트 본체만이 아니라 versions.listTree()로 그 안의 미디어·슬라이드까지 훑는다.
//   그래서 여기서도 '플레이리스트 → 그 안의 아이템' 순서로 같은 판정을 돌린다.
//   문구를 만드는 쪽은 packages/main/src/services/scheduleAssignments.ts의 assignableBlocks()다.
//
// 이 판정은 그룹 권한을 '적용'한 조직(actor.enforced)에서만 돈다 —
// 서버도 create()/update()에서 actor.enforced일 때만 승인 버전 변환을 태운다.
// =============================================================================

/** 콘텐츠 하나가 이 그룹의 기기로 갈 수 있는가. 위 서버 reachesGroup()과 같은 판정. */
export function contentReachesGroup(content: ContentAccessMetadata | undefined, groupId: string | null): boolean {
  // 소유 그룹을 모르는 콘텐츠(적용 전 조직, 목록에 메타가 안 붙은 경우)는 막지 않는다.
  // 화면이 서버보다 엄해지면 "왜 못 고르지"가 되고, 그건 저장 실패보다 알아차리기 어렵다.
  if (!content || content.ownerGroupId === undefined) return true;
  // 공통(중앙 소유)은 모든 그룹의 상위라 어느 그룹 기기에도 간다(2026-09-17 대교 보고).
  // 여기 목록에 들어온 시점에서 이미 읽을 수 있는 콘텐츠다 — 그룹 사용자에게 중앙 비공개
  // 콘텐츠는 서버가 목록에서 걸러 내므로, 이 예외가 가시성을 넓히지는 않는다.
  if ((content.ownerGroupId ?? null) === null) return true;
  return content.ownerGroupId === groupId || content.sharedToAll === true;
}

/** 편성을 막는 콘텐츠 하나. `via`가 null이면 플레이리스트 자신이, 아니면 그 안의 콘텐츠가 원인이다. */
export type GroupBlock = {
  ownerGroupId: string | null;
  playlistName: string;
  via: { kind: '미디어' | '슬라이드'; name: string } | null;
};

/**
 * 이 플레이리스트를 groupId 기기에 편성할 수 없게 만드는 첫 콘텐츠. 편성할 수 있으면 null.
 * 서버가 의존 트리를 훑다가 처음 걸리는 것으로 던지므로 여기서도 처음 걸리는 하나만 돌려준다.
 */
export function playlistGroupBlock(playlist: Playlist | undefined, groupId: string | null,
  mediaById: Map<string, MediaItem>, noticeById: Map<string, Notice>): GroupBlock | null {
  if (!playlist) return null;
  const playlistName = playlist.name;
  if (!contentReachesGroup(playlist, groupId)) return { ownerGroupId: playlist.ownerGroupId ?? null, playlistName, via: null };
  for (const item of playlist.items ?? []) {
    const dep: ContentAccessMetadata & { name?: string; originalName?: string } | undefined =
      item.noticeId ? noticeById.get(item.noticeId) : item.mediaId ? mediaById.get(item.mediaId) : undefined;
    if (!dep || contentReachesGroup(dep, groupId)) continue;
    return { ownerGroupId: dep.ownerGroupId ?? null, playlistName,
      via: { kind: item.noticeId ? '슬라이드' : '미디어', name: dep.name ?? dep.originalName ?? '이름 확인 불가' } };
  }
  return null;
}

/**
 * 왜 못 쓰는지 한 줄. 플레이리스트를 고를 때도, 기기를 고를 때도 같은 문장을 쓴다 —
 * 같은 사실을 두 자리에서 다르게 적으면 어느 쪽이 맞는지 알 수 없다.
 * 이름 뒤에는 조사를 붙이지 않는다(이름마다 은/는·이/가가 달라진다).
 */
export function groupBlockReason(block: GroupBlock, ownerLabel: string, targetLabel: string): string {
  const what = block.via ? `${block.via.kind}(${block.via.name})` : `플레이리스트(${block.playlistName})`;
  return `${ownerLabel} 소유 ${what} 때문에 ${targetLabel}에는 편성할 수 없습니다. (조직 공유 아님)`;
}

function BlockTimelineEditor({ segments, playlists, sections, blockReason, mediaById, noticeById, layer, onChange }: {
  segments: Segment[]; playlists: Playlist[];
  /** 플레이리스트를 소유 그룹별로 묶은 것(라이브러리와 같은 묶음). null 이면 묶지 않고 평평하게. */
  sections: GroupSection<Playlist>[] | null;
  /** 이 플레이리스트가 지금 고른 기기에 못 가는 이유(한국어). 갈 수 있으면 null. */
  blockReason: (playlistId: string) => string | null;
  mediaById: Map<string, MediaItem>; noticeById: Map<string, Notice>;
  layer: AssignmentLayer; onChange: (next: Segment[]) => void;
}) {
  const defaultPlaylistId = playlists.find(item => !item.id.startsWith('cv-') && !blockReason(item.id))?.id ?? '';
  const barRef = useRef<HTMLDivElement | null>(null);
  const selection = useCalendarRange();
  const placeholder = segments.length === 1 && !segments[0]!.playlistId && segments[0]!.startMinute === 0 && segments[0]!.endMinute === 1440;
  // 열린 구간 — 플레이리스트·시간·삭제를 이 자리에서 다 처리한다.
  const [editing, setEditing] = useState<string | null>(null);
  const draggedRef = useRef(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // 바깥을 누르면 닫는다 — 닫기 버튼을 찾아 누를 필요가 없다.
  useEffect(() => {
    if (!editing) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-segment]') || (panelRef.current && target && panelRef.current.contains(target))) return;
      setEditing(null);
    };
    document.addEventListener('mousedown', onDown);
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setEditing(null); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [editing]);
  const ordered = [...segments].sort((a, b) => a.startMinute - b.startMinute);
  const patch = (key: string, next: Partial<Segment>) => onChange(segments.map(s => s.key === key ? { ...s, ...next } : s));
  const minuteAt = (clientX: number) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return 0;
    return snapMinute((clientX - rect.left) / rect.width * 1440);
  };
  const drag = (key: string, mode: 'start' | 'end' | 'move') => (event: { clientX: number; preventDefault(): void; stopPropagation(): void }) => {
    const target = segments.find(s => s.key === key);
    if (!target || !barRef.current) return;
    event.preventDefault(); event.stopPropagation();
    const origin = minuteAt(event.clientX);
    const base = { start: target.startMinute, end: target.endMinute };
    draggedRef.current = false;
    const others = segments.filter(s => s.key !== key);
    // 이웃 구간을 넘어설 수 없다 — 한 기기가 같은 시각에 두 화면을 틀 수는 없다.
    const lower = Math.max(0, ...others.filter(s => s.endMinute <= base.start).map(s => s.endMinute));
    const upper = Math.min(1440, ...others.filter(s => s.startMinute >= base.end).map(s => s.startMinute));
    const onMove = (native: PointerEvent) => {
      const delta = minuteAt(native.clientX) - origin;
      if (delta !== 0) draggedRef.current = true;
      if (mode === 'start') patch(key, { startMinute: Math.min(base.end - SNAP, Math.max(lower, base.start + delta)) });
      else if (mode === 'end') patch(key, { endMinute: Math.max(base.start + SNAP, Math.min(upper, base.end + delta)) });
      else {
        const width = base.end - base.start;
        const start = nearestFreeStart(others, width, Math.max(0, Math.min(1440 - width, base.start + delta)));
        if (start !== null) patch(key, { startMinute: start, endMinute: start + width });
      }
    };
    const onUp = () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
    window.addEventListener('pointermove', onMove); window.addEventListener('pointerup', onUp);
  };
  const lookup = (id: string) => playlists.find(p => p.id === id);
  const playlistName = (id: string) => lookup(id)?.name ?? (id ? '지정된 플레이리스트 (이름 확인 불가)' : '');
  const add = (slot: { startMinute: number; endMinute: number } | null) => {
    if (!slot) return;
    const key = newKey();
    onChange([...segments, { key, playlistId: defaultPlaylistId, ...slot }]);
    // 새 구간은 곧바로 열어 준다 — 무엇을 틀지부터 골라야 하기 때문이다.
    setEditing(key);
  };

  return <div className="relative flex flex-col gap-2">
    <div>
      <div className="flex justify-between text-[11px] text-text-dim">{['00:00', '06:00', '12:00', '18:00', '24:00'].map(l => <span key={l}>{l}</span>)}</div>
      {/* 모든 조작이 이 타임라인 위에서 끝난다 — 빈 곳은 추가, 구간은 눌러서 편집. */}
      <div ref={barRef} aria-label="24시간 타임라인" title="빈 곳을 클릭하면 그 시간에 플레이리스트가 추가됩니다."
        className="relative mt-1 h-16 cursor-copy select-none rounded border border-line bg-panel" style={laneStyle}
        onPointerDown={event => {
          if ((event.target as HTMLElement).closest('[data-segment]') && !placeholder) return;
          const occupied = placeholder ? [] : segments;
          const minute = minuteAt(event.clientX);
          if (occupied.some(s => s.startMinute <= minute && minute < s.endMinute)) return;
          const lower = Math.max(0, ...occupied.filter(s => s.endMinute <= minute).map(s => s.endMinute));
          const upper = Math.min(1440, ...occupied.filter(s => s.startMinute >= minute).map(s => s.startMinute));
          selection.down(event, 'editor', 288, (start, end, moved) => {
            if (!moved) { if (placeholder) setEditing(segments[0]!.key); else add(freeSlotAt(occupied, minute)); return; }
            const startMinute = Math.max(lower, start * SNAP);
            const endMinute = Math.min(upper, Math.max(start + 1, end) * SNAP);
            if (endMinute <= startMinute) return;
            const key = newKey();
            onChange([...occupied, { key, playlistId: defaultPlaylistId, startMinute, endMinute }]);
            draggedRef.current = true; setEditing(key);
          }, true);
        }}
        onPointerMove={selection.move} onPointerUp={selection.up} onPointerCancel={selection.cancel}
        onClick={event => {
          if (selection.clicked()) return;
          if ((event.target as HTMLElement).closest('[data-segment]')) return;
          if (editing) { setEditing(null); return; }
          add(freeSlotAt(segments, minuteAt(event.clientX)));
        }}>
        {ordered.map(segment => { const thumbs = stripThumbs(lookup(segment.playlistId), mediaById, noticeById);
          return <div key={segment.key} data-segment
          aria-label={`${playlistName(segment.playlistId) || '플레이리스트 미선택'} ${minuteToHHMM(segment.startMinute)}–${minuteToHHMM(segment.endMinute)}`}
          className={`absolute top-1 flex h-14 cursor-grab items-center overflow-hidden rounded border px-1 text-[11px] ${layer === 'base'
            ? 'border-warning bg-warning/15 text-warning' : 'border-accent bg-accent-soft text-accent-dim'} ${editing === segment.key ? 'ring-2 ring-accent' : ''}`}
          style={{ left: `${segment.startMinute / 1440 * 100}%`, width: `${Math.max(0.8, (segment.endMinute - segment.startMinute) / 1440 * 100)}%` }}
          onPointerDown={placeholder ? undefined : drag(segment.key, 'move')}
          onClick={event => {
            event.stopPropagation();
            if (selection.clicked()) return;
            // 끌어서 옮긴 뒤의 클릭은 편집으로 치지 않는다.
            if (draggedRef.current) { draggedRef.current = false; return; }
            setEditing(current => current === segment.key ? null : segment.key);
          }}>
          {/* 썸네일 띠 — 이 구간에 무엇이 도는지 색이 아니라 내용으로 보인다. */}
          {thumbs.length > 0 && <span aria-hidden className="pointer-events-none absolute inset-0 flex">
            {thumbs.map((thumb, index) => <span key={index} style={{ flex: `${thumb.weight} 1 0%` }}
              className="relative min-w-0 overflow-hidden border-r border-white/40 last:border-r-0">
              {thumb.url
                ? <img src={thumb.url} alt="" draggable={false} className="h-full w-full object-cover opacity-70" />
                : <span className="flex h-full w-full items-end justify-center bg-black/5 pb-0.5 text-[9px] text-text-dimmer">{thumb.label}</span>}
            </span>)}
          </span>}
          {/* 이름이 썸네일 위에서도 읽히도록 얇은 막을 덮는다. */}
          {thumbs.length > 0 && <span aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/85 via-white/40 to-white/10" />}
          <span aria-label="시작 시각 조절" onPointerDown={drag(segment.key, 'start')}
            className="absolute inset-y-0 left-0 w-2 cursor-ew-resize rounded-l bg-black/10" />
          {/* 이름과 함께 정확한 시각을 적는다 — 끌어서 맞출 때 몇 시인지 바로 보여야 한다. */}
          <span className="pointer-events-none relative ml-2 mr-3 flex min-w-0 flex-col leading-tight">
            <span className="truncate font-medium">{playlistName(segment.playlistId) || '플레이리스트 선택'}</span>
            <span className="truncate font-mono text-[10px] text-text-dim">
              {minuteToHHMM(segment.startMinute)}–{minuteToHHMM(segment.endMinute)}</span>
          </span>
          <span aria-label="종료 시각 조절" onPointerDown={drag(segment.key, 'end')}
            className="absolute inset-y-0 right-0 w-2 cursor-ew-resize rounded-r bg-black/10" />
        </div>; })}
        {selection.range && <div aria-label="추가할 시간 구간" className="pointer-events-none absolute inset-y-1 z-10 rounded border border-accent bg-accent-soft/80 px-2 text-xs"
          style={{ left: `${selection.range.start / 288 * 100}%`, width: `${Math.max(1, selection.range.end - selection.range.start) / 288 * 100}%` }}>
          {minuteToHHMM(selection.range.start * SNAP)}–{minuteToHHMM(selection.range.end * SNAP)}
        </div>}
        {!segments.length && <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[12px] text-text-dim">
          빈 곳을 클릭해 플레이리스트를 추가하세요.</span>}
      </div>
    </div>
    {ordered.map(editingSegment => {
      const wholeDay = segments.length === 1 && editingSegment.startMinute === 0 && editingSegment.endMinute === 1440;
      return <div key={editingSegment.key} role="group" aria-label="구간 설정"
      className="flex flex-wrap items-center gap-2 py-1 text-[13px]">
      {/* 대상 기기 그룹이 쓸 수 없는 플레이리스트는 목록에서 빼지 않는다 — 빠지면 "왜 안 보이지"가 된다.
          자리는 그대로 두고 비활성 + 이유(제목은 짧게, title은 한 문장)로 알린다. */}
      <select aria-label="플레이리스트" value={editingSegment.playlistId}
        title={blockReason(editingSegment.playlistId) ?? undefined}
        className={`min-w-40 flex-1 rounded border p-1.5 ${editingSegment.playlistId
          ? blockReason(editingSegment.playlistId) ? 'border-red bg-panel-2 text-red' : 'border-line bg-panel-2'
          : 'border-accent/60 bg-accent-faint font-medium text-accent-dim'}`}
        onChange={event => patch(editingSegment.key, { playlistId: event.target.value })}>
        {!editingSegment.playlistId && <option value="" disabled hidden>
          {defaultPlaylistId ? '플레이리스트 미지정' : '사용 가능한 플레이리스트가 없습니다'}
        </option>}
        {/* 고를 수 있는 것은 원본뿐이다. 이미 저장된 승인본(cv-…)은 지금 값일 때만 자리에 남겨
            무엇이 편성돼 있는지 보이게 한다 — 값이 사라지면 편성이 지워진 것처럼 읽힌다.
            이름까지 못 읽은 id(그룹 사용자가 중앙 편성을 볼 때)는 자리 표시로 남긴다. */}
        {editingSegment.playlistId && !playlists.some(p => p.id === editingSegment.playlistId)
          && <option value={editingSegment.playlistId}>지정된 플레이리스트 (이름 확인 불가)</option>}
        {(() => {
          // 지금 값이 승인본(cv-)이면 그 원본은 같은 이름으로 한 번 더 보이므로 숨긴다 — 플레이리스트가
          // 둘인 것처럼 읽힌다(2026-10-01 대교). 다른 것을 고르면 승인본이 빠지고 원본이 다시 보인다.
          const current = playlists.find(item => item.id === editingSegment.playlistId);
          const shadowed = current?.id.startsWith('cv-') ? current.approvedFrom : undefined;
          const pickable = (item: Playlist) => (!item.id.startsWith('cv-') || item.id === editingSegment.playlistId) && item.id !== shadowed;
          const option = (playlist: Playlist) => { const reason = blockReason(playlist.id);
            return <option key={playlist.id} value={playlist.id}
              // 이미 골라 둔 것이 나중에 막히면(기기를 더 담는 등) 값이 사라지면 안 되므로 그때는 잠그지 않는다.
              disabled={!!reason && editingSegment.playlistId !== playlist.id} title={reason ?? undefined}>
              {playlist.name}{reason ? ' — 이 기기 그룹에서 사용 불가' : ''}</option>; };
          // 그룹이 여럿 보이는 사람에게는 라이브러리와 같은 묶음(내 자료 · 그룹별)으로 나눠 보여 준다(2026-10-01 대교).
          // 빈 묶음은 머리를 두지 않는다 — 고를 것이 없는 머리는 노이즈다.
          return sections
            ? sections.map(section => { const items = section.items.filter(pickable); return items.length
                ? <optgroup key={section.key} label={section.label}>{items.map(option)}</optgroup> : null; })
            : playlists.filter(pickable).map(option);
        })()}
      </select>
      {/* 하루 종일은 이 구간의 시간 설정이다 — 켜면 하루 전체를 이 플레이리스트가 가진다. */}
      <label className="flex items-center gap-1.5">
        <input type="checkbox" aria-label="하루 종일" checked={wholeDay}
          onChange={event => onChange(event.target.checked
            ? [{ ...editingSegment, startMinute: 0, endMinute: 1440 }]
            : segments.map(s => s.key === editingSegment.key ? { ...s, startMinute: 540, endMinute: 1080 } : s))} />하루 종일
      </label>
      {/* 하루 종일이면 시간 칸은 자리를 지킨 채 비활성으로 둔다 — 줄이 흔들리면 안 된다. */}
      <TimeField label="시작 시각" minute={editingSegment.startMinute} disabled={wholeDay}
        onChange={value => patch(editingSegment.key, retimeSegment(editingSegment, 'start', value))} />
      <span className="text-text-dim">–</span>
      <TimeField label="종료 시각" minute={editingSegment.endMinute} disabled={wholeDay}
        onChange={value => patch(editingSegment.key, retimeSegment(editingSegment, 'end', value))} />
      <Button aria-label="구간 삭제" className="!px-2 !py-1"
        onClick={() => { onChange(segments.filter(s => s.key !== editingSegment.key)); setEditing(null); }}>삭제</Button>
    </div>; })}
    <div><Button aria-label="플레이리스트 추가" title="플레이리스트 추가" className={iconButton} disabled={!nextFreeSlot(segments)}
      onClick={() => add(nextFreeSlot(segments))}><PlusIcon /></Button></div>
  </div>;
}

export function AssignmentDialog({ scope, templates, blocks, playlists, media, notices, devices, groups, assignments, initial, occurrenceDate, preset, batchSize, batchDevices, onClose, onSaved }: {
  scope: WorkflowScope; templates: ScheduleType[]; blocks: TimelineBlock[]; playlists: Playlist[];
  media: MediaItem[]; notices: Notice[];
  devices: WorkflowScope['devices']; groups: WorkflowScope['groups'];
  assignments: ScheduleAssignment[];
  initial: ScheduleAssignment | null; occurrenceDate: string | null; preset: CreatePreset | null;
  batchSize: number; batchDevices: string[];
  onClose: () => void; onSaved: (change?: ScheduleMutation) => void;
}) {
  // 저장 후 전원 충돌 안내에만 쓴다 — 공휴일 규칙을 가진 기기가 있을 때만 받는다.
  const { holidays } = usePublicHolidays(devices.some(d => d.powerSchedule?.holiday));
  // 대행 '읽기 전용'이면 저장·삭제·승인까지 모두 막는다(검수 S1). 읽기는 그대로 연다 —
  // 무엇이 편성돼 있는지는 봐야 하고, 눌러 봐야 403을 만나는 일만 없애면 된다.
  //
  // 역할 조건도 함께 얹는다. 목록은 이미 얹고 있었는데 이 창이 빠져 있어 조회자에게
  // 삭제가 눌리고 「삭제 확정」까지 열렸다(QA A1). 서버가 역할을 강제해 실제로 지워지지는
  // 않았지만, 눌리는 버튼은 눌린다는 약속이다.
  const saveGate = useWriteGate().and(canWriteSchedules(scope.actor), SCHEDULE_WRITE_DENIED);
  const formScrollRef = useRef<HTMLDivElement | null>(null);
  const [moreContentBelow, setMoreContentBelow] = useState(false);
  useEffect(() => {
    const node = formScrollRef.current;
    if (!node) return;
    const read = () => setMoreContentBelow(node.scrollHeight - node.clientHeight - node.scrollTop > 2);
    read();
    node.addEventListener('scroll', read, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(read);
    observer?.observe(node);
    if (node.firstElementChild) observer?.observe(node.firstElementChild);
    return () => { node.removeEventListener('scroll', read); observer?.disconnect(); };
  }, []);
  const [name, setName] = useState(initial?.name ?? '');
  const [segments, setSegments] = useState<Segment[]>(() => initial?.blocks?.length
    ? [...initial.blocks].sort((a, b) => a.startMinute - b.startMinute)
      .map(block => ({ key: block.id, playlistId: block.playlistId, startMinute: block.startMinute, endMinute: block.endMinute }))
    : [{ key: newKey(), playlistId: '', startMinute: preset?.startMinute ?? 0, endMinute: preset?.endMinute ?? 1440 }]);
  const [layer, setLayer] = useState<AssignmentLayer>(initial?.layer ?? preset?.layer ?? 'base');
  // 새 편성은 모든 기기가 기본이다. 캘린더 칸을 눌러 들어오면 그 기기만 고른 채로 연다.
  // 편집할 때는 이 편성이 실제로 올라가 있는 기기 전부를 보여 준다 — 목록 자체가 대상이다.
  const [selected, setSelected] = useState<Set<string>>(new Set(initial
    ? (batchDevices.length ? batchDevices : [initial.deviceId])
    : preset?.deviceIds?.length ? preset.deviceIds : devices.map(device => device.id)));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [startDate, setStartDate] = useState(initial?.startDate ?? preset?.date ?? todayISO());
  const [endDate, setEndDate] = useState(initial?.endDate ?? preset?.endDate ?? preset?.date ?? todayISO());
  const [calendar, setCalendar] = useState<AssignmentCalendar>(initial?.calendar === 'lunar' ? 'lunar' : 'solar');
  const [repeatKind, setRepeatKind] = useState<Exclude<AssignmentRepeat['kind'], 'monthly-weekday'>>(
    initial?.repeat.kind === 'monthly-weekday' ? 'monthly' : initial?.repeat.kind ?? 'none');
  const [monthlyMode, setMonthlyMode] = useState<'date' | 'weekday'>(initial?.repeat.kind === 'monthly-weekday' ? 'weekday' : 'date');
  const [weekdays, setWeekdays] = useState<Set<number>>(new Set(
    initial?.repeat.kind === 'weekly' ? initial.repeat.weekdays : [weekdayOf(preset?.date ?? todayISO())]));
  const [repeatEndKind, setRepeatEndKind] = useState<AssignmentRepeatEnd['kind']>(initial?.repeatEnd.kind ?? 'never');
  const [repeatCount, setRepeatCount] = useState(initial?.repeatEnd.kind === 'count' ? String(initial.repeatEnd.count) : '10');
  const [repeatUntil, setRepeatUntil] = useState(initial?.repeatEnd.kind === 'until' ? initial.repeatEnd.date
    : addDays(initial?.endDate ?? preset?.date ?? todayISO(), 30));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  // 미리보기에서 정한 재생 순서. 저장할 때 함께 보낸다.
  const [priorityAt, setPriorityAt] = useState<string | undefined>(initial?.priorityAt);
  const [askCentral, setAskCentral] = useState<{ target: ScheduleAssignment; above: boolean } | null>(null);
  // 되돌리기에 쓸 '고치기 전' 모습. 열 때의 값을 그대로 붙잡아 둔다.
  const batchRows = useRef(initial ? assignments.filter(a => a.batchId === initial.batchId) : []).current;
  const mediaById = new Map(media.map(item => [item.id, item]));
  const noticeById = new Map(notices.map(item => [item.id, item]));

  // 하루 종일 = 00:00~24:00 한 구간. 애플 캘린더의 "하루 종일"과 같은 뜻이다.
  const allDay = segments.length === 1 && segments[0]!.startMinute === 0 && segments[0]!.endMinute === 1440;
  const blocksReady = segments.length > 0 && segments.every(s => !!s.playlistId) && !segmentsOverlap(segments);
  const writableGroups = groups.filter(g => devices.some(d => d.groupId === g.id));
  const ungrouped = devices.filter(d => !d.groupId || !writableGroups.some(g => g.id === d.groupId));
  // 그룹과 미지정 기기를 같은 모양의 구역으로 다룬다.
  const sections = [
    ...writableGroups.map(group => ({ id: group.id, name: group.name, members: devices.filter(d => d.groupId === group.id) })),
    ...(ungrouped.length ? [{ id: '__ungrouped__', name: '그룹 미지정', members: ungrouped }] : []),
  ].filter(section => section.members.length > 0);

  // ── 그룹 경계 ─────────────────────────────────────────────────────────────
  // 대상 기기의 그룹이 쓸 수 없는 플레이리스트는 애초에 고를 수 없어야 한다. 예전에는 고를 수는 있고
  // 저장할 때서야 서버가 거절했다(2026-09-16 대교 보고). 판정은 위 playlistGroupBlock 한 곳에서만 한다.
  // 중앙(조직 Owner·중앙 관리자)은 그룹 격리를 넘어다닌다 — 서버 reachesGroup 과 같은 예외다
  // (2026-09-17 대교). 화면이 서버보다 엄해지면 "왜 못 고르지"가 되고, 그건 저장 실패보다
  // 알아차리기 어렵다.
  const enforceGroups = scope.actor.enforced && !scope.actor.central;
  const groupName = (id: string) => groups.find(g => g.id === id)?.name ?? '조회 불가 그룹';
  const ownerLabel = (id: string | null) => id === null ? '공통' : `'${groupName(id)}' 그룹`;
  const deviceGroupLabel = (id: string | null) => id === null ? '그룹 미지정 기기' : `'${groupName(id)}' 그룹 기기`;
  /** 이 플레이리스트가 '지금 고른 기기'에 못 가는 이유. 갈 수 있으면 null. */
  const playlistBlockReason = (playlistId: string): string | null => {
    if (!enforceGroups || !playlistId) return null;
    const playlist = playlists.find(p => p.id === playlistId);
    for (const groupId of [...new Set(devices.filter(d => selected.has(d.id)).map(d => d.groupId ?? null))]) {
      const block = playlistGroupBlock(playlist, groupId, mediaById, noticeById);
      if (block) return groupBlockReason(block, ownerLabel(block.ownerGroupId), deviceGroupLabel(groupId));
    }
    return null;
  };
  const defaultPlaylistId = playlists.find(item => !item.id.startsWith('cv-') && !playlistBlockReason(item.id))?.id ?? '';
  useEffect(() => {
    if (initial || !defaultPlaylistId) return;
    setSegments(current => current.some(segment => !segment.playlistId)
      ? current.map(segment => segment.playlistId ? segment : { ...segment, playlistId: defaultPlaylistId })
      : current);
  }, [initial, defaultPlaylistId]);
  /** 반대 방향 — 지금 고른 플레이리스트가 이 그룹의 기기로 못 가는 이유. 그룹마다 한 번만 따진다. */
  const deviceBlockCache = new Map<string | null, string | null>();
  const deviceBlockReason = (groupId: string | null): string | null => {
    if (!enforceGroups) return null;
    if (deviceBlockCache.has(groupId)) return deviceBlockCache.get(groupId) ?? null;
    let reason: string | null = null;
    for (const segment of segments) {
      if (!segment.playlistId) continue;
      const block = playlistGroupBlock(playlists.find(p => p.id === segment.playlistId), groupId, mediaById, noticeById);
      if (block) { reason = groupBlockReason(block, ownerLabel(block.ownerGroupId), deviceGroupLabel(groupId)); break; }
    }
    deviceBlockCache.set(groupId, reason);
    return reason;
  };
  // 이미 골라 둔 조합 중 어긋난 것. 고르고 나서 기기를 더 담으면 여기에 걸린다 —
  // 고른 것을 몰래 빼지 않고(그러면 무엇이 사라졌는지 모른다) 이유를 적고 저장만 막는다.
  const groupMismatch = devices.filter(device => selected.has(device.id))
    .map(device => deviceBlockReason(device.groupId ?? null)).find(reason => !!reason) ?? '';

  const toggleDevice = (id: string) => setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const toggleMany = (ids: string[]) => {
    setSelected(prev => {
      const next = new Set(prev);
      const all = ids.every(id => next.has(id));
      for (const id of ids) { if (all) next.delete(id); else next.add(id); }
      return next;
    });
  };
  // 무엇이 대상인지 한 줄로 알려준다 — 목록을 펼치지 않아도 알 수 있어야 한다.
  const targetSummary = !selected.size ? '선택된 기기 없음'
    : selected.size === devices.length ? `모든 기기 ${devices.length}대`
      : devices.filter(device => selected.has(device.id)).map(device => device.name).slice(0, 3).join(', ')
        + (selected.size > 3 ? ` 외 ${selected.size - 3}대` : '');

  const repeat: AssignmentRepeat = repeatKind === 'weekly'
    ? { kind: 'weekly', weekdays: [...weekdays].sort() }
    : { kind: repeatKind === 'monthly' && monthlyMode === 'weekday' ? 'monthly-weekday' : repeatKind };
  const monthlyDay = calendar === 'lunar' ? solarToLunar(startDate)?.day ?? 1 : Number(startDate.slice(8));
  const monthlyNth = nthWeekdayOfMonth(startDate);
  const moveStart = (next: string) => { setStartDate(next); setEndDate(next); };
  const repeats = repeatKind !== 'none';
  const repeatEnd: AssignmentRepeatEnd = !repeats ? { kind: 'never' }
    : repeatEndKind === 'count' ? { kind: 'count', count: Number(repeatCount) }
      : repeatEndKind === 'until' ? { kind: 'until', date: repeatUntil } : { kind: 'never' };
  const rule: AssignmentRule = { calendar, startDate, endDate, repeat, repeatEnd,
    ...(initial?.skipDates?.length ? { skipDates: initial.skipDates } : {}) };
  const ruleOk = isValidAssignmentRule(rule);
  // 승인 방식과 별개인 권한 서열 — 상위 권한자가 올린 스케줄은 보기만 가능하다(서버 assertModifiable과 같은 규칙).
  const protectedSchedule = !!initial && !canModifyAssignment(scope.actor, initial);
  // 이름은 거들 뿐이다 — 비워 두면 애플 캘린더처럼 기본 이름으로 등록된다.
  const finalName = name.trim() || DEFAULT_SCHEDULE_NAME;
  // 바뀐 것이 없으면 저장할 것도 없다 — 순서에 좌우되지 않도록 같은 모양으로 맞춰 비교한다.
  const signature = (value: { name: string; layer: AssignmentLayer; deviceIds: string[]; calendar: AssignmentCalendar;
    blocks: { playlistId: string; startMinute: number; endMinute: number }[]; startDate: string; endDate: string;
    repeat: AssignmentRepeat; repeatEnd: AssignmentRepeatEnd; skipDates: string[] }) => JSON.stringify([
      value.name, value.layer, value.calendar, value.startDate, value.endDate,
      [...value.deviceIds].sort(),
      [...value.blocks].sort((a, b) => a.startMinute - b.startMinute)
        .map(block => [block.playlistId, block.startMinute, block.endMinute]),
      [value.repeat.kind, value.repeat.kind === 'weekly' ? [...value.repeat.weekdays].sort((a, b) => a - b) : null],
      [value.repeatEnd.kind, value.repeatEnd.kind === 'count' ? value.repeatEnd.count
        : value.repeatEnd.kind === 'until' ? value.repeatEnd.date : null],
      [...value.skipDates].sort(),
    ]);
  const changed = !initial || priorityAt !== initial.priorityAt
    || signature({ name: finalName, layer, deviceIds: [...selected], calendar,
    blocks: segments, startDate, endDate, repeat, repeatEnd, skipDates: initial.skipDates ?? [] })
    !== signature({ name: initial.name, layer: initial.layer, calendar: initial.calendar ?? 'solar',
      deviceIds: batchDevices.length ? batchDevices : [initial.deviceId], blocks: initial.blocks,
      startDate: initial.startDate, endDate: initial.endDate, repeat: initial.repeat, repeatEnd: initial.repeatEnd,
      skipDates: initial.skipDates ?? [] });
  const valid = !protectedSchedule && blocksReady && ruleOk && selected.size > 0 && changed && !groupMismatch;
  // 디바이스 목록이 곧 대상이므로 저장·삭제는 언제나 이 편성 전체를 다룬다.
  const scopeQuery = initial ? '?scope=batch' : '';
  const previewBody = JSON.stringify({ name: finalName, blocks: segments.map(s => ({ playlistId: s.playlistId,
    startMinute: s.startMinute, endMinute: s.endMinute })), deviceIds: [...selected], layer, startDate, endDate,
    repeat, repeatEnd, calendar, skipDates: initial?.skipDates ?? [] });
  const previewKey = `${initial?.id ?? ''}:${previewBody}`;
  const [preflight, setPreflight] = useState<{ key: string; conflicts: { deviceId: string; reason?: string }[]; error?: string } | null>(null);
  const needsCheck = !saveGate.blocked && !scope.actor.central && !protectedSchedule && ruleOk && segments.length > 0 && !segmentsOverlap(segments) && selected.size > 0;
  useEffect(() => {
    if (!needsCheck) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void workflowJson<{ deviceId: string; reason?: string }[]>(`schedule-assignments/preview${initial ? `?id=${encodeURIComponent(initial.id)}` : ''}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: previewBody, signal: controller.signal,
      }).then(conflicts => { if (!controller.signal.aborted) setPreflight({ key: previewKey, conflicts }); })
        .catch(e => { if (!controller.signal.aborted) setPreflight({ key: previewKey, conflicts: [], error: describeApiError(e) }); });
    }, 200);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [previewKey, needsCheck]);
  const checking = needsCheck && preflight?.key !== previewKey;
  const previewError = needsCheck && preflight?.key === previewKey ? preflight.error : undefined;
  const needsApproval = needsCheck && preflight?.key === previewKey && preflight.conflicts.length > 0;

  // 기기 한 대가 같은 시각에 틀 수 있는 화면은 하나다. 저장 전에 겹치는 날을 알려준다.
  // 레이어가 달라도 겹친다 — 이벤트가 기본을 덮으므로 같은 기기의 모든 편성을 본다.
  const conflicts = !ruleOk ? [] : assignments
    .filter(other => selected.has(other.deviceId)
      && other.id !== initial?.id && other.batchId !== initial?.batchId)
    .filter(other => other.blocks.some(b => segments.some(s => b.startMinute < s.endMinute && s.startMinute < b.endMinute)))
    .map(other => ({ other, dates: overlappingDates(rule, other) }))
    .filter(hit => hit.dates.length > 0)
    .sort((a, b) => a.dates[0]!.localeCompare(b.dates[0]!));
  const overlapDays = conflicts.reduce((total, hit) => total + hit.dates.length, 0);
  const conflictSummary = conflicts.length === 0 ? '' : (() => {
    const [first] = conflicts;
    const days = conflicts.reduce((total, hit) => total + hit.dates.length, 0);
    const names = [...new Set(conflicts.map(hit => hit.other.name))];
    return `${first!.dates[0]} 등 ${days}일이 기존 편성 '${names[0]}'${names.length > 1 ? ` 외 ${names.length - 1}건` : ''}과 겹칩니다.`;
  })();

  // 연 기기를 그대로 보여 준다 — 다른 기기를 보여 주면 방금 누른 편성이 어디 있는지 알 수 없다.
  const previewDeviceId = initial?.deviceId ?? [...selected][0] ?? '';
  // 눌러서 연 그 날을 그대로 본다. 새로 만들 때는 부딪히는 첫날이 가장 알고 싶은 날이다.
  const previewDate = occurrenceDate && assignmentOccursOn(rule, occurrenceDate) ? occurrenceDate
    : conflicts.find(hit => hit.other.deviceId === previewDeviceId)?.dates[0] ?? startDate;
  const previewDevice = devices.find(d => d.id === previewDeviceId);
  const draft: ScheduleAssignment = {
    id: initial?.id ?? '__draft__', batchId: initial?.batchId ?? '', deviceId: previewDeviceId,
    groupId: initial?.groupId ?? null, layer, templateId: '', name: finalName,
    blocks: [...segments].sort((a, b) => a.startMinute - b.startMinute).map((s, index) => ({
      id: `draft-${index}`, scheduleTypeId: '', playlistId: s.playlistId,
      startMinute: s.startMinute, endMinute: s.endMinute, color: '', label: '',
      ownerUserId: null, createdAt: '', updatedAt: '' })),
    startDate, endDate, repeat, repeatEnd, calendar,
    ...(initial?.skipDates?.length ? { skipDates: initial.skipDates } : {}),
    // 색은 편성표에 지금 보이는 그대로 — 편집한다고 색이 바뀌어 보이면 어느 것인지 헷갈린다.
    ...((initial ? initial.central : scope.actor.central) ? { central: true } : {}),
    ...(initial?.approval ? { approval: initial.approval } : {}),
    ...(priorityAt ? { priorityAt } : {}),
    revision: initial?.revision ?? 0,
    createdAt: initial?.createdAt ?? new Date().toISOString(), updatedAt: '' };
  const previewSiblings = assignments.filter(other => other.deviceId === previewDeviceId
    && other.id !== initial?.id && isAssignmentPlayable(other) && assignmentOccursOn(other, previewDate));
  const showPreview = ruleOk && !!previewDevice && segments.length > 0 && assignmentOccursOn(draft, previewDate);
  const changePriority = (target: ScheduleAssignment, above: boolean) => {
    if (protectedSchedule || saveGate.blocked) return;
    if (above && target.central && !scope.actor.central) { setAskCentral({ target, above }); return; }
    const next = reorderedPriority([...previewSiblings, draft], target.id, above);
    if (next) setPriorityAt(next);
  };

  const save = async () => {
    if (!valid || busy || checking || previewError) return;
    setBusy(true); setError('');
    try {
      // 수정 저장에는 제외 날짜를 싣지 않는다 — 기기마다 다른 제외 날짜는 서버가 행별로 지킨다.
      const body = { name: finalName, blocks: [...segments].sort((a, b) => a.startMinute - b.startMinute)
          .map(s => ({ playlistId: s.playlistId, startMinute: s.startMinute, endMinute: s.endMinute })),
        deviceIds: [...selected], layer, startDate, endDate, repeat, repeatEnd,
        calendar, ...(initial ? {} : { skipDates: [] }), priorityAt: priorityAt ?? null };
      const saved = await workflowJson<ScheduleAssignment[]>(initial ? `schedule-assignments/${encodeURIComponent(initial.id)}${scopeQuery}` : 'schedule-assignments',
        { method: initial ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      toast(saved.some(a => a.approval?.status === 'pending')
        ? (initial ? '수정 승인 요청을 보냈습니다. 승인 전까지 기존 스케줄이 그대로 재생됩니다.' : '승인 요청을 보냈습니다. 승인 전에는 재생되지 않습니다.')
        : '저장되었습니다.', 'success');
      const powerWarning = powerConflictMessage(saved, devices, undefined, holidays);
      if (powerWarning) toast(powerWarning, 'error');
      // 복제본(수정 요청)으로 저장된 경우 되돌리기 단계는 남기지 않는다 — 요청은 캘린더에서 지워(철회) 취소한다.
      const proposed = saved.some(a => a.approval?.status === 'pending' && a.approval.replacesId);
      onSaved(proposed ? undefined : initial ? { kind: 'update', before: batchRows, after: saved } : { kind: 'create', after: saved });
    } catch (e) { setError(describeApiError(e)); setBusy(false); }
  };
  /** 승인·반려는 중앙 관리자만 한다. 처리하면 캘린더 표시와 재생 여부가 함께 바뀐다. */
  const decide = async (status: 'approved' | 'rejected') => {
    if (!initial || busy) return;
    if (status === 'rejected' && !approvalNote.trim()) { setError('반려 사유를 입력하세요.'); return; }
    setBusy(true); setError('');
    try {
      await workflowJson(`schedule-assignments/${encodeURIComponent(initial.id)}/approval`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status, revision: initial.revision, ...(approvalNote.trim() ? { note: approvalNote.trim() } : {}) }) });
      toast(status === 'approved' ? '편성을 승인했습니다.' : '편성을 반려했습니다.', 'success'); onSaved();
    } catch (e) { setError(describeApiError(e)); setBusy(false); }
  };
  const remove = async () => {
    if (!initial || busy) return;
    setBusy(true); setError('');
    try {
      const result = await workflowJson<{ removed: number; requested: number; cancelled?: number }>(`schedule-assignments/${encodeURIComponent(initial.id)}${scopeQuery}`, { method: 'DELETE' });
      // 대기 중인 요청은 지워지지 않고 '요청 취소됨'으로 남는다(SCDS-87). 되돌리기 기록 대상이 아니다.
      toast(result.cancelled ? '승인 요청을 취소했습니다. 승인 관리의 \'요청 취소됨\'에서 볼 수 있습니다.'
        : result.requested ? '삭제 승인 요청을 보냈습니다. 승인 전까지 기존 스케줄이 그대로 재생됩니다.' : '편성을 삭제했습니다.', 'success');
      onSaved(result.requested || result.cancelled ? undefined : { kind: 'delete', before: batchRows });
    } catch (e) { setError(describeApiError(e)); setBusy(false); }
  };
  /**
   * 애플 캘린더의 "이 이벤트만 삭제" — 회차는 그대로 두고 그 하루만 건너뛴다.
   * 'one'은 이 기기(캘린더에서 누른 칸의 기기)에서만, 'batch'는 함께 올린 모든 기기에서 그 날짜를 뺀다.
   * 그 날짜를 빼서 남는 회차가 없는 기기는 서버가 행을 지운다.
   */
  const skipOccurrence = async (skipScope: 'one' | 'batch') => {
    if (!initial || !occurrenceDate || busy) return;
    setBusy(true); setError('');
    try {
      const result = await workflowJson<{ saved: ScheduleAssignment[]; removed: number; requested: number }>(
        `schedule-assignments/${encodeURIComponent(initial.id)}/skip?scope=${skipScope}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ date: occurrenceDate }) });
      const touched = skipScope === 'one' ? batchRows.filter(a => a.id === initial.id) : batchRows;
      toast(result.requested ? '삭제 승인 요청을 보냈습니다. 승인 전까지 기존 스케줄이 그대로 재생됩니다.'
        : `${skipScope === 'one' ? deviceName(initial.deviceId) + '의 ' : ''}${occurrenceDate} 편성을 제외했습니다.`, 'success');
      // 승인 요청은 되돌리기 단계로 남기지 않는다. 남는 행이 없어 지워진 기기는 삭제로 되돌린다.
      onSaved(result.requested ? undefined
        : result.saved.length ? { kind: 'update', before: touched, after: result.saved } : { kind: 'delete', before: touched });
    } catch (e) { setError(describeApiError(e)); setBusy(false); }
  };
  const deviceName = (id: string) => devices.find(device => device.id === id)?.name ?? '이 기기';

  const fieldLabel = 'font-mono text-[12px] font-medium uppercase tracking-monoWide text-text-dim';
  // 경고는 안내 문구 자리를 대체한다 — 줄이 늘어나면 팝업 높이가 흔들린다.
  const repeatWarning = repeats && repeatEndKind === 'count' && !(Number.isInteger(Number(repeatCount)) && Number(repeatCount) >= 1 && Number(repeatCount) <= MAX_REPEAT_COUNT)
    ? `반복 횟수는 1회부터 ${MAX_REPEAT_COUNT}회까지 지정할 수 있습니다.`
    : repeats && repeatEndKind === 'until' && repeatUntil < startDate ? '반복 종료 날짜는 시작일과 같거나 나중이어야 합니다.'
      : calendar === 'lunar' && solarToLunar(startDate) === null ? '음력으로 바꿀 수 있는 시작일을 선택하세요.' : '';
  const lastDateLabel = ruleOk ? assignmentLastDate(rule) : null;
  // 하루짜리 단발 편성은 고를 것이 없으므로 곧바로 삭제한다.
  const multiDay = !!initial && (initial.repeat.kind !== 'none' || initial.startDate !== initial.endDate);

  // 창 크기는 무엇을 고르든 그대로다 — 내용이 늘면 안에서 스크롤한다.
  // 제출·삭제 줄은 ModalShell의 고정 푸터로 넘긴다 — 본문이 길어도 화면 밖으로 밀리지 않는다(검수 S3·S9).
  return <ModalShell title={initial ? '스케줄 정보' : '스케줄 추가'}
    bodyClassName="!overflow-hidden !gap-0 !pb-0 relative"
    panelClassName="flex h-[min(800px,100vh-64px)] w-[920px] flex-col" onClose={() => { if (!busy) onClose(); }}
    footer={<>
      <span className="mr-auto">{protectedSchedule ? <span className="text-sm text-text-dim">{roleLabel(assignmentCreatorRole(initial!))}가 올린 스케줄입니다. 보기만 가능합니다.</span>
        // 대기 중인 요청은 '삭제'가 아니라 '승인 요청 취소'다 — 지우면 흔적 없이 사라지는 줄 알았다(SCDS-87).
        // 거둬들이는 것은 요청한 사람뿐이다. 승인자·다른 사람에게는 버튼이 없다 — 승인자는 본문의 승인·반려로 처리한다(SCDS-98).
        : initial?.approval?.status === 'pending' ? (!isOwnPendingRequest(scope, initial) ? null : confirmDelete
          ? <span className="flex flex-wrap items-center gap-2 text-[13px]">
            <Button variant="danger" {...saveGate.props} disabled={busy || saveGate.blocked} onClick={() => void remove()}>
              {batchSize > 1 ? `${batchSize}대 요청 취소 확정` : '요청 취소 확정'}</Button>
            <Button onClick={() => setConfirmDelete(false)}>그대로 두기</Button></span>
          : <Button variant="danger" {...saveGate.props} disabled={busy || saveGate.blocked}
            title="승인 전에 요청을 거둬들입니다. 승인 관리의 '요청 취소됨'에 남습니다."
            onClick={() => setConfirmDelete(true)}>승인 요청 취소</Button>)
        : initial && (confirmDelete
        // 버튼 문구가 곧 확인 문구다 — 무엇이 지워지는지 버튼에 적혀 있으면 안내문은 군더더기다.
        ? <span className="flex flex-wrap items-center gap-2 text-[13px]">
          {/* 날짜 하나만: 이 기기에서만 / 함께 올린 모든 기기에서. 한 대짜리면 고를 것이 없으니 하나만 둔다. */}
          {multiDay && occurrenceDate && <Button variant="danger" {...saveGate.props} disabled={busy || saveGate.blocked}
            onClick={() => void skipOccurrence('one')}>{batchSize > 1 ? `이 기기의 ${occurrenceDate}만 삭제` : `${occurrenceDate}만 삭제`}</Button>}
          {multiDay && occurrenceDate && batchSize > 1 && <Button variant="danger" {...saveGate.props} disabled={busy || saveGate.blocked}
            onClick={() => void skipOccurrence('batch')}>모든 기기의 {occurrenceDate}만 삭제</Button>}
          <Button variant="danger" {...saveGate.props} disabled={busy || saveGate.blocked} onClick={() => void remove()}>
            {multiDay ? '반복 전체 삭제' : batchSize > 1 ? `${batchSize}대 삭제` : '삭제 확정'}</Button>
          {/* 푸터 오른쪽에도 「취소」(편집 취소)가 있다 — 무엇을 취소하는지 이름으로 갈라 둔다. */}
          <Button onClick={() => setConfirmDelete(false)}>삭제 취소</Button></span>
        : <Button variant="danger" {...saveGate.props} disabled={busy || saveGate.blocked}
          onClick={() => setConfirmDelete(true)}>삭제</Button>)}</span>
      {/* 셸 머리의 ✕가 이미 「닫기」다 — 같은 이름을 하나 더 두면 무엇을 누르라는지 말로 가리킬 수 없다. */}
      <Button disabled={busy} onClick={onClose}>취소</Button>
      <Button variant="primary" {...saveGate.props}
        disabled={!valid || busy || checking || !!previewError || saveGate.blocked}
        onClick={() => void save()}>{checking ? '확인 중' : needsApproval ? (initial && initial.approval?.status !== 'pending' ? '수정 승인 요청' : '승인 요청') : initial ? '저장' : '추가'}</Button>
    </>}>
    <div ref={formScrollRef} aria-label="스케줄 상세 내용" className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
    <div className="flex flex-col gap-3 pb-5 pr-1">
    <fieldset disabled={!!protectedSchedule || saveGate.blocked} className="m-0 min-w-0 shrink-0 border-0 p-0"
      onPointerDownCapture={event => { if (protectedSchedule || saveGate.blocked) { event.preventDefault(); event.stopPropagation(); } }}
      onClickCapture={event => { if (protectedSchedule || saveGate.blocked) { event.preventDefault(); event.stopPropagation(); } }}>
      <div className="flex flex-col gap-3 pr-1">
      {/* 이름과 레이어 — 애플 캘린더의 제목·캘린더 선택 자리다. */}
      <div className="flex items-center gap-2">
        {/* 열자마자 제목부터 쓸 수 있어야 한다 — 애플 캘린더와 같은 시작점이다. */}
        <input aria-label="스케줄 이름" value={name} onChange={event => setName(event.target.value)} maxLength={120}
          autoFocus={!protectedSchedule} placeholder={DEFAULT_SCHEDULE_NAME}
          className="min-w-0 flex-1 rounded border border-line bg-panel-2 p-2 text-[15px] font-medium" />
        <Segmented value={layer} onChange={setLayer} options={[{ value: 'base', label: '기본' }, { value: 'event', label: '이벤트' }]} />
      </div>
      {/* 하루 24시간 안에서 무엇을 언제 트는지 — "위치 추가" 자리에 타임라인이 온다. */}
      <div className="flex flex-col gap-1.5">
        <span className={fieldLabel}>플레이리스트</span>
        <BlockTimelineEditor segments={segments} playlists={playlists} blockReason={playlistBlockReason}
          sections={groupSections(playlists, groups.filter(g => scope.actor.central || scope.actor.groups.some(x => x.groupId === g.id)), scope.actor.central)}
          mediaById={mediaById} noticeById={noticeById}
          layer={layer} onChange={setSegments} />
        {/* 경고는 안내 문구 자리를 대체한다 — 줄이 늘어나면 팝업 높이가 흔들린다.
            플레이리스트 미선택은 드롭다운이 직접 안내하므로 여기서 또 말하지 않는다. */}
        {groupMismatch
          // 그룹이 어긋난 조합은 서버가 어차피 거절한다. 무엇이 왜 안 되는지 여기서 먼저 말한다.
          ? <p role="alert" className="text-[12px] text-red">{groupMismatch}</p>
          : blocksReady || (segments.length > 0 && !segmentsOverlap(segments))
          ? <p className="text-[12px] text-text-dim">{allDay ? '하루 종일 이 구성으로 재생됩니다.'
            : `${segments.length}개 구간 · 하루 중 ${Math.round(segments.reduce((total, s) => total + (s.endMinute - s.startMinute), 0) / 60 * 10) / 10}시간 재생됩니다.`}</p>
          : <p role="alert" className="text-[12px] text-red">
            {!segments.length ? '재생할 플레이리스트를 하나 이상 추가하세요.'
              : '구간이 서로 겹칩니다. 시간을 조정하세요.'}</p>}
      </div>
      {/* 하루 종일 · 대상 · 반복 — 애플 캘린더의 설정 묶음처럼 한 상자에 담는다. */}
      <div className="flex shrink-0 flex-col gap-4 border-t border-line pt-3" aria-label="반복 설정">
      {pickerOpen && <ModalShell title="대상 기기 선택" panelClassName="flex w-[560px] flex-col h-[min(80vh,34rem)]"
        onClose={() => setPickerOpen(false)}
        footer={<><span className="mr-auto text-[13px] text-text-dim">{targetSummary}</span>
          <Button variant="primary" onClick={() => setPickerOpen(false)}>선택 완료</Button></>}>
        <div className="flex min-h-0 flex-1 flex-col gap-3">
        {/* 그룹은 체크박스(한 번에 켜고 끄기), 기기는 칩(개별 선택) — 두 층위를 다른 컨트롤로 구분한다. */}
        <div className="min-h-0 flex-1 overflow-auto rounded border border-line">
          {!sections.length && <p className="px-2 py-3 text-[13px] text-text-dim">편성할 수 있는 기기가 없습니다.</p>}
          {/* 고른 플레이리스트를 못 받는 기기는 목록에서 빼지 않는다 — 자리는 그대로 두고 비활성 + 이유.
              「모든 기기」·그룹 체크박스는 켤 수 있는 기기만 켠다(꺼져 있는 것을 억지로 켜지 않는다). */}
          {/* 모든 기기 — 그룹을 하나씩 켜지 않아도 되게 맨 위에 둔다. */}
          {sections.length > 0 && (() => {
            const every = sections.flatMap(section => section.members.map(d => d.id));
            const usable = sections.flatMap(section => section.members.filter(d => !deviceBlockReason(d.groupId ?? null)).map(d => d.id));
            const picked = every.filter(id => selected.has(id)).length;
            const all = usable.length > 0 && usable.every(id => selected.has(id));
            return <label className="sticky top-0 z-10 flex items-center gap-2 border-b border-line bg-panel px-2 py-1.5 text-[13px] font-semibold">
              <input type="checkbox" aria-label="모든 기기" checked={all} disabled={!usable.length}
                title={usable.length ? undefined : '고른 플레이리스트를 받을 수 있는 기기가 없습니다.'}
                ref={node => { if (node) node.indeterminate = picked > 0 && !all; }}
                onChange={() => toggleMany(usable)} />
              <span>모든 기기</span>
              <span className={`ml-auto shrink-0 rounded px-1.5 py-0.5 font-mono text-[11px] font-normal ${picked
                ? 'bg-accent-soft text-accent' : 'text-text-dim'}`}>{picked}/{every.length}대</span>
            </label>;
          })()}
          {sections.map(section => {
            const picked = section.members.filter(d => selected.has(d.id)).length;
            const usable = section.members.filter(d => !deviceBlockReason(d.groupId ?? null));
            const all = usable.length > 0 && usable.every(d => selected.has(d.id));
            // 한 구역의 기기는 보통 같은 그룹이라 이유도 하나다. 첫 이유를 구역 전체의 설명으로 쓴다.
            const sectionReason = section.members.map(d => deviceBlockReason(d.groupId ?? null)).find(reason => !!reason) ?? undefined;
            return <section key={section.id} className="border-b border-line last:border-b-0">
              <label className="flex items-center gap-2 bg-panel-2 px-2 py-1.5 text-[13px] font-semibold">
                <input type="checkbox" aria-label={`${section.name} 전체`} checked={all} disabled={!usable.length}
                  title={usable.length ? undefined : sectionReason}
                  ref={node => { if (node) node.indeterminate = picked > 0 && !all; }}
                  onChange={() => toggleMany(usable.map(d => d.id))} />
                <span className="truncate">{section.name}</span>
                <span className={`ml-auto shrink-0 rounded px-1.5 py-0.5 font-mono text-[11px] font-normal ${picked
                  ? 'bg-accent-soft text-accent' : 'text-text-dim'}`}>{picked}/{section.members.length}대</span>
              </label>
              {/* 이유는 구역 머리에 한 번만 적는다 — 칩마다 반복하면 목록이 읽히지 않는다. */}
              {!usable.length && sectionReason && <p className="px-2 pb-1 text-[11px] text-red">{sectionReason}</p>}
              <div className="flex flex-wrap gap-1 px-2 py-1.5">{section.members.map(d => {
                // Chip은 disabled를 받지 않는다(components/ui.tsx). 자리를 지킨 채 눌리지 않게 하고,
                // 이유는 감싼 span의 title로 붙인다 — 목록에서 빼면 "왜 안 보이지"가 된다.
                const reason = deviceBlockReason(d.groupId ?? null);
                return <span key={d.id} title={reason ?? undefined} aria-disabled={reason ? true : undefined}
                  className={reason ? 'cursor-not-allowed' : undefined}>
                  <Chip active={selected.has(d.id)} onClick={() => { if (!reason) toggleDevice(d.id); }}
                    className={`!py-0.5 text-[12px] ${reason ? 'pointer-events-none opacity-40' : ''}`}>{d.name}</Chip>
                </span>; })}</div>
            </section>;
          })}
        </div>
        </div>
      </ModalShell>}
      {/* 반복을 먼저 정하고, 그 반복이 필요한 것만 아래에서 묻는다. */}
      <div className="flex flex-col items-start gap-3"><span className={fieldLabel}>반복</span>
        <Segmented value={repeatKind} onChange={setRepeatKind} options={[
          { value: 'none', label: '안 함' }, { value: 'weekly', label: '매주' }, { value: 'monthly', label: '매월' },
          { value: 'yearly', label: '매년' }]} />
      </div>
      {/* 반복 종류에 따라 줄 수가 달라도 높이는 고정한다 — 창이 꿈틀거리면 안 된다. */}
      <SmoothHeight><div data-repeat-detail className="flex flex-col gap-3 text-[13px]">
      {/* 매년 반복은 기준 달력이 핵심이다 — 달력 팝업 안에만 두면 찾을 수 없었다(SCDS-91).
          설·추석처럼 음력으로 해마다 날짜가 바뀌는 행사를 여기서 바로 고른다. 저장은 늘 양력 ISO. */}
      {repeatKind === 'yearly' && <div role="group" aria-label="날짜 기준" className="flex flex-wrap items-center gap-2">
        <span className="shrink-0 text-text-dim">날짜 기준</span>
        <Segmented value={calendar} onChange={setCalendar}
          options={[{ value: 'solar', label: '양력' }, { value: 'lunar', label: '음력' }]} />
        <span className="text-[12px] text-text-dim">{calendar === 'lunar'
          ? `매년 음력 ${lunarDateLabel(startDate)?.replace('음력 ', '') ?? ''}에 맞춰 날짜가 바뀝니다.`
          : '매년 같은 양력 날짜에 편성됩니다.'}</span>
      </div>}
      {(repeatKind === 'none' || repeatKind === 'yearly') && <div className="flex flex-wrap items-center gap-3">
        <CalendarField label="시작일" value={startDate} calendar={calendar}
          onChange={(next, basis) => { setCalendar(basis); setStartDate(next); if (next > endDate) setEndDate(next); }} />
        <CalendarField label="종료일" value={endDate} calendar={calendar}
          onChange={(next, basis) => { setCalendar(basis); setEndDate(next); }} />
        {/* 경고는 안내 문구 자리를 대체한다 — 줄이 늘어나면 팝업 높이가 흔들린다. */}
        {isValidAssignmentRule({ ...rule, repeat: { kind: 'none' }, repeatEnd: { kind: 'never' } })
          ? <span className="text-[12px] text-text-dim">{startDate === endDate
            ? (repeatKind === 'none' ? '하루만 편성됩니다.' : '매년 이 하루에 편성됩니다.')
            : (repeatKind === 'none' ? '시작일부터 종료일까지 편성됩니다.' : '매년 이 기간에 편성됩니다.')}</span>
          : <span role="alert" className="text-[12px] text-red">종료일은 시작일과 같거나 나중이어야 합니다.</span>}
      </div>}
      {repeatKind === 'weekly' && <>
        <div className="flex flex-wrap items-center gap-2">
          <span className="shrink-0 text-text-dim">반복할 요일</span>
          <div role="group" aria-label="요일 선택" className="flex flex-wrap gap-1">
            {[1, 2, 3, 4, 5, 6, 0].map(day => <Chip key={day} active={weekdays.has(day)}
              onClick={() => setWeekdays(prev => { const next = new Set(prev); if (next.has(day)) next.delete(day); else next.add(day); return next; })}>
              {DAY_LABELS[day]}</Chip>)}
          </div>
        </div>
        <StartsOn label="시작" value={startDate} calendar={calendar}
          onChange={(next, basis) => { setCalendar(basis); setStartDate(next); setEndDate(next); }} />
      </>}
      {repeatKind === 'monthly' && <>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented value={monthlyMode} onChange={setMonthlyMode} options={[
            { value: 'date', label: calendar === 'lunar' ? '음력 날짜로' : '날짜로' },
            { value: 'weekday', label: '요일로' }]} />
          {monthlyMode === 'date'
            ? <label className="flex items-center gap-1.5">매월{calendar === 'lunar' ? ' 음력' : ''}
              <select aria-label="반복할 날짜" value={monthlyDay} className="rounded border border-line bg-panel-2 p-1.5"
                onChange={event => moveStart(nextMatchingDate(startDate, date => calendar === 'lunar'
                  ? solarToLunar(date)?.day === Number(event.target.value) : Number(date.slice(8)) === Number(event.target.value)))}>
                {Array.from({ length: calendar === 'lunar' ? 30 : 31 }, (_, index) => index + 1)
                  .map(day => <option key={day} value={day}>{day}일</option>)}
              </select></label>
            : <label className="flex items-center gap-1.5">매월
              <select aria-label="반복할 주" value={monthlyNth} className="rounded border border-line bg-panel-2 p-1.5"
                onChange={event => moveStart(nextNthWeekday(startDate, Number(event.target.value), weekdayOf(startDate)))}>
                {['첫째', '둘째', '셋째', '넷째', '다섯째'].map((name, index) =>
                  <option key={name} value={index + 1}>{name} 주</option>)}
              </select>
              <select aria-label="반복할 요일" value={weekdayOf(startDate)} className="rounded border border-line bg-panel-2 p-1.5"
                onChange={event => moveStart(nextNthWeekday(startDate, monthlyNth, Number(event.target.value)))}>
                {[0, 1, 2, 3, 4, 5, 6].map(day => <option key={day} value={day}>{DAY_LABELS[day]}요일</option>)}
              </select></label>}
        </div>
        <StartsOn label="시작" value={startDate} calendar={calendar}
          onChange={(next, basis) => { setCalendar(basis); setStartDate(next); setEndDate(next); }} />
      </>}
      {/* 반복 종료는 반복할 때만 의미가 있다. */}
      {repeats && <div className="flex flex-wrap items-center gap-2">
        <span className="shrink-0 text-text-dim">반복 종료</span>
        <Segmented value={repeatEndKind} onChange={setRepeatEndKind} options={[
            { value: 'never', label: '안 함' }, { value: 'count', label: '횟수' }, { value: 'until', label: '날짜' }]} />
          {/* 횟수와 날짜는 서로 배타적이다. 같은 자리에서 교체되므로 줄 수는 그대로다. */}
          {repeatEndKind === 'count' && <label className="flex items-center gap-1.5">
            <input aria-label="반복 횟수" type="number" min={1} max={MAX_REPEAT_COUNT} value={repeatCount}
              onChange={e => setRepeatCount(e.target.value)} className="w-20 rounded border border-line bg-panel-2 p-1.5" />회
          </label>}
          {repeatEndKind === 'until' && <label className="flex items-center gap-1.5">
            <input aria-label="반복 종료 날짜" type="date" value={repeatUntil}
              onChange={e => setRepeatUntil(e.target.value)} className="rounded border border-line bg-panel-2 p-1.5" />까지
          </label>}
      </div>}
      {/* 요약은 한 줄뿐이고, 경고가 있으면 그 자리를 대신한다. */}
      {repeatWarning
        ? <p role="alert" className="text-[12px] text-red">{repeatWarning}</p>
        : <p className="text-[12px] text-text-dim">{describeAssignmentRule(rule)}{repeats && ruleOk && lastDateLabel ? ` · 마지막 ${lastDateLabel}` : ''}</p>}
      </div></SmoothHeight>
      </div>
      <div className="flex shrink-0 flex-col gap-3 border-t border-line pt-3" aria-label="대상 기기 설정">
        <div className="flex items-center justify-between">
          <span className={fieldLabel}>디바이스</span>
        </div>
        <div className="flex flex-wrap items-center gap-2" aria-label="선택된 기기">
          <Button aria-label="기기 선택" title="디바이스 추가" className={iconButton} onClick={() => setPickerOpen(true)}><PlusIcon /></Button>
          {/* 고른 뒤에 어긋나게 된 기기(플레이리스트를 바꿨거나 기기를 더 담은 경우)는 몰래 빼지 않고
              같은 자리에 빨갛게 남겨 둔다 — 무엇을 빼야 저장되는지 보여야 한다. */}
          {devices.filter(device => selected.has(device.id)).map(device => {
            const reason = deviceBlockReason(device.groupId ?? null);
            return <span key={device.id} title={reason ?? undefined}
            className={`inline-flex items-center gap-1 rounded-full py-1 pl-3 pr-1.5 text-sm ${reason
              ? 'bg-red/10 text-red ring-1 ring-red/40' : 'bg-accent-soft text-accent'}`}>
            {reason && <span aria-hidden>⚠</span>}{device.name}
            <button type="button" aria-label={`${device.name} 선택 제거`} onClick={() => toggleDevice(device.id)}
              className={`flex h-5 w-5 items-center justify-center rounded-full ${reason ? 'hover:bg-red/15' : 'hover:bg-accent/15'}`}>×</button>
          </span>; })}
          {!selected.size && <p className="text-sm text-text-dim">선택된 기기가 없습니다.</p>}
        </div>
      </div>
      {error && <p role="alert" className="text-[13px] text-red">{error}</p>}
      {/* 중앙 관리자 편성보다 먼저 재생하려면 승인을 받아야 한다 — 순서를 올리기 전에 묻는다. */}
      {askCentral && <ModalShell title="승인 요청" onClose={() => setAskCentral(null)} size="sm"
        footer={<>
          {/* 이 모달을 여는 스케줄 창에도 「취소」가 있다 — 여기서는 무엇을 두는지로 이름을 붙인다. */}
          <Button onClick={() => setAskCentral(null)}>그대로 두기</Button>
          <Button variant="primary" onClick={() => {
            const next = reorderedPriority([...previewSiblings, draft], askCentral.target.id, askCentral.above);
            if (next) setPriorityAt(next);
            setAskCentral(null);
          }}>순서 올리기</Button>
        </>}>
        <div className="flex flex-col gap-4 text-sm">
          <p>중앙 관리자 스케줄 '{askCentral.target.name}'보다 먼저 재생하려면 승인이 필요합니다.</p>
          <p className="text-text-dim">순서를 올리고 저장하면 승인 요청이 함께 전송되며, 승인 전에는 하위 우선순위로 유지됩니다.</p>
        </div>
      </ModalShell>}
      {/* 승인 상태 — 그룹의 승인 방식과 내 권한에 따라 저장이 승인 요청으로 바뀐다. */}
      {checking && <p role="status" className="text-sm text-text-dim">승인 필요 여부를 확인하고 있습니다…</p>}
      {previewError && <p role="alert" className="text-sm text-red">승인 확인 실패: {previewError}</p>}
      {needsApproval && <div role="status" className="border-l-2 border-amber-400 pl-3 text-sm text-amber-700">
        <p>{initial && initial.approval?.status !== 'pending'
          ? '저장하면 수정 승인 요청으로 등록됩니다. 승인 전까지 기존 스케줄이 그대로 재생되고, 승인되면 이 내용이 기존 스케줄을 대체합니다.'
          : '저장하면 승인 요청으로 등록됩니다. 승인 전에는 재생되지 않습니다.'}</p>
        {preflight!.conflicts.map(c => <p key={c.deviceId}>{devices.find(d => d.id === c.deviceId)?.name}: {c.reason}</p>)}
      </div>}
      {initial?.approval && <div role="status" aria-label="승인 상태"
        className={`flex flex-wrap items-center gap-2 rounded border px-2 py-1.5 text-[12px] ${initial.approval.status === 'pending'
          ? 'border-blue bg-blue-soft text-blue' : initial.approval.status === 'rejected'
            ? 'border-red-300 bg-red-50 text-red' : 'border-line bg-panel-2 text-text-dim'}`}>
        <span className="font-semibold">{approvalActionLabel(initial.approval.action)} {approvalLabel(initial.approval.status)}</span>
        <span>{currentApprovalReason(scope, initial)}{initial.approval.note ? ` · ${initial.approval.note}` : ''}
          {initial.approval.status === 'pending'
            ? (initial.approval.replacesId ? ' · 승인 전까지 기존 스케줄이 그대로 재생됩니다.' : ' · 승인 전에는 재생되지 않습니다.')
            : initial.approval.status === 'rejected' || initial.approval.status === 'cancelled' ? ' · 재생되지 않습니다.' : ''}</span>
        {canDecideAssignment(scope, initial) && initial.approval.status === 'pending' && <input aria-label="반려 사유" placeholder="검토 의견 / 반려 사유"
          value={approvalNote} onChange={e => setApprovalNote(e.target.value)} className="rounded border border-line bg-panel p-2" />}
        {canDecideAssignment(scope, initial) && initial.approval.status === 'pending' &&
          <Button className="ml-auto !py-1" {...saveGate.props} disabled={busy || saveGate.blocked}
            onClick={() => void decide('approved')}>승인</Button>}
        {canDecideAssignment(scope, initial) && initial.approval.status === 'pending' &&
          <Button variant="danger" className="!py-1" {...saveGate.props} disabled={busy || saveGate.blocked}
            onClick={() => void decide('rejected')}>반려</Button>}
      </div>}
      </div>
    </fieldset>
    {/* 미리보기도 설정과 같은 흐름으로 스크롤한다. */}
    {(showPreview || conflictSummary) && <div className="border-t border-line pt-2">
      {/* 겹침 안내는 미리보기 안에서 한 번만 말한다. 같은 사실을 두 곳에서 다르게 적으면 안 된다. */}
      {showPreview
        ? <BoardPreview draft={draft} siblings={previewSiblings} date={previewDate} overlapDays={overlapDays}
          deviceName={previewDevice!.name} labelClassName={fieldLabel}
          onReorder={protectedSchedule || saveGate.blocked ? null : changePriority} />
        : conflictSummary && <p role="status" className="rounded border border-warning/40 bg-warning/10 px-2 py-1.5 text-[12px] text-warning">
          <span aria-hidden className="mr-1">⚠</span>{conflictSummary}
        </p>}
    </div>}
    </div>
    </div>
    {/* 아래로 이어지는 내용 위에 겹쳐 그려, 섹션이 여기서 끝난 것처럼 보이지 않게 한다. */}
    {moreContentBelow && <div aria-hidden="true" data-scroll-continuation
      className="pointer-events-none absolute inset-x-5 bottom-0 h-6 bg-gradient-to-t from-panel/80 to-transparent" />}
  </ModalShell>;
}
